// SKADE COP — inngangspunkt. Gater på autentisering (live-modus), wirer så
// datafeed → kart → verktøy → UX-lag (kommandopalett, kontekstmeny, snarveier).
// UX-først: kartet er alltid rolig, all makt ligger i ⌘K + én kontekstmeny +
// enkelttast-snarveier.
import './style.css';
import { forward as mgrsForward, toPoint as mgrsToPoint } from 'mgrs';
import type { Track, CombatReport, ReportType, FieldMessage, Requirement, SporUtenPosisjon } from '@skade/contracts';
import { REPORT_PRECEDENCE, ALERT_PRECEDENCE, casualtyPrecedence, SUPPLY_LEVEL_PRECEDENCE, meldingerOmObjekt, objektTraadSammendrag, posisjonsTekst, grunnbildeForsinkelseTekst, erForeldet, foreldelsesTekst, grunnbildeLevetidTekst, type GrunnbildeObjekt, type ObjektAnker, haversineM, latLonToMgrs, GNA_BFT_SOURCE, parseReports, reportSummary, mergeReport, STATUS_LABEL, STATUS_COLOR, parseIntelOne, parseFieldMessage, makeFieldMessage, buildIntelPackage, detectAnomalies, anomalyByTrack, parseChatEvent, toGeoJSON, toKML, trackToExportPoint, reportToExportPoint, type ExportPoint, trackToCotXml, reportToCotXml, CallForFireSchema, buildCffText, dangerCloseCheck, dangerCloseRing, engagementRing, FIRE_WEAPON_LABEL, FIRE_STATUS_LABEL, FIRE_TARGET_PRIORITY_LABEL, THREAT_SYSTEMS, type FriendlyPos, FireTargetSchema, nextFireStatus, type FireTarget, parseRfiOne, mergeRfi, answerRfi, type Rfi, parseCasualtyOne, mergeCasualty, type Casualty, parseLogStatOne, mergeLogStat, worstLevel, SUPPLY_LEVEL_COLOR, type SupplyClass, type SupplyLevel, type LogStat, parseResupplyOne, mergeResupply, type ResupplyRequest, EsoSoldierStatusSchema, ChecklistAssignmentSchema, type EsoSoldierStatus, type ChecklistAssignment, KRISTIANSUND_AOR, type AreaOfResponsibility, districtAreas, HV_DISTRICTS, buildAlertFeed, alertCounts, announceNewSensorAlerts, kvitterAlert, type Alert, type NamedPoint, mergeActivity, parseCalendarActivity, type CalendarActivity, type UnitRef, type CommandLevel, OrbatEventSchema, TeigSchema, type Contact, parseDelegasjon, erAktiv, harMyndighet, erIldFrigivning, delegasjonsHendelse, NIVAA_LABEL, type Delegasjon, type UnitAddress, KAPABILITET_TYPE_LABEL, type Kapabilitet } from '@skade/contracts';
import { ReportThreadView } from './ui/report-thread.js';
import { IntelWorkspace } from './ui/intel.js';
import { PttPanel, speak } from './ui/ptt.js';
import { TimeMachine } from './ui/timemachine.js';
import { FireSupportComposer, type FireParams } from './ui/firesupport.js';
import { TargetListPanel } from './ui/targetlist.js';
import { AlertsPanel } from './ui/alerts.js';
import { RfiPanel } from './ui/rfi.js';
import { DispatchPanel } from './ui/dispatch.js';
import { AarPanel } from './ui/aar.js';
import { ArkivPanel, arkivHent, arkivSend, dekningsrutenett, observasjonGrunnlag } from './ui/arkiv.js';
import { nyesteSporForPerson } from './ui/kontaktkort.js';
import { EmconStripe } from './ui/emcon-stripe.js';
import { KoStripe } from './ui/kostripe.js';
import { KanaloversiktPanel, type KanaloversiktData } from './ui/kanaloversikt.js';
import { Grunnbildelag } from './map/grunnbildelag.js';
import { MeldFraLinje } from './ui/meldfra.js';
import { CalendarPanel } from './ui/calendar.js';
import { FieldCalcPanel } from './ui/fieldcalc.js';
import { OrbatCreator } from './ui/orbat-creator.js';
import { BookmarksPanel } from './ui/bookmarks.js';
import { WeatherPanel } from './ui/weather.js';
import { LysforholdPanel, LYSFORHOLD_IKON } from './ui/lysforhold.js';
import { SiktPanel } from './ui/sikt.js';
import { MalpekingPanel } from './ui/malpeking.js';
import { OrdrePanel, type forflytningsSpor } from './ui/ordre.js';
import { PlanprosessPanel, PLANPROSESS_IKON } from './ui/planprosess.js';
import { SkyggeLag, SKYGGE_IKON } from './map/skygge.js';
import { DekningLag } from './map/dekning.js';
import { GeointLag, GEOINT_IKON, type GeointJobberSvar } from './map/geointlag.js';
import { TerrariumTileCache } from '@skade/terrain';
import { aktiver3dStandard, overvaakBasiskart, settTerreng, terrengPaa, type Ekstraoverlegg } from './map/basemaps.js';
import { NineLineComposer, buildNineLineText } from './ui/nineline.js';
import { CasualtyPanel } from './ui/casualty.js';
import { LogisticsPanel } from './ui/logistics.js';
import type { FireRing, FireTargetMark } from './map/cop-map.js';
import { myndighetsNivaa, ROLE_COMMAND_ECHELON, type User } from '@skade/rbac';
import { DemoFeed, LiveFeed, type Feed } from './data/feed.js';
import { CopMap } from './map/cop-map.js';
import { CompassOverlay } from './ui/compass.js';
import { TerritoryLayer, type AorScope } from './tools/territory.js';
import { Graticule } from './map/grid.js';
import { KritiskeLag, KRITISK_KAT } from './map/kritiske.js';
import { InfraLag } from './map/infra.js';
import { KritiskePanel } from './ui/kritiske-panel.js';
import { SkandiaLag } from './map/skandia.js';
import { Shell, bekreft, sporTekst, type RosterItem } from './ui/shell.js';
import { CommandPalette, type Command } from './ui/command-palette.js';
import { ToolDock } from './ui/tooldock.js';
import { icon } from './ui/icons.js';
import { WindowManager } from './ui/windows.js';
import { ContextMenu } from './ui/context-menu.js';
import { installShortcuts } from './ui/shortcuts.js';
import { installerGester } from './ui/gester.js';
import {
  KartobjektRegister, OBSERVASJON, PLANOBJEKT, Seleksjonseier, TYPE_ETIKETT,
  objektId, type Kartobjekt,
} from './objekt/kartobjekt.js';
import { MeasureTool } from './tools/measure.js';
import { DrawTool } from './tools/draw.js';
import { GeofenceTool } from './tools/geofence.js';
import { RouteTool } from './tools/route.js';
import { TacGraphicsTool } from './tools/tacgraphics.js';
import { UnitTool, BFT_SELF_ID, enhetDetaljer, gnaRosterSub, maalingsLinje, type PlacedUnit } from './tools/units.js';
import { UnitPalette } from './tools/unit-palette.js';
import { OrbatVelgerPanel } from './tools/orbat-velger-panel.js';
import { byggOrbatKatalog, orbatKatalogPost } from './tools/orbat-velger.js';
import { velgOrdreformat } from '@skade/hildr';
import { ReportComposer } from './tools/report-composer.js';
import { applyMode, cycleMode } from './theme-mode.js';
import { demoUser } from './session.js';
import { byggKontekst, velgEgenposisjon, kortId, GPS_FERSK_MS, type Kontekst, type Egenposisjon, type GpsFix } from './kontekst.js';
import { MAKS_HENDELSER, type Hendelse } from './ui/hendelseslogg.js';
import { LINSER, LINSE_ETIKETT, LINSE_SOKEORD } from './ui/feltnett.js';
import { MyndighetPanel } from './ui/myndighet.js';
import { fetchMe, logout } from './auth/api.js';
import { showLogin } from './auth/login-view.js';
import { SyncBus } from './sync.js';
import { Kartmodus } from './map/kartmodus.js';
import { standalonePanelId, popOut, POPOUT_KEY_BY_CLASS } from './panels/popout.js';
import { mountPlanprosessPanel, PLAN_SYNC } from './panels/planprosess-panel.js';
import { mountRosterPanel } from './panels/roster-panel.js';
import { mountC2Panel } from './panels/c2-panel.js';
import { mountIntelPanel } from './panels/intel-panel.js';
// Chat/TOC finnes bare som ARVET pop-out-rute (?panel=chat) — ingen dør i UI-et.
import { mountChatPanel } from './panels/chat-panel.js';
import { mountSentralbordPanel } from './panels/sentralbord-panel.js';
import { mountDirectoryPanel } from './panels/directory-panel.js';
import { mountChecklistPanel } from './panels/checklist-panel.js';
import { mountRfiPanel } from './panels/rfi-panel.js';
import { mountDispatchPanel } from './panels/dispatch-panel.js';
import { mountCasualtyPanel } from './panels/casualty-panel.js';
import { mountLogisticsPanel } from './panels/logistics-panel.js';
import { mountAdminPanel } from './panels/admin-panel.js';
import { mountPersonregisterPanel } from './panels/personregister-panel.js';
import { mountKapabilitetsplanleggerPanel } from './panels/kapabilitetsplanlegger-panel.js';
import { mountEsoPanel } from './panels/eso-panel.js';
import { mountOrbatPanel } from './panels/orbat-panel.js';
import { AdminPanel } from './ui/admin.js';
import { PersonregisterPanel, personregisterHent, personregisterSend } from './ui/personregister.js';
import { KapabilitetsplanleggerPanel, kapabilitetHent, kapabilitetSend } from './ui/kapabilitetsplanlegger.js';
import { ChecklistPanel } from './ui/checklist.js';
import { EsoPanel } from './ui/eso.js';
import { DirectoryPanel } from './ui/directory.js';
import { sokGnaBrukere } from './gna-katalog.js';
import { Sentralbord } from './ui/sentralbord.js';

const root = document.getElementById('app');
if (!root) throw new Error('#app mangler');

// Ekte backend? Uten VITE_SKADE_WS kjører demo-feed → vis DEMO-merke, hopp
// over innlogging (frittstående UI-visning uten backend).
const wsUrl = import.meta.env.VITE_SKADE_WS as string | undefined;

// ── Ruting: poppet-ut panel vs. full COP ────────────────────────────────
const panelId = standalonePanelId();
if (panelId) {
  // Frittstående panel-vindu (multi-skjerm). Autentiser som ellers.
  const boot = (u: User) => bootPanel(panelId, u);
  if (!wsUrl) boot(demoUser);
  else fetchMe().then((u) => (u ? boot(u) : showLogin(root!, boot)));
} else if (!wsUrl) {
  bootApp(demoUser, true); // Demo-modus: ingen backend å autentisere mot.
} else {
  fetchMe().then((user) => {
    if (user) bootApp(user, false);
    else showLogin(root!, (u) => bootApp(u, false));
  });
}

// ── Frittstående panel-oppstart (eget vindu/skjerm) ─────────────────────────
function bootPanel(id: string, user: User) {
  applyMode('natt');
  const feed: Feed = wsUrl ? new LiveFeed(wsUrl) : new DemoFeed();
  const sync = new SyncBus();
  if (id === 'roster') mountRosterPanel(root!, feed, sync);
  else if (id === 'c2') mountC2Panel(root!, feed, sync, user);
  else if (id === 'intel') mountIntelPanel(root!, feed, sync, user);
  else if (id === 'chat') mountChatPanel(root!, feed, sync, user);
  else if (id === 'feltveksler') mountSentralbordPanel(root!, feed, sync, user);
  else if (id === 'directory') mountDirectoryPanel(root!, feed, sync, user);
  // Sjekklista er et utviklerverktøy (se bootApp) — ruta finnes bare uten backend.
  else if (id === 'checklist') { if (!wsUrl) mountChecklistPanel(root!); }
  else if (id === 'rfi') mountRfiPanel(root!, feed, sync, user);
  else if (id === 'dispatch') mountDispatchPanel(root!, feed, sync, user);
  else if (id === 'perstat') mountCasualtyPanel(root!, feed, sync, user);
  else if (id === 'logistics') mountLogisticsPanel(root!, feed, sync, user);
  else if (id === 'admin') mountAdminPanel(root!, feed, sync, user);
  else if (id === 'personregister') mountPersonregisterPanel(root!, feed, sync, user);
  else if (id === 'kapabilitetsplanlegger') mountKapabilitetsplanleggerPanel(root!, feed, sync, user);
  else if (id === 'eso') mountEsoPanel(root!, feed, sync, user);
  else if (id === 'orbat') mountOrbatPanel(root!, feed, sync, user);
  else if (id === 'planprosess') mountPlanprosessPanel(root!, sync);
}

/** Løs operatørens eget område-id fra org.area (matcher nummer eller navn mot
 * distriktets områder). Null hvis ikke entydig. */
function resolveAreaId(user: User): string | undefined {
  const d = user.org.district;
  const area = (user.org.area ?? '').toLowerCase().trim();
  const district = HV_DISTRICTS.find((x) => x.id === d);
  if (!district || !area) return undefined;
  const hit = districtAreas(district).find((a) =>
    a.id.toLowerCase() === area || String(a.number) === area || area.includes(String(a.number)) ||
    a.name.toLowerCase() === area || area.includes(a.name.toLowerCase()) ||
    a.municipalities.some((m) => area.includes(m.toLowerCase())));
  return hit?.id;
}

/** Tier-scope for AOR-fargelegging: eget område + eget distrikt. Distrikts-
 * roller (distriktsstab/FOH) ser hele eget distrikt mørkere. */
function scopeForUser(user: User): AorScope {
  return {
    district: user.org.district,
    areaId: resolveAreaId(user),
    districtWide: user.role === 'distriktsstab' || user.role === 'foh',
  };
}

/** Presis egen ansvarsgrense der den finnes (i dag: Kristiansund/HV-11-203). */
function aorForUser(user: User): AreaOfResponsibility | null {
  return resolveAreaId(user) === KRISTIANSUND_AOR.areaId ? KRISTIANSUND_AOR : null;
}

/**
 * Verktøydocken og PTT-panelet stables over «Meld fra»-linja etter MÅLT høyde,
 * ikke et hardkodet tall (se `--meldfra-h`/`--dock-h` i style.css). Linja er
 * ikke konstant høy — chip-raden legger til en hel rad når feltet ikke er
 * tomt, og en lang feilmelding kan bli tolinjers — så et fast tall er alltid
 * ett innholdsendring bak virkeligheten. En ResizeObserver holder de to
 * CSS-variablene ferske uansett hva som endrer høyden.
 */
function stillBunnstabelDynamisk(meldfraEl: HTMLElement, dockEl: HTMLElement): void {
  const oppdater = () => {
    document.documentElement.style.setProperty('--meldfra-h', `${meldfraEl.getBoundingClientRect().height}px`);
    document.documentElement.style.setProperty('--dock-h', `${dockEl.getBoundingClientRect().height}px`);
  };
  const obs = new ResizeObserver(oppdater);
  obs.observe(meldfraEl);
  obs.observe(dockEl);
  oppdater();
}

// ── App-oppstart (kalles først når en bruker er autentisert) ───────────────
function bootApp(user: User, demo: boolean) {
  applyMode('natt');
  let lastAlarmTrack: Track | null = null;
  let lastTracks: Track[] = [];
  // Ferske uposisjonerte deteksjoner (ukalibrert kilde, VALE SENS-21) — egen
  // liste fordi en `Track` ikke kan uttrykke «ingen posisjon» (lat/lon er
  // obligatorisk). Fylles av `feed.onSporUtenPosisjon`, leses av `refreshAlerts`.
  let lastSporUtenPosisjon: SporUtenPosisjon[] = [];
  // Verktøylinja bygges nederst, men lag-tilstand speiles inn i den (setActive/
  // setCount) fra kall-punkter definert lenger opp.
  let dock: ToolDock | undefined;
  /**
   * Siste GPS-avlesning fra kartets geolocate — MED tidsstempel, fordi en gammel
   * fix ikke er «hvor jeg er nå». Dette er den ENESTE kilden i appen som får
   * kalle seg målt egenposisjon; kartsenteret er noe annet (se `kontekst.ts`).
   */
  let gpsFix: GpsFix | null = null;

  const shell = new Shell(
    root!,
    user,
    [
      { id: 'sensors', name: 'SPARK-sensorer', color: 'var(--aff-friend)' },
      { id: 'targets', name: 'Deteksjonsmål', color: 'var(--aff-hostile)' },
    ],
    (id, on) => cop.setLayerVisible(id, on),
    () => { if (lastAlarmTrack) cop.flyTo(lastAlarmTrack); },
    demo,
    (active) => handleEmergency(active),
    demo ? undefined : async () => { await logout(); location.reload(); },
    user.role !== 'menig', // canAdmin: menig kan ikke invitere (hub håndhever autoritativt)
    () => toggleAdmin(),
    () => cycleMode(),
  );

  // 112-nødknapp: kringkast egen posisjon (eller kartsenter) til hele nettet.
  function handleEmergency(active: boolean) {
    // ÉN posisjonskilde: fersk GPS, ellers kartsenter (`kontekst.ts`). Før dette
    // ble «første spor med kind==='sensor'» brukt som egen posisjon — det kunne
    // være en vilkårlig SPARK-node i feeden, altså en annens sted i egen nødmelding.
    const p = egenposisjon();
    const c = cop.map.getCenter();
    const lon = p?.lon ?? c.lng;
    const lat = p?.lat ?? c.lat;
    feed.send?.({ type: 'emergency', lon, lat, cancel: !active });
    cop.setEmergency('meg', lon, lat, active);
    shell.setEmergency(active);
    if (active) shell.showAlarm('112 SENDT — posisjon kringkastet til nettet');
    const naa = Date.now();
    loggHendelse({
      id: `em-meg-${naa}`, tsMs: naa, type: 'varsel',
      // ⚠ DEN ENE PLASSEN `flash` ER ÆRLIG. FLASH er høyeste presedens i
      // NATO-meldingstjeneste, og `alerts.ts` reserverer den bevisst for det et
      // MENNESKE erklærer — regelutløste varsler får aldri mer enn IMMEDIATE.
      // Her har noen trykket nødknappen selv. Avlysningen er ikke FLASH, men den
      // må fram raskt til alle som har begynt å rykke ut: PRIORITY.
      hastegrad: active ? 'flash' : 'priority',
      tittel: active ? '112 SENDT — egen nødalarm' : '112 AVLYST — egen nødalarm',
      detalj: p ? `posisjonskilde: ${p.kilde}` : 'ingen posisjonskilde',
      farge: active ? 'var(--danger)' : 'var(--sage)',
      onClick: () => cop.map.flyTo({ center: [lon, lat], zoom: 15 }),
    });
  }

  // Multi-skjerm-buss: poppede paneler kan be hovedkartet fokusere et objekt.
  const sync = new SyncBus();
  shell.onPopoutRoster = () => popOut('roster');

  // ── Kartmodus: ÉN eier av kartklikkene ────────────────────────────────────
  // Før dette hadde måle-, rute-, akse- og tegneverktøyet hver sin
  // `map.on('click')`, og ingen slo av de andre. To verktøy tolket samme klikk,
  // og kartets egne lag svarte i tillegg med sin egen tooltip. Se
  // `map/kartmodus.ts` for hele historien.
  const kartmodus = new Kartmodus();
  // TRÅKORS OG AO-RØDMERKING HØRER I KARTLAG-MENYEN (⧉), ikke bare i docken.
  //
  // Fagansvarlig meldte «Finner ikke tråd kors av/på» og «Heller ikke hvor jeg tar
  // bort rødmerking eget ao» — etter å ha blitt henvist til «menyen for kart». Det
  // var et reelt funn: bryterne fantes, men lå i dock-gruppa «Kart» blant 17
  // oppføringer, mens brukeren lette i kartlag-velgeren som HETER Kartlag.
  //
  // Begge flater beholdes, men de LESER SAMME KILDE (`er()`), så de kan ikke vise
  // ulik tilstand. `territory` og `dock` opprettes lenger ned; disse funksjonene
  // kjører først på klikk, så sen binding er trygt.
  const kartOverlegg: Ekstraoverlegg[] = [
    {
      navn: 'Tråkors (kartsenter)',
      tittel: 'Kors i kartsenteret, for å lese av grid — av/på (H)',
      er: () => shell.isCrosshairOn(),
      veksle: (paa) => {
        const ny = shell.setCrosshair(paa);
        dock?.setActive('crosshair', ny);
        return ny;
      },
    },
    {
      navn: 'Eget AO (rødmerket)',
      tittel: 'Rødmerking av eget ansvarsområde (kommune/fylke) — av/på',
      er: () => territory.isVisible(),
      veksle: (paa) => {
        if (territory.isVisible() !== paa) territory.toggle();
        const ny = territory.isVisible();
        dock?.setActive('territory', ny);
        return ny;
      },
    },
  ];
  const cop = new CopMap(
    document.getElementById('map')!,
    (t) => shell.showDetail(t),
    () => kartmodus.eierKlikk(),
    kartOverlegg,
  );

  // ── TRÅDEN PÅ SPORET (UX-funn F4, HØY) ────────────────────────────────────
  //
  // «En melding i Feltveksler blir aldri et objekt på kartet, og et spor/kontakt
  // på kartet har ingen egen samtaletråd. To parallelle virkeligheter: det man
  // sier og det man ser møtes aldri.»
  //
  // ⚠ INGEN NY MELDINGSTJENESTE. `CombatReport` har alt en dialogtråd, ligger på
  // kartet, deles over hubben og replayes ved reload — den manglet bare hva den
  // handler om (`omObjekt`, se contracts). Å bygge et parallelt trådsystem for
  // spor ville laget den TREDJE meldingsvirkeligheten i et system rapporten
  // kritiserer for å ha to.
  shell.onSporTraad = (t) => {
    const alle = [...reports.values()];
    const sam = objektTraadSammendrag(alle, 'spor', t.id);
    return {
      sammendrag: sam.tekst,
      antall: sam.meldinger,
      onMeld: () => composer.openForObjekt(
        // Etiketten fryses NÅ: et spor skifter navn når fusjonen får ny
        // informasjon, og meldingen skal fortsatt kunne leses som «dette meldte
        // hun om KJØRETØY 3» — også etter at sporet er sluppet.
        { slag: 'spor', id: t.id, etikett: t.label },
        t.lon, t.lat,
      ),
      onApne: () => {
        // Åpner den NYESTE meldingen om sporet. `meldingerOmObjekt` gir eldste
        // først (en tråd leses framover), så den siste er den å hoppe inn i.
        const m = meldingerOmObjekt(alle, 'spor', t.id);
        const siste = m[m.length - 1];
        if (siste) openThread(siste);
      },
    };
  };

  sync.on('focus', (m) => {
    if (typeof m.lon === 'number' && typeof m.lat === 'number') {
      cop.map.flyTo({ center: [m.lon, m.lat], zoom: 15 });
      cop.dropPin([m.lon, m.lat]);
    }
  });
  const grid = new Graticule(cop.map);
  // Kritiske objekter (J-5): MIL/EN/TR, toggle-bare undergrupper, AV som standard.
  // Redigerbare: blyant i boblen → endre navn/kategori/hvorfor + flytt på kartet.
  const kritiske = new KritiskeLag(cop.map);
  // Infrastruktur fra OSM (master/tårn/kraft/sykehus) MED høyde — eget under-lag
  // og samtidig inngangsdata til antenne-/radardekning (radiohorisont ~ √h).
  const infra = new InfraLag(cop.map);
  const skandia = new SkandiaLag(cop.map);
  // Redigeringer lagres lokalt (localStorage). Deling til andre operatører krever
  // en egen collab-kanal i hubben — se VALE (følger i eget pass).
  const mkDiv = (cls: string, text: string) => { const d = document.createElement('div'); d.className = cls; d.textContent = text; return d; };

  /**
   * Kartklikk på et kritisk objekt.
   *
   * FUNN 06: DETALJENE går nå i HØYREPANELET (`shell.showDetailRows`), som er
   * bygget for dem og står åpent uansett. Boblen er redusert til en kort
   * BEKREFTELSE på hva som ble truffet + blyanten, fordi en boble ligger oppå det
   * operatøren nettopp pekte på og forsvinner ved neste klikk.
   */
  function openKoPopup(id: string, lngLat: maplibregl.LngLatLike) {
    const o = kritiske.byId(id);
    if (!o) return;
    const color = KRITISK_KAT[o.kat]?.color ?? '#c8d8c8';
    shell.showDetailRows(
      o.name,
      `${KRITISK_KAT[o.kat]?.label ?? o.kat} · KRITISK OBJEKT`,
      [
        ['Kategori', KRITISK_KAT[o.kat]?.label ?? o.kat],
        ['Distrikt', o.districtName],
        ['MGRS', o.mgrs],
        ['Hvorfor', o.why],
      ],
      [{ label: '✎ Rediger objekt', run: () => openKoEditor(id, lngLat) }],
    );
    const box = document.createElement('div');
    box.className = 'ko-popup';
    // Blyant oppe til venstre (rediger).
    const editBtn = document.createElement('button');
    editBtn.className = 'ko-edit-btn'; editBtn.title = 'Rediger objekt (navn, kategori, flytt)';
    editBtn.innerHTML = icon('pencil');
    editBtn.onclick = () => openKoEditor(id, lngLat);
    box.append(editBtn);
    const title = mkDiv('ko-pop-t', o.name); title.style.color = color;
    box.append(title, mkDiv('ko-pop-meta', 'Detaljer står i høyrepanelet →'));
    cop.popupAt(lngLat, box);
  }

  function openKoEditor(id: string, lngLat: maplibregl.LngLatLike) {
    const o = kritiske.byId(id);
    if (!o) return;
    const box = document.createElement('div');
    box.className = 'ko-popup ko-editor';
    box.append(mkDiv('ko-pop-t', 'Rediger objekt'));
    const name = document.createElement('input'); name.className = 'ko-in'; name.value = o.name; name.placeholder = 'Navn';
    const kat = document.createElement('select'); kat.className = 'ko-in';
    for (const k of ['MIL', 'EN', 'TR'] as const) {
      const opt = document.createElement('option'); opt.value = k; opt.textContent = KRITISK_KAT[k].label; if (k === o.kat) opt.selected = true; kat.append(opt);
    }
    const why = document.createElement('textarea'); why.className = 'ko-in ko-ta'; why.value = o.why; why.placeholder = 'Hvorfor / betydning';
    box.append(labelWrap('Navn', name), labelWrap('Kategori', kat), labelWrap('Hvorfor', why));
    const row = document.createElement('div'); row.className = 'ko-btnrow';
    const save = document.createElement('button'); save.className = 'ko-btn ko-btn-primary'; save.textContent = 'Lagre';
    save.onclick = () => { kritiske.editMeta(id, { name: name.value.trim() || o.name, kat: kat.value as typeof o.kat, why: why.value.trim() }); cop.closePopups(); shell.showAlarm('Objekt oppdatert'); };
    const move = document.createElement('button'); move.className = 'ko-btn'; move.textContent = 'Flytt på kartet';
    move.onclick = () => { kritiske.editMeta(id, { name: name.value.trim() || o.name, kat: kat.value as typeof o.kat, why: why.value.trim() }); kritiske.startMove(id); cop.closePopups(); shell.showAlarm('Klikk på kartet for å flytte objektet hit'); };
    row.append(save, move);
    box.append(row);
    cop.popupAt(lngLat, box);
  }

  function labelWrap(label: string, ctrl: HTMLElement): HTMLElement {
    const w = document.createElement('label'); w.className = 'ko-field';
    w.append(mkDiv('ko-field-l', label), ctrl); return w;
  }

  /** Boble for et OSM-infrastrukturelement: navn, type, HØYDE og terrenghøyde
   *  (det dekningsmodulen regner på), samt kilde — ærlig om hvor data kommer fra. */
  function openInfraPopup(p: Record<string, unknown>, lngLat: maplibregl.LngLatLike) {
    const kind = String(p.kind ?? '');
    const type = String(p.towerType ?? '');
    const h = typeof p.heightM === 'number' ? p.heightM : null;
    const ele = typeof p.elevationM === 'number' ? p.elevationM : null;
    const amsl = typeof p.amslM === 'number' ? p.amslM : null;
    // Detaljene i høyrepanelet (FUNN 06) — høyde og kilde er nettopp det
    // dekningsberegningen hviler på, og skal kunne leses uten at en boble dekker
    // masten man vurderer.
    shell.showDetailRows(
      String(p.name ?? 'Infrastruktur'),
      [infra.kindLabel(kind as never), type].filter(Boolean).join(' · ') || 'INFRASTRUKTUR (OSM)',
      [
        ['Høyde over bakken', h !== null ? `${h} m` : 'ikke oppgitt'],
        ['Terreng', ele !== null ? `${ele} m o.h.` : ''],
        ['Antenne', amsl !== null ? `${amsl} m o.h.` : ''],
        ['Operatør', p.operator ? String(p.operator) : ''],
        ['Kilde', `${String(p.source ?? 'OSM')} · © OpenStreetMap-bidragsytere`],
      ],
    );
    const box = document.createElement('div');
    box.className = 'ko-popup';
    const title = mkDiv('ko-pop-t', String(p.name ?? 'Infrastruktur'));
    title.style.color = infra.kindColor(kind as never);
    box.append(title);
    box.append(mkDiv('ko-pop-meta', h !== null ? `${h} m over bakken · detaljer i høyrepanelet →` : 'Detaljer i høyrepanelet →'));
    cop.popupAt(lngLat, box);
  }

  // ── Tegnforklaring: hva ◆ betyr, hvor mange det er, og av/på per kategori ──
  const kritiskePanel = new KritiskePanel({
    koCounts: () => kritiske.counts(),
    koIsOn: (k) => kritiske.isOn(k),
    koSetOn: (k, on) => { kritiske.setOn(k, on); },
    koSymbol: (k) => kritiske.symbolDataUrl(k),
    koColor: (k) => KRITISK_KAT[k].color,
    koLabel: (k) => KRITISK_KAT[k].label,
    koInView: () => kritiske.inViewCount(),
    koFitNearest: () => kritiske.fitToNearest(),
    infraCounts: () => infra.counts(),
    infraIsOn: (k) => infra.isOn(k),
    infraSetOn: (k, on) => { infra.setOn(k, on); },
    infraSymbol: (k) => infra.symbolDataUrl(k),
    infraColor: (k) => infra.kindColor(k),
    infraLabel: (k) => infra.kindLabel(k),
    infraStatusText: () => infra.statusText(),
    infraWithHeight: () => infra.withHeightCount(),
    infraLoad: (force) => { infra.enableAll(); void infra.load(force); },
  });
  shell.mapEl.appendChild(kritiskePanel.el);

  // Én sannhet: lagene varsler, dock-knapp + tegnforklaring speiler tilstanden.
  function syncKritiskeUi() {
    dock?.setActive('kritiske', kritiske.anyOn());
    dock?.setCount('kritiske', kritiske.visibleCount());
    dock?.setActive('infra', infra.anyOn());
    dock?.setCount('infra', infra.visibleCount());
    kritiskePanel.render();
  }
  kritiske.onStateChange = () => syncKritiskeUi();
  infra.onStateChange = () => syncKritiskeUi();
  // «Ingen objekter i utsnittet»-hintet må være sant NÅ, ikke slik det var før
  // kamerabevegelsen — så tegnforklaringen oppdateres når kartet har landet.
  cop.map.on('moveend', () => { if (kritiskePanel.isOpen()) kritiskePanel.render(); });

  /**
   * Dock-knappen «Kritiske objekter»: slår laget PÅ (aldri av ved første trykk),
   * åpner tegnforklaringen, og zoomer til nærmeste objekter hvis utsnittet er
   * tomt — «jeg ser ingenting»-fellen lukket. Nytt trykk når alt er på og synlig
   * lukker/åpner bare tegnforklaringen; skjuling skjer bevisst derfra.
   */
  function showKritiske() {
    const wasOn = kritiske.anyOn();
    kritiske.enableAll();
    kritiskePanel.show();
    if (kritiske.inViewCount() === 0) {
      const n = kritiske.fitToNearest();
      shell.showAlarm(`Kritiske objekter PÅ (${kritiske.visibleCount()}) — zoomer til ${n} nærmeste`);
    } else if (!wasOn) {
      shell.showAlarm(`Kritiske objekter PÅ — ${kritiske.inViewCount()} i utsnittet, ${kritiske.visibleCount()} totalt`);
    }
    syncKritiskeUi();
  }

  /** Dock-knappen «Infrastruktur (OSM)»: slå på + hent for gjeldende utsnitt. */
  function showInfra() {
    kritiskePanel.show();
    infra.enableAll();
    void infra.load(false).then((st) => {
      syncKritiskeUi();
      if (st === 'nede' || st === 'for-stort') shell.showAlarm(`Infrastruktur (OSM): ${infra.statusText()}`);
      else shell.showAlarm(`Infrastruktur (OSM): ${infra.visibleCount()} elementer, ${infra.withHeightCount()} med høyde`);
    });
    syncKritiskeUi();
  }

  cop.map.on('click', (e) => {
    // Eier et verktøy kartet, er klikket verktøyets. Uten denne vakta fikk
    // operatøren en boble om kritiske objekter midt i en avstandsmåling — og det
    // fikk måleverktøyet til å se ut som det ikke var slått på.
    if (kartmodus.eierKlikk()) return;
    // Flytte-modus: neste klikk flytter objektet som redigeres.
    if (kritiske.isMoving()) {
      const moved = kritiske.relocateTo(e.lngLat.lng, e.lngLat.lat);
      if (moved) shell.showAlarm(`«${moved.name}» flyttet hit`);
      return;
    }
    const hit = kritiske.hitAt(e.point);
    if (hit) { openKoPopup(String(hit.properties?.id ?? ''), e.lngLat); return; }
    // OSM-infrastruktur (mast/tårn/kraft/sykehus) → boble med høyde.
    const inf = infra.hitAt(e.point);
    if (inf) { openInfraPopup(inf.properties ?? {}, e.lngLat); return; }
    // SKANDIA-styrke (SE/DK) → detaljer i høyrepanelet + kort bekreftelse i boble.
    const sk = skandia.hitAt(e.point);
    if (sk) {
      const p = sk.properties ?? {};
      const nasjon = p.nation === 'SWE' ? 'Sverige · Hemvärnet' : 'Danmark · Hjemmeværnet';
      shell.showDetailRows(String(p.name ?? ''), `${nasjon} · SKANDIA`, [
        ['Nasjon', nasjon],
        ['Echelon', String(p.echelon ?? '')],
        ['Stab', String(p.hq ?? '')],
      ]);
      const box = document.createElement('div');
      box.className = 'ko-popup';
      box.append(
        mkDiv('ko-pop-t', String(p.name ?? '')),
        mkDiv('ko-pop-meta', 'Detaljer i høyrepanelet →'),
      );
      cop.popupAt(e.lngLat, box);
    }
  });
  const compass = new CompassOverlay(cop.map);
  shell.mapEl.appendChild(compass.el);
  function toggleCompass() { compass.el.classList.toggle('show'); }
  const measure = new MeasureTool(cop.map, kartmodus);
  measure.onEndring = () => objekter.meld();
  // Sletting MÅ svare. Et menyvalg som ser ut til å virke, men ikke hadde noe å
  // gjøre, er ikke til å skille fra et som er ødelagt — og det var nettopp
  // uklarheten «kan jeg slette målinger?» som gjorde at ingen fant veien ut.
  function slettMaalinger() {
    const n = measure.nullstill();
    shell.showAlarm(n
      ? `${n} avstandsmåling${n === 1 ? '' : 'er'} slettet`
      : 'Ingen avstandsmålinger på kartet');
  }
  const draw = new DrawTool(cop.map, kartmodus);
  const geofence = new GeofenceTool(
    cop.map,
    (e) => {
      shell.showAlarm(`TEIG ${e.kind === 'enter' ? 'INN' : 'UT'}: ${e.track.label} · ${e.fence.name}`);
    },
    // Lokale endringer sendes til hubben, som persisterer OG pusher til GNÅ.
    // Uten dette lever teigen bare i denne nettleseren — som før.
    (h) => {
      if (h.op === 'upsert') feed.send?.({ type: 'teig', teig: h.teig });
      else feed.send?.({ type: 'teig-slett', id: h.id });
      objekter.meld();
    },
  );
  const route = new RouteTool(cop.map, kartmodus);
  route.onEndring = () => objekter.meld();
  const tac = new TacGraphicsTool(cop.map, kartmodus);
  tac.onEndring = () => objekter.meld();
  // Territorielle ansvarsområder (hele HV): eget område/distrikt mørkere rødt,
  // resten lysere. Presis egen grense der den finnes (Kristiansund/HV-11-203).
  const territory = new TerritoryLayer(cop.map, () => kartmodus.eierKlikk());
  territory.setScope(scopeForUser(user), aorForUser(user));
  // Rødmerking av eget AO. Fagansvarlig fant ikke bryteren: «Heller ikke hvor jeg
  // tar bort rødmerking eget ao.» Den fantes — men uten tilstandsvisning, under
  // systemordet «AOR», som oppføring nr. 16 av 17. Docken viser nå tilstanden.
  function toggleTerritory() { dock?.setActive('territory', territory.toggle()); }
  // Enheter (APP-6): plasser/dra/navngi + del i sanntid via collab-kanalen.
  // ── Ordregang (fase 3): flytt enhet → fempunktsordre fra @skade/hildr ───────
  // Sporet tegnes som stiplet linje så det ikke forveksles med tegnet grafikk
  // eller målt sporhistorikk: dette er en PLANLAGT rute, ikke en observert.
  const SPOR_KILDE = 'ordre-spor';
  function visOrdreSpor(spor: ReturnType<typeof forflytningsSpor> | null) {
    const tomt = { type: 'FeatureCollection' as const, features: [] as unknown[] };
    const data = spor ? { type: 'FeatureCollection' as const, features: [spor] } : tomt;
    const kilde = cop.map.getSource(SPOR_KILDE) as maplibregl.GeoJSONSource | undefined;
    if (kilde) { kilde.setData(data as never); return; }
    if (!cop.map.isStyleLoaded()) return;
    cop.map.addSource(SPOR_KILDE, { type: 'geojson', data: data as never });
    cop.map.addLayer({
      id: SPOR_KILDE, type: 'line', source: SPOR_KILDE,
      layout: { 'line-cap': 'round' },
      paint: { 'line-color': '#c8a24a', 'line-width': 2.5, 'line-dasharray': [2, 1.5], 'line-opacity': 0.9 },
    });
  }

  // ── Utvikling til Strid (UtS/HPBP) — planprosessen ──────────────────────────
  // Koblingene som gjør panelet til et stabsverktøy og ikke et skjema: VTSSN
  // hentes fra MET (samme rute som værpanelet), og KAVOD «observasjon og
  // skuddfelt» / «dekning og skjul» besvares av terrengmotoren. A og V (taktiske
  // vurderinger i angrep/forsvar) kan ikke regnes ut, og panelet sier det.
  /** Vær for kartsenteret — delt, fordi et poppet-ut planpanel spør om det samme. */
  async function vaerForKartsenter(): Promise<Record<string, unknown> | null> {
    const c = cop.map.getCenter();
    try {
      const r = await fetch(`/met/forecast?lat=${c.lat.toFixed(4)}&lon=${c.lng.toFixed(4)}`, { credentials: 'include' });
      if (!r.ok) return null;
      const d = await r.json() as { series?: Record<string, unknown>[] };
      return d.series?.[0] ?? null;
    } catch { return null; }
  }

  const planprosess = new PlanprosessPanel({
    varsle: (t) => shell.showAlarm(t),
    hentVaer: vaerForKartsenter,
    // Soltidene regnes lokalt for kartsenteret — ingen nettverkskall, og derfor
    // virker de også når sambandet er nede.
    hentPosisjon: () => {
      const c = cop.map.getCenter();
      return { lat: c.lat, lon: c.lng };
    },
    hentLende: async (faktor) => {
      // Ærlig: dette er en HENVISNING til verktøyet som regner, ikke et
      // ferdig tall. Å late som panelet har regnet ut en viewshed uten at
      // operatøren har satt observatørpunkt ville vært oppdiktet underlag.
      if (/Observasjon/.test(faktor)) {
        toggleDekning();
        return 'Se dekningslaget: sektor-viewshed fra valgt OP (jordkrumming, k=4/3).';
      }
      if (/Dekning/.test(faktor)) {
        toggleSkygge();
        return 'Se terrengskygge + dødvinkler i dekningslaget for skjul mot observasjon.';
      }
      return null;
    },
    apneOrdre: () => { if (ordrePanel.gjeldendeOrdre()) ordrePanel.el.classList.add('open'); },
  });
  shell.mapEl.appendChild(planprosess.el);
  function togglePlanprosess() { planprosess.toggle(); }

  /**
   * Speil planprosessens tilstand i verktøylinja: åpen/lukket, og antall
   * KRITISKE mangler som teller. Telleren fantes i panelet (`kritiskeMangler()`)
   * men var aldri koblet til dock-en — motoren var ferdig, visningen manglet.
   * Poenget med tallet er at en sjef ser at planen har hull UTEN å åpne panelet.
   */
  function syncPlanprosessUi() {
    dock?.setActive('planprosess', planprosess.erApen());
    dock?.setCount('planprosess', planprosess.kritiskeMangler());
  }
  planprosess.onStateChange = () => syncPlanprosessUi();

  // Betjen et poppet-ut planpanel. Det vinduet har ingen kart, så det spør HIT
  // etter det som må leses av kartet. Uten disse tre svarene ville pop-outen vært
  // en knapp som åpnet et halvdødt panel — nettopp det jeg tidligere utsatte den
  // for å unngå.
  sync.on(PLAN_SYNC.bedOmVaer, () => {
    void vaerForKartsenter().then((serie) => sync.post({ type: PLAN_SYNC.vaerSvar, serie }));
  });
  // Kartsenteret til et poppet-ut planpanel: både på forespørsel og når kartet
  // flyttes, så soltidene i panelet følger kartet uten at operatøren gjør noe.
  const sendSenter = () => {
    const c = cop.map.getCenter();
    sync.post({ type: PLAN_SYNC.senterSvar, senter: { lat: c.lat, lon: c.lng } });
  };
  sync.on(PLAN_SYNC.bedOmSenter, sendSenter);
  cop.map.on('moveend', sendSenter);

  sync.on(PLAN_SYNC.bedOmLende, (m) => {
    const faktor = String((m as { faktor?: unknown }).faktor ?? '');
    if (/Observasjon/.test(faktor)) toggleDekning();
    else if (/Dekning/.test(faktor)) toggleSkygge();
    else return;
    shell.showAlarm(`Planprosessen (eget vindu) ba om «${faktor}» — terrengverktøyet er slått på her.`);
  });
  sync.on(PLAN_SYNC.apneOrdre, () => {
    if (ordrePanel.gjeldendeOrdre()) ordrePanel.el.classList.add('open');
  });

  const ordrePanel = new OrdrePanel({
    visSpor: (spor) => visOrdreSpor(spor),
    varsle: (t) => shell.showAlarm(t),
    // Sending går som feltmelding: den EKTE leveringsveien som finnes i dag
    // (persisteres i hubben og replayes ved reload). Toveis kvittering fra
    // enheten er fase 5 og krever GNÅ-broen — den lover vi ikke her.
    send: (tekst, ordre) => {
      const [lon, lat] = myPosition();
      const m = makeFieldMessage({
        id: `ord-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`,
        author: user.id, tsMs: Date.now(), lat, lon, text: tekst,
      });
      addField(m, false);
      feed.send?.({ type: 'field', field: m });
      // ⚠ UX-FUNN F5: ordren havnet i loggen som `melding`, fordi den går på
      // feltmelding-veien. En forflytningsordre til et lag så altså ut som en
      // chatlinje. Egen linje med egen type OVERSKRIVER den (samme id-prefiks er
      // med vilje ulikt, så begge står — feltmeldingen ER den faktiske
      // leveransen, ordrelinja er det operatøren leter etter). IMMEDIATE fordi
      // en ordre ingen har lest er en ordre ingen kan utføre.
      loggHendelse({
        id: `ordre-${ordre.ordreId}`, tsMs: Date.now(), type: 'ordre',
        tittel: `Ordre ${ordre.ordreId} → ${ordre.til}`,
        detalj: `fra ${ordre.fra || 'ukjent utsteder'} · sendt som feltmelding`,
        hastegrad: 'immediate',
        onClick: () => { ordrePanel.el.classList.add('open'); },
      });
      return true;
    },
  });
  shell.mapEl.appendChild(ordrePanel.el);

  const units = new UnitTool(cop.map, () => {
    feed.send?.({ type: 'units', units: units.snapshot() });
    rebuildRoster();
  }, kartmodus);
  // ── Forflytningsordre: bak et EKSPLISITT valg ─────────────────────────────
  //
  // ⚠ HVA SOM GIKK GALT: en dragning på kartet SKREV ordren og slo opp panelet av
  // seg selv. Terskelen i `units.ts` (FLYTT_TERSKEL_PX) fjernet fumleklikkene,
  // men en reell dragning gjorde det fortsatt: retter du opp en enhet du slapp
  // 40 m feil, satt du plutselig med en ferdig utfylt fempunktsordre for
  // «forflytning» — med enhetsnavn, marsjfart, ETA og ildåpningsbestemmelse — som
  // ingen hadde bestemt. Ordren SELV er sterk og uendret; det var utløseren som
  // var feil. Nå: dragning FLYTTER (og deler flyttingen, som før), og operatøren
  // får et tilbud. Sier han nei, kan han hente ordren igjen fra enhetens meny.
  //
  // Siste registrerte forflytning per enhet — grunnlaget «Gi ordre …» bruker.
  const sisteFlytting = new Map<string, { fra: { lon: number; lat: number }; til: { lon: number; lat: number } }>();
  /** Åpner ordrepanelet for en registrert forflytning. Utsteder = innlogget
   * operatørs rolle + distrikt, så «FRA» i ordren er sann og etterprøvbar. */
  function skrivForflytningsordre(u: PlacedUnit) {
    const f = sisteFlytting.get(u.id);
    if (!f) {
      shell.showAlarm(`Ingen forflytning registrert for ${u.label} — dra enheten til ny posisjon først.`);
      return;
    }
    // Nivået MÅ følge med (kartmanøver fase 3): `velgOrdreformat` i @skade/hildr
    // bruker det til å velge IRFMI for lag og tropp, som GRUS krever, og
    // fempunktsordre for kompani og over. Uten echelon falt alt tilbake til
    // fempunktsordre — et lagfører fikk en stabsordre han skulle lest opp på
    // sambandet. Er enheten ikke ORBAT-koblet, er nivået ukjent, og
    // fempunktsordren er riktig fallback (en for fyldig ordre skader mindre enn
    // en for tynn).
    const echelon = orbatKatalogPost(u.id, orbatKatalogNa())?.echelon;
    ordrePanel.visForFlytting(
      { id: u.id, label: u.label, echelon },
      f.fra, f.til,
      `${user.role.toUpperCase()} ${user.org.district}`,
      user.org.area ? `${user.org.district}/${user.org.area}` : user.org.district,
    );
  }
  units.onMoved = (u, fra, til) => {
    sisteFlytting.set(u.id, { fra, til });
    // Avstanden gjør tilbudet konkret: «flyttet 0,04 km» avslører at dette var en
    // korreksjon, ikke en forflytning — og da skal operatøren slippe å lure på om
    // han nettopp har generert en ordre.
    const km = (haversineM({ lat: fra.lat, lon: fra.lon }, { lat: til.lat, lon: til.lon }) / 1000).toFixed(2);
    // Tilbudet må navngi det formatet operatøren FAKTISK får. Teksten sa
    // «fempunktsordre» uansett nivå, og etter at echelon begynte å følge med
    // (fase 3) ville et lag blitt lovet en fempunktsordre og fått en IRFMI —
    // altså et tilbud som ikke stemte med resultatet.
    const ordreNavn = velgOrdreformat(orbatKatalogPost(u.id, orbatKatalogNa())?.echelon) === 'irfmi'
      ? 'IRFMI-ordre' : 'fempunktsordre';
    shell.showTilbud(
      `${u.label} flyttet ${km} km. Skriv ${ordreNavn} for denne forflytningen?`,
      [
        { label: 'Skriv ordre', primar: true, run: () => skrivForflytningsordre(u) },
        // Nei-valget sier hva som skjer videre, ellers tror operatøren at
        // muligheten er borte for godt.
        { label: 'Bare flytt', run: () => shell.showAlarm('Enheten er flyttet. «Gi ordre …» ligger i enhetens meny.') },
      ],
    );
  };
  // «Endre type/symbol …» og «Gi ordre …» i enhetens høyreklikk-meny.
  units.onEndreType = (u) => unitPalette.openForEndring({ id: u.id, label: u.label, sidc: u.sidc });
  units.onGiOrdre = (u) => skrivForflytningsordre(u);
  units.harOrdregrunnlag = (u) => sisteFlytting.has(u.id);

  const unitPalette = new UnitPalette(
    (sidc, label, coord) => {
      if (coord) {
        units.placeAt(coord[0], coord[1], sidc, label); // høyreklikk → plasser direkte
      } else {
        units.arm(sidc, label);
        shell.showAlarm('Klikk på kartet for å plassere enheten');
      }
    },
    // ENDRE-modus: oppdater enheten som ALLEREDE står der. Samme id → ORBAT-
    // koblingen (id = ORBAT-unit-id) overlever en symbolretting. Navnet røres
    // ikke: enheten heter det operatøren har døpt den, ikke katalognavnet.
    (id, sidc, typeLabel) => {
      if (units.setSidc(id, sidc)) {
        const u = units.get(id);
        const orbat = u && !/^u\d+$/.test(u.id) ? ` · ORBAT-kobling ${u.id} beholdt` : '';
        shell.showAlarm(`${u?.label ?? 'Enheten'}: symbol endret til ${typeLabel}${orbat}`);
        // Detaljpanelet skal vise det NYE symbolet med det samme — et panel som
        // står igjen med det gamle er verre enn et tomt (funn 3/5).
        if (u) visEnhetIPanel(u);
      } else {
        shell.showAlarm('Enheten finnes ikke lenger — symbolet ble ikke endret.');
      }
    },
  );

  // ── Enhetsvalg → detaljpanelet (UX-funn 3/5) ──────────────────────────────
  //
  // ⚠ HVA SOM GIKK GALT: klikk på et plassert APP-6-symbol markerte bare
  // symbolet (popup), mens høyrepanelet — flata som er BYGGET for å lese et
  // objekt — sto igjen på «Velg et objekt på kartet». Objektlista gjorde det
  // samme via `focus()`. Nå fyller ETHVERT enhetsvalg panelet: navn, type/
  // symbol, MGRS, ORBAT-kjeden, «sist endret» — og handlingene bak KNAPPER,
  // så «se» (standard) og «endre» (valg) ikke blandes. Knappene GJENBRUKER
  // redigeringslogikken som allerede finnes (palettens endre-modus, ordre-
  // gangen, `units.remove`) — det samlede fanepanelet er uker-horisonten.
  function visEnhetIPanel(u: PlacedUnit) {
    const erBft = u.id === BFT_SELF_ID;
    const orbat = erBft ? null : units.orbatOppslag?.(u.id) ?? null;
    const d = enhetDetaljer(u, orbat, Date.now());
    // BFT er en måling — den kan ikke endres, gis ordre eller slettes herfra.
    const handlinger = erBft ? undefined : [
      { label: '✎ Endre type/symbol', run: () => unitPalette.openForEndring({ id: u.id, label: u.label, sidc: u.sidc }) },
      // Uten ordregrunnlag sier skrivForflytningsordre selv fra (alarm med
      // veien videre) — knappen lyver ikke, den forklarer.
      { label: 'Gi ordre …', run: () => skrivForflytningsordre(u) },
      { label: 'Slett (ta av kartet)', run: () => {
        if (units.remove(u.id)) {
          shell.clearDetail();
          shell.showAlarm(orbat
            ? `${u.label} tatt av kartet. Enheten står fortsatt i ORBAT.`
            : `${u.label} tatt av kartet.`);
        }
      } },
    ];
    shell.showDetailRows(d.tittel, d.undertittel, d.rader, handlinger);
  }
  units.onVelg = (u) => visEnhetIPanel(u);

  // ── ORBAT-VELGER: kartenheten ER ORBAT-enheten (kartmanøver fase 2) ───────
  //
  // Enhetspaletten over velger et SYMBOL og gir enheten en løpenummer-id. Da er
  // enheten ikke noe sted i styrkestrukturen, og alt nedstrøms må gjette: ordren
  // vet ikke hvilket nivå den gjelder, og kan derfor ikke følge GRUS (lag og
  // tropp skal ha IRFMI, ikke fempunktsordre). Velges enheten fra ORBAT i
  // stedet, blir kartenhetens id ORBAT-unit-id-en — ett navnerom, ett oppslag.
  //
  // Katalogen bygges per oppslag framfor å caches: ORBAT endres mens appen står
  // åpen (operatøren oppretter et lag; en annen operatørs hendelse kommer inn
  // via collab), og en cache fra oppstart ville svart for en struktur som ikke
  // finnes lenger. ~300 rader per popup-åpning er ikke målbart.
  const orbatKatalogNa = () => byggOrbatKatalog(orbatCreator.enheter());
  /** ORBAT-oppslag for enhetskortet: nivå + kommandokjede, ikke bare id-en. */
  units.orbatOppslag = (id) => {
    const post = orbatKatalogPost(id, orbatKatalogNa());
    return post ? { navn: post.navn, echelonNavn: post.echelonNavn, stiTekst: post.stiTekst } : null;
  };
  const orbatVelger = new OrbatVelgerPanel({
    enheter: () => orbatCreator.enheter(),
    plasserte: () => units.snapshot().map((u) => u.id),
    onVelg: (p, coord) => {
      if (coord) {
        units.placeAt(coord[0], coord[1], p.sidc, p.label, p.id);
        shell.showAlarm(`${p.label} plassert og koblet til ORBAT (${p.id}).`);
      } else {
        units.arm(p.sidc, p.label, p.id);
        shell.showAlarm(`Klikk på kartet for å plassere ${p.label} (ORBAT ${p.id}).`);
      }
    },
    // En enhet som alt står på kartet skal ikke kunne plasseres to steder — vis
    // hvor den er i stedet, så operatøren kan dra den dit han mente.
    onVis: (id) => units.focus(id),
  });

  // ── HENDELSESLOGG (høyre panel, øverst) ───────────────────────────────────
  // Ett sted der alt som skjer blir synlig: rapporter, varsler, statusendringer,
  // PERSTAT/LOGSTAT/RFI. Ingen ny datakilde — hvert kall står der handlingen
  // ALLEREDE skjedde, så loggen kan ikke komme ut av takt med virkeligheten.
  const hendelser = new Map<string, Hendelse>();
  function loggHendelse(h: Hendelse) {
    hendelser.set(h.id, h);
    // Cap mot ubegrenset vekst på en maskin som står på i døgn: eldste ut.
    if (hendelser.size > MAKS_HENDELSER * 2) {
      const eldste = [...hendelser.values()].sort((a, b) => a.tsMs - b.tsMs)[0];
      if (eldste) hendelser.delete(eldste.id);
    }
    tegnHendelser();
  }
  function tegnHendelser() {
    // Tida leses ETT sted per tegning, så alle rader måles mot samme nå.
    shell.setHendelser([...hendelser.values()], Date.now());
  }
  // Alderen må tikke selv om ingenting skjer — ellers står «nå» på en hendelse
  // som er en time gammel, og det er verre enn ingen alder.
  setInterval(() => tegnHendelser(), 15_000);

  // ── AAR (AAR-01) ───────────────────────────────────────────────────────
  // Samme hendelseskart som loggen over — ingen ny aggregering. `getHendelser`
  // leser `hendelser`-kartet direkte, så AAR-en alltid ser det operatøren ser
  // i tidslinja i det øyeblikket den genereres.
  const aarPanel = new AarPanel({
    getHendelser: () => [...hendelser.values()],
    naa: () => Date.now(),
    enhet: () => user.org?.district ?? '',
  });
  shell.mapEl.appendChild(aarPanel.el);
  shell.onGenererAar = () => aarPanel.toggle();

  // ── MYNDIGHET & ROE (matrisa over beslutningsrett, delt over hubben) ──────
  //
  // Eiers bestilling: autorisasjoner og ROE som en MATRISE fra nasjonalt nivå
  // til enkeltmann, tett knyttet til ORBAT, uten underskrifts-seremoni —
  // SPORET er kontrollen. Hver utstedelse/trekking går derfor både til
  // hendelsesloggen og over hubben (som stempler utsteder autoritativt).
  //
  // Persistens i localStorage (som Dagsedler): en delegasjon er en STÅENDE
  // ordre — den skal overleve en sidelasting, ellers har enheten mistet
  // beslutningsrett fordi noen trykket F5. Hubben replayer ikke collab-kinds
  // utover chat/tegning ennå, så lokal persistens er det ærlige nivået.
  const MYND_KEY = `skade.myndighet.v1.${user.org?.district ?? 'na'}`;
  const delegasjoner = new Map<string, Delegasjon>();
  try {
    for (const raw of JSON.parse(localStorage.getItem(MYND_KEY) ?? '[]') as unknown[]) {
      const d = parseDelegasjon(raw);
      if (d) delegasjoner.set(d.id, d);
    }
  } catch { /* korrupt lager — start tomt, matrisas iboende nivåer gjelder */ }
  function lagreDelegasjoner() {
    try { localStorage.setItem(MYND_KEY, JSON.stringify([...delegasjoner.values()])); } catch { /* fullt */ }
  }
  /** Nyeste versjon av posten vinner (en trukket post ER nyere enn utstedelsen). */
  function upsertDelegasjon(d: Delegasjon) {
    const e = delegasjoner.get(d.id);
    if (e && (e.trukketMs !== undefined || e.tsMs > d.tsMs) && d.trukketMs === undefined) return;
    delegasjoner.set(d.id, d);
    if (delegasjoner.size > 500) {
      const eldsteDode = [...delegasjoner.values()]
        .filter((x) => !erAktiv(x, Date.now())).sort((a, b) => a.tsMs - b.tsMs)[0];
      if (eldsteDode) delegasjoner.delete(eldsteDode.id);
    }
    lagreDelegasjoner();
  }
  function loggDelegasjon(d: Delegasjon) {
    const trukket = d.trukketMs !== undefined;
    const h = delegasjonsHendelse(d, trukket);
    loggHendelse({
      id: `myn-${d.id}-${trukket ? 'trukket' : 'aktiv'}`,
      tsMs: trukket ? (d.trukketMs ?? d.tsMs) : d.tsMs,
      type: 'autorisasjon', tittel: h.tittel, detalj: h.detalj,
      // En delegasjon som TRES INN er hastende (noen har fått beslutningsrett de
      // ikke hadde); en som TREKKES er like hastende (noen har mistet den, og kan
      // handle på gammel forutsetning). Begge IMMEDIATE, samme grunn.
      hastegrad: 'immediate',
      onClick: () => { if (!myndighetPanel.el.classList.contains('open')) myndighetPanel.toggle(); },
    });
  }
  /** Enhetens ORBAT-adresse + beslutningsnivå (FOH = nasjonal, over echelon). */
  const minEnhet: UnitAddress = {
    echelon: ROLE_COMMAND_ECHELON[user.role],
    district: user.org?.district, area: user.org?.area,
    troop: user.org?.troop, team: user.org?.team,
  };
  const mittNivaa = myndighetsNivaa(user);
  const myndighetPanel = new MyndighetPanel({
    self: { id: user.id, role: user.role, org: user.org, enhet: minEnhet, nivaa: mittNivaa },
    getDelegasjoner: () => [...delegasjoner.values()],
    publish: (d) => {
      upsertDelegasjon(d);
      loggDelegasjon(d);
      feed.send?.({ type: 'myndighet', myndighet: d });
    },
    naa: () => Date.now(),
  });
  shell.mapEl.appendChild(myndighetPanel.el);
  function toggleMyndighet() { myndighetPanel.toggle(); }

  // ── Stridsmeldinger (C2) ────────────────────────────────────────────────
  const reports = new Map<string, CombatReport>();
  let pendingReport: { type: ReportType; fields: Record<string, string>; omObjekt?: ObjektAnker } | null = null;

  function rebuildReports() {
    const items = [...reports.values()]
      .sort((a, b) => b.tsMs - a.tsMs)
      .map((r) => ({
        id: r.id, label: reportSummary(r), sub: `${r.type} · ${STATUS_LABEL[r.status]}`,
        aff: 'unknown', color: STATUS_COLOR[r.status], onClick: () => openThread(r),
      }));
    shell.setReports(items);
  }
  /**
   * Linjene mellom forankrede meldinger og objektet de handler om (funn F4).
   *
   * ⚠ TEGNES OM PÅ BÅDE NY MELDING OG NYE SPOR. Et spor beveger seg; en linje som
   * bare ble tegnet da meldingen kom, ville pekt på der kjøretøyet sto for fem
   * minutter siden — altså gitt operatøren en feil kobling framfor ingen.
   */
  function tegnAnkerlinjer() {
    const par: Array<{ fra: [number, number]; til: [number, number] }> = [];
    for (const r of reports.values()) {
      const a = r.omObjekt;
      if (!a || a.slag !== 'spor') continue;
      const t = lastTracks.find((x) => x.id === a.id);
      // Sluppet spor → ingen linje. Meldingen står igjen med sin frosne etikett,
      // og en linje ut i tomrommet ville påstått at objektet fortsatt er i bildet.
      if (!t) continue;
      par.push({ fra: [r.lon, r.lat], til: [t.lon, t.lat] });
    }
    cop.tegnAnkerlinjer(par);
  }

  function addReport(r: CombatReport, share: boolean) {
    const existing = reports.get(r.id);
    const merged = existing ? mergeReport(existing, r) : r; // konfliktfri sanntidsfletting
    reports.set(r.id, merged);
    cop.renderReports([...reports.values()]);
    tegnAnkerlinjer();
    rebuildReports();
    if (threadId === merged.id) threadView?.update(merged); // åpen tråd oppdateres live
    if (share) feed.send?.({ type: 'report', report: merged });
    // FUNN 24: en innsendt rapport var usynlig utenfor kartet og meldingslista.
    // NÅ gir hver melding — egen som mottatt — en linje i hendelsesloggen med det
    // samme. En ny melding logges som «rapport»; en endring på en melding vi
    // allerede hadde er en STATUS-endring, og skal ikke se ut som en ny melding.
    const nytt = !existing;
    loggHendelse({
      // Nøkkelen inkluderer `updatedMs`, så hver statusendring blir sin egen linje
      // i stedet for å overskrive historikken for det samme caset.
      id: `rep-${merged.id}-${nytt ? 'ny' : merged.updatedMs}`,
      tsMs: nytt ? merged.tsMs : (merged.updatedMs || merged.tsMs),
      type: nytt ? 'rapport' : 'status',
      // Hastegrads-gulvet per meldingstype (`REPORT_PRECEDENCE` i contracts) —
      // samme kilde som `toStanag` stempler meldingshodet med, så det operatøren
      // ser i loggen og det mottakeren får på wire er den SAMME påstanden.
      hastegrad: REPORT_PRECEDENCE[merged.type],
      tittel: reportSummary(merged),
      detalj: `${merged.type} · ${STATUS_LABEL[merged.status]} · ${kortId(merged.author)}${share ? '' : ' (mottatt)'}`,
      farge: STATUS_COLOR[merged.status],
      onClick: () => openThread(merged),
    });
  }
  function createReport(
    type: ReportType, fields: Record<string, string>, lon: number, lat: number,
    omObjekt?: ObjektAnker,
  ) {
    const id = `rep-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
    const now = Date.now();
    addReport({ id, type, lat, lon, tsMs: now, author: user.id, fields, status: 'ny', updatedMs: now, thread: [], omObjekt }, true);
  }
  const composer = new ReportComposer((type, fields, coord, omObjekt) => {
    if (coord) createReport(type, fields, coord[0], coord[1], omObjekt);
    else {
      // taOver FØRST: en påbegynt måling/akse skal avbrytes, ikke ligge og lytte
      // på samme klikk som plasserer meldingen.
      kartmodus.taOver('place-report');
      // ⚠ ANKERET BÆRES MED I VENTETILSTANDEN (funn F4). Uten dette ville en
      // melding forankret i et spor mistet forankringen i det øyeblikket
      // operatøren måtte peke på kartet — altså nettopp i det tilfellet der hun
      // presiserer HVOR på sporet observasjonen var.
      pendingReport = { type, fields, omObjekt };
      shell.showAlarm('Klikk på kartet for å plassere meldingen');
    }
  }, () => kontekst());

  // ── Ildstøtte / kill-chain ─────────────────────────────────────────────────
  const fireRings: FireRing[] = [];
  const fireTargets: FireTargetMark[] = [];
  const targetList: FireTarget[] = [];
  let targetSeq = 1000;
  let pendingFire: FireParams | null = null;
  let pendingWez: string | null = null;
  function renderFires() { cop.renderFireSupport(fireRings, fireTargets); }
  const targetPanel = new TargetListPanel({
    getTargets: () => targetList,
    onFocus: (t) => { cop.map.flyTo({ center: [t.lon, t.lat], zoom: 14 }); cop.highlightAt(t.lon, t.lat); },
    onAdvance: (id) => {
      const t = targetList.find((x) => x.id === id);
      if (!t) return;
      const n = nextFireStatus(t.status);
      if (!n) return;
      // MYNDIGHETSGATEN. Overgangen ready→shot ER frigivningen (se
      // erIldFrigivning i contracts) — å BE om ild kan enhver observatør, å
      // FRIGI den krever `ildstotte-frigivning` fra matrisa: iboende på ditt
      // nivå, eller delegert til NØYAKTIG din enhet. Avslaget sier hvorfor og
      // hvor svaret bor; frigivningen logges med kilden (iboende/delegert +
      // vilkår) — det er sporet som er kontrollen, ikke en underskrift.
      if (erIldFrigivning(t.status, n)) {
        const st = harMyndighet('ildstotte-frigivning', minEnhet, mittNivaa, [...delegasjoner.values()], Date.now());
        if (st.status === 'ingen') {
          shell.showAlarm('SKUDD IKKE FRIGITT — din enhet har ikke frigivningsmyndighet for ildstøtte. Se Myndighet & ROE.');
          if (!myndighetPanel.el.classList.contains('open')) myndighetPanel.toggle();
          return;
        }
        const kilde = st.status === 'iboende'
          ? `iboende myndighet (${NIVAA_LABEL[mittNivaa]})`
          : `delegert av ${st.delegasjon?.utstedtAv ?? '?'}${st.delegasjon?.vilkaar ? ` · vilkår: ${st.delegasjon.vilkaar}` : ''}`;
        loggHendelse({
          id: `ild-frigitt-${t.id}`, tsMs: Date.now(), type: 'autorisasjon',
          tittel: `Ild frigitt: ${t.number}`, detalj: `${user.id} · ${kilde}`,
          // IMMEDIATE: fra dette øyeblikket kan det falle ild i ruta. Alle som
          // beveger seg i området må se linja, ikke lete etter den.
          hastegrad: 'immediate',
          // ⚠ UX-rapportens Fase 1: «klikk på et kort → kartet flyr til stedet».
          // En rad uten `onClick` tegnes som en <div> og er død — og en frigitt
          // ild man ikke kan finne på kartet er den verst tenkelige raden å ha
          // uten klikk.
          onClick: () => { cop.map.flyTo({ center: [t.lon, t.lat], zoom: 14 }); cop.highlightAt(t.lon, t.lat); },
        });
      }
      t.status = n; targetPanel.render();
    },
    onRemove: (id) => fjernMaal(id),
  });

  /**
   * Fjern et mål OG geometrien som hører til det.
   *
   * Skilt ut som egen funksjon fordi mållista ikke lenger er den eneste veien
   * inn: Objekter-lista og Delete-gesten fjerner det samme målet, og geometri-
   * ryddingen under skal ikke finnes i to utgaver.
   */
  function fjernMaal(id: string) {
      const i = targetList.findIndex((x) => x.id === id);
      if (i >= 0) targetList.splice(i, 1);
      // ⚠ GEOMETRIEN MÅ FØLGE MÅLET. Før fjernet ✕ bare raden i lista, mens
      // fareavstands-ringen og målkrysset ble stående i kartet for alltid, uten
      // noen eier og uten noen vei ut. Det er samme feilklasse som
      // måleverktøyet i bølge 152: en handling som ser ut som veien ut, men
      // ikke er det.
      const ringerFør = fireRings.length;
      for (let k = fireRings.length - 1; k >= 0; k -= 1) {
        if (fireRings[k].targetId === id) fireRings.splice(k, 1);
      }
      for (let k = fireTargets.length - 1; k >= 0; k -= 1) {
        if (fireTargets[k].targetId === id) fireTargets.splice(k, 1);
      }
      if (fireRings.length !== ringerFør || fireTargets.length) renderFires();
      targetPanel.render();
  }

  /** Egne styrker for danger-close: vennlige spor (BFT/PLI) + plasserte enheter. */
  function friendlyPositions(): FriendlyPos[] {
    const out: FriendlyPos[] = [];
    for (const t of lastTracks) {
      if (t.affiliation === 'friend') out.push({ id: t.id, label: t.label, lat: t.lat, lon: t.lon });
    }
    // Inkluder alle vennlige identiteter (STANAG): F=venn, A=antatt venn,
    // D/M=øvelsesvenn, J/K=joker/faker (egne som spiller fiende). Fail-safe:
    // heller ta med en tvilsom «venn» enn å utelate en reell egen fra sjekken.
    for (const u of units.snapshot()) {
      if ('FADMJK'.includes(u.sidc[1] ?? '')) out.push({ id: u.id, label: u.label, lat: u.lat, lon: u.lon });
    }
    // Egen GPS-posisjon deltar ALLTID her, uavhengig av om BFT-deling er på.
    // Danger-close er en LOKAL sikkerhetssjekk på denne enheten, ikke en
    // kringkasting — «jeg deler ikke posisjon med andre» (BFT av/EMCON) skal
    // aldri bety «systemet varsler meg ikke om at MIN egen ildstøtte er farlig
    // nær MEG». `clearSelf()` fjerner BFT_SELF_ID helt fra `units` ved avslag,
    // så uten dette faller egen posisjon helt ut av sjekken idet BFT slås av.
    // Kartsenteret brukes ALDRI her (det er kun `velgEgenposisjon` sin jobb) —
    // et sted operatøren bare ser på er ikke der operatøren står, og ville gitt
    // enten en falsk trygghet eller en falsk alarm.
    if (gpsFix && Date.now() - gpsFix.tsMs <= GPS_FERSK_MS) {
      out.push({ id: 'gps:self', label: 'Egen posisjon (GPS)', lat: gpsFix.lat, lon: gpsFix.lon });
    }
    return out;
  }

  function placeFireMission(p: FireParams, lon: number, lat: number) {
    const dc = dangerCloseCheck({ lat, lon }, friendlyPositions(), p.weapon);
    // Fratricide-gate: ved DANGER CLOSE må en KOMMANDANT faktisk godkjenne.
    // Vi auto-stempler ALDRI autorisasjon; operatøren blir bedt om reelle
    // kommandant-initialer. Avbryter operatøren, står brief-en som utestående.
    if (dc.declareDangerClose) {
      const nearest = dc.within[0];
      sporTekst({
        tittel: '⚠ DANGER CLOSE',
        tekst: `${dc.within.length} egne innenfor ${dc.radiusM} m ` +
          `(nærmest ${nearest.friendly.label} ${Math.round(nearest.distanceM)} m).\n\n` +
          'Skriv KOMMANDANTENS initialer for å autorisere ild — la stå tomt for å sende UAUTORISERT (utestående):',
        bekreftTekst: 'Autoriser',
        onSvar: (entered) => {
          const authInitials = entered && entered.trim() ? entered.trim().slice(0, 16) : undefined;
          fullforFireMission(p, lon, lat, dc, authInitials);
        },
      });
      return;
    }
    fullforFireMission(p, lon, lat, dc, undefined);
  }

  function fullforFireMission(
    p: FireParams, lon: number, lat: number,
    dc: ReturnType<typeof dangerCloseCheck>, authInitials: string | undefined,
  ) {
    const cff = CallForFireSchema.parse({
      id: `cff-${Date.now().toString(36)}`, observer: myCallsign,
      missionType: p.missionType, targetLat: lat, targetLon: lon,
      targetDesc: p.targetDesc, ammunition: p.ammunition, control: p.control,
      weapon: p.weapon, dangerClose: dc.declareDangerClose,
      authInitials,
      tsMs: Date.now(),
    });
    // `targetId` binder ring og kryss til målet, slik at mållistas ✕ kan ta med
    // seg geometrien. Uten den ble ringen foreldreløs (se FireRing).
    fireRings.push({ ring: dangerCloseRing({ lat, lon }, p.weapon), role: 'danger', targetId: cff.id });
    fireTargets.push({ lat, lon, label: `${FIRE_WEAPON_LABEL[p.weapon]}: ${p.targetDesc}`, targetId: cff.id });
    renderFires();
    // Legg målet i mållista (kill-chain-sporing).
    targetList.push(FireTargetSchema.parse({
      id: cff.id, number: `AB${targetSeq++}`, desc: p.targetDesc, lat, lon,
      priority: dc.declareDangerClose ? 'priority' : 'routine', status: 'requested', tsMs: Date.now(),
    }));
    targetPanel.render();
    cop.highlightAt(lon, lat);
    // Del ildmålet som stridsmelding (CONTACT) så TOC/naboer ser kill-chain.
    createReport('CONTACT', { enemy: p.targetDesc, action: buildCffText(cff) }, lon, lat);
    if (dc.declareDangerClose) {
      shell.showAlarm(authInitials
        ? `⚠ DANGER CLOSE autorisert av ${authInitials} — ${dc.within.length} egne innenfor ${dc.radiusM} m.`
        : `⚠ DANGER CLOSE UAUTORISERT — brief sendt som UTESTÅENDE. Krever kommandant-godkjenning før ild.`);
    } else {
      shell.showAlarm(`Ildmål plassert (${FIRE_WEAPON_LABEL[p.weapon]}). CFF-brief lagt i stridsmelding.`);
    }
  }

  function placeWez(threatId: string, lon: number, lat: number) {
    const sys = THREAT_SYSTEMS.find((s) => s.id === threatId);
    if (!sys) return;
    fireRings.push({ ring: engagementRing({ lat, lon }, sys.rangeM), role: 'wez' });
    fireTargets.push({ lat, lon, label: `WEZ: ${sys.name}` });
    renderFires();
    shell.showAlarm(`Trussel-ring plassert: ${sys.name} (${(sys.rangeM / 1000).toFixed(0)} km rekkevidde).`);
  }

  const fireComposer = new FireSupportComposer(
    (p) => { kartmodus.taOver('place-fire'); pendingFire = p; pendingWez = null; shell.showAlarm('Klikk på kartet for å plassere ildmålet'); },
    (threatId) => { kartmodus.taOver('place-fire'); pendingWez = threatId; pendingFire = null; shell.showAlarm('Klikk på kartet for å plassere trussel-ringen'); },
  );
  /**
   * «Ta alle enheter av kartet» — med bekreftelse.
   *
   * ⚠ UX-funn 2026-08-14: bekreftelses-asymmetrien gikk MOTSATT VEI. `bekreft()`
   * hadde to kallsteder i hele appen («Slippe sporet?» og «Begynne å dele egen
   * posisjon?»), mens å slette ALT operatøren hadde plassert gikk på ett klikk
   * uten anger og uten angre-mulighet. Friksjonen hører der konsekvensen er
   * størst.
   */
  function bekreftAlleEnheterAv() {
    const n = units.snapshot().filter((u) => u.id !== BFT_SELF_ID).length;
    if (!n) { shell.showAlarm('Ingen plasserte enheter på kartet.'); return; }
    bekreft({
      tittel: `Ta ${n} enhet(er) av kartet?`,
      tekst: 'Alle enheter DU har plassert fjernes fra situasjonsbildet, også for '
        + 'de andre operatørene. Det finnes ingen angre-funksjon.',
      punkter: [
        'ORBAT-strukturen røres ikke — enhetene består der.',
        'Andre operatørers enheter berøres ikke.',
      ],
      bekreftTekst: 'Ta alle av kartet',
      onBekreft: () => { units.clear(); shell.showAlarm(`${n} enhet(er) tatt av kartet.`); },
    });
  }

  function clearFires() { fireRings.length = 0; fireTargets.length = 0; renderFires(); shell.showAlarm('Ildstøtte-laget tømt.'); }

  // ── Terreng, sikt og lysforhold (@skade/terrain) ────────────────────────────
  // Erstatter den gamle SunCalc-avlesningen (contracts/astro.ts): to
  // sol-implementasjoner ville gitt ULIKE tider i panel og skyggelag, som er
  // nettopp den slags inkonsistens vi rydder bort. Alle fire modulene deler ÉN
  // flis-cache, så høydedata for samme område hentes bare én gang.
  const demCache = new TerrariumTileCache();
  const kartSenter = () => { const c = cop.map.getCenter(); return { lon: c.lng, lat: c.lat }; };
  const kartBoks = () => {
    const b = cop.map.getBounds();
    return { north: b.getNorth(), south: b.getSouth(), west: b.getWest(), east: b.getEast() };
  };

  const lysforhold = new LysforholdPanel({ center: kartSenter });
  shell.mapEl.appendChild(lysforhold.el);
  function toggleLysforhold() { lysforhold.toggle(); }

  const sikt = new SiktPanel(cop.map, { cache: demCache, center: kartSenter, alarm: (t) => shell.showAlarm(t) });
  shell.mapEl.appendChild(sikt.el);
  function toggleSikt() { sikt.toggle(); }
  // Dock-merket lyste ALDRI for terrengverktøyene, i motsetning til rutenett og
  // trådkors: `onStateChange` var implementert i panelene, men aldri tilordnet.
  sikt.onStateChange = () => dock?.setActive('sikt', sikt.apen);
  // Siktelinja er et OBJEKT i høyrepanelet, og lista skal endre seg når linja gjør
  // det (nytt punkt, nytt resultat, nullstilt).
  sikt.onEndring = () => objekter.meld();
  /** Fjern siktelinja fra kartet. Panelets × betyr LUKK, ikke slett — derfor
   * må sletting finnes som sin egen, synlige handling (jf. bølge 152). */
  function slettSikt() {
    sikt.nullstill();
    shell.showAlarm('Siktelinja er fjernet fra kartet.');
  }

  // ── Målpeking i kameraet («target toggle», bølge MAAL-01) ─────────────────
  // Se vale/MAALPEKING_KONSEPT_2026-08-17.md. Horisont+havflate (avstand,
  // ingen DEM) og landemerke-resection (asimut) — komplementære til
  // maalestimat.ts sin stråle-mot-DEM (bølge 193), som ikke svarer for mål på
  // vann eller vannrette bilder.
  const malpeking = new MalpekingPanel(cop.map, { alarm: (t) => shell.showAlarm(t) });
  shell.mapEl.appendChild(malpeking.el);
  function toggleMalpeking() { malpeking.toggle(); }
  malpeking.onStateChange = () => dock?.setActive('malpeking', malpeking.apen);
  malpeking.onEndring = () => objekter.meld();
  function nullstillMalpeking() {
    malpeking.nullstill();
    shell.showAlarm('Målpekingen er fjernet fra kartet.');
  }

  const dekning = new DekningLag(cop.map, { cache: demCache, center: kartSenter, alarm: (t) => shell.showAlarm(t) });
  shell.mapEl.appendChild(dekning.el);
  function toggleDekning() { dekning.toggle(); }
  dekning.onStateChange = () => dock?.setActive('dekning', dekning.apen);
  dekning.onEndring = () => objekter.meld();
  /** Fjern dekningsflaten/viewshed fra kartet. `tøm()` fantes ferdig skrevet i
   * `map/dekning.ts` men ble ALDRI kalt — nøyaktig samme «manglende ledning» som
   * måleverktøyet hadde i bølge 152. Dette er ledningen. */
  function slettDekning() {
    dekning.tøm();
    shell.showAlarm('Dekningsberegningen er fjernet fra kartet.');
  }

  const skygge = new SkyggeLag(cop.map, { bounds: kartBoks, varsle: (t) => shell.showAlarm(t) });
  shell.mapEl.appendChild(skygge.el);
  function toggleSkygge() { skygge.toggle(); }

  // GEO-08: ferdige fotogrammetriprodukter (GEO-03/04) som togglbare rasterlag.
  const geointLag = new GeointLag(cop.map, {
    hent: () => arkivHent('/geoint/jobber') as Promise<GeointJobberSvar>,
    varsle: (t) => shell.showAlarm(t),
    // GEO-09: samme endepunkt panelet allerede har lest fra i hubben
    // (`POST /geoint/jobb/avbryt`) — bekreftet her (bekreft()) fordi et
    // avbrudd stopper reelt arbeid på noden, ikke bare et lokalt utkast.
    // ⚠ Lukker operatøren dialogen uten å bekrefte, kalles `onBekreft` aldri —
    // det løftet her forblir da bevisst uavgjort (ingen handling er riktig
    // handling ved avbrutt bekreftelse), i stedet for å late som noe skjedde.
    avbryt: (jobbId) => new Promise((resolve, reject) => {
      bekreft({
        tittel: 'Avbryte GEOINT-jobben?',
        tekst: 'Jobben stoppes på prosesseringsnoden og kan ikke gjenopptas — den må sendes inn på nytt om den trengs igjen.',
        bekreftTekst: 'Avbryt jobben',
        avbrytTekst: 'Behold jobben',
        onBekreft: () => { arkivSend('/geoint/jobb/avbryt', { id: jobbId }).then(() => resolve()).catch(reject); },
      });
    }),
  });
  shell.mapEl.appendChild(geointLag.el);
  geointLag.onStateChange = () => dock?.setActive('geoint', geointLag.open);
  function toggleGeointLag() { geointLag.toggle(); }

  // 3D-terreng: eierens førstevalg. Av/på-bryteren står ETT sted — i kartlag-
  // velgeren på kartet (funn 25b; se `TERRENG_EIES_AV_KARTVELGEREN`). Rapporterer
  // ærlig hvis DEM-kilden ikke kunne aktiveres.
  // Slå på 3D som førstevalg så snart stilen er klar (eier-krav). Idempotent:
  // aktiver3dStandard rører ikke noe hvis terrenget alt er på, og returnerer
  // false hvis DEM-kilden ikke kunne aktiveres — da sier vi det i stedet for å
  // late som kartet er tredimensjonalt.
  if (cop.map.isStyleLoaded()) aktiver3dStandard(cop.map);
  else cop.map.once('load', () => { aktiver3dStandard(cop.map); });

  // Et grunnkart som ikke laster skal ALDRI feile stille (funn 25c). Målt: 2 av 18
  // flyfoto-fliser svarte HTTP 503 uten at operatøren fikk et ord om det, og
  // HTTP 404 gir ikke engang en error-hendelse fra MapLibre. Kobles til
  // alarmbanneret — samme kanal som resten av kartvarslene.
  overvaakBasiskart(cop.map, (tekst) => shell.showAlarm(tekst));

  // Kalles fra kommandopaletten. Kartlag-velgeren speiler tilstanden selv via
  // `map.on('terrain')`, så bryteren der oppdateres uansett hvor 3D slås på.
  function toggle3d() {
    const på = settTerreng(cop.map, !terrengPaa(cop.map));
    shell.showAlarm(på ? '3D-terreng på — dra med høyreknapp/Ctrl for å vippe kameraet.' : '3D-terreng av (flatt kart).');
  }
  shell.onNewReport = () => composer.toggle();
  shell.onPopoutC2 = () => popOut('c2');

  // ── Melding som «case»: tråd-visning + statusflyt (CRM/OODA) ─────────────
  let threadView: ReportThreadView | null = null;
  let threadId: string | null = null;
  const threadHost = document.createElement('div');
  threadHost.className = 'thread-host';
  root!.appendChild(threadHost);
  function closeThread() {
    threadHost.classList.remove('open');
    threadHost.innerHTML = '';
    threadView = null; threadId = null;
    cop.clearHighlight();
  }
  function mutateReport(id: string, fn: (r: CombatReport) => CombatReport) {
    const cur = reports.get(id);
    if (!cur) return;
    addReport({ ...fn(cur), updatedMs: Date.now() }, true);
  }
  function openThread(r: CombatReport) {
    cop.flyToReport(r); // fly + pulserende highlight (skiller seg ut blant hundrevis)
    threadId = r.id;
    threadView = new ReportThreadView(reports.get(r.id) ?? r, {
      onStatus: (s) => mutateReport(r.id, (rep) => ({ ...rep, status: s })),
      onComment: (text) => mutateReport(r.id, (rep) => ({
        ...rep,
        thread: [...rep.thread, { id: `c-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`, author: user.id, tsMs: Date.now(), text }],
      })),
      onGoto: () => { const cur = reports.get(r.id); if (cur) cop.flyToReport(cur); },
      onClose: () => closeThread(),
    });
    threadHost.innerHTML = '';
    threadHost.appendChild(threadView.el);
    threadHost.classList.add('open');
  }
  cop.onReportSelect = (r) => openThread(r);

  // ── Etterretning (INTEL) — data→informasjon→etterretning ────────────────
  const intelHost = document.createElement('div');
  intelHost.className = 'intel-host';
  root!.appendChild(intelHost);
  // KART FØRST (UX_DESIGNSYSTEM.md §2, Bølge 241/SENS-45): «Bruk kartsenter»
  // fantes, men krevde at analytikeren FØRST panorerte dit — direkte kartklikk
  // manglet. Samme kartmodus-mønster som RFI-stedet/kapabilitetsplasseringen.
  let pendingIntelSted: ((mgrs: string) => void) | null = null;
  const intelWs = new IntelWorkspace({
    user,
    onShare: (p) => feed.send?.({ type: 'intel', intel: p }),
    getReports: () => [...reports.values()],
    onFocus: (lat, lon) => { cop.map.flyTo({ center: [lon, lat], zoom: 14 }); cop.highlightAt(lon, lat); },
    // Sted-feltet (funn 8): «Bruk kartsenter» leser trådkorspunktet — det er
    // KARTSENTERET (det analytikeren ser på), aldri egen posisjon, og verdien
    // går synlig inn i feltet der den kan rettes før lagring.
    hentKartsenter: () => { const c = cop.map.getCenter(); return { lat: c.lat, lon: c.lng }; },
    armStedKlikk: (ferdig) => {
      kartmodus.taOver('place-intel-sted');
      pendingIntelSted = ferdig;
      shell.showAlarm('Klikk i kartet der produktet gjelder.');
    },
  });
  intelHost.appendChild(intelWs.el);
  function toggleIntel() { intelHost.classList.toggle('open'); }
  kartmodus.registrer({
    id: 'place-intel-sted',
    navn: 'PLASSER ETTERRETNINGSSTED',
    stopp: () => { if (pendingIntelSted) { pendingIntelSted = null; kartmodus.frigi('place-intel-sted'); } },
  });
  cop.map.on('click', (e) => {
    if (!pendingIntelSted) return;
    const ferdig = pendingIntelSted;
    pendingIntelSted = null;
    kartmodus.frigi('place-intel-sted');
    let mgrs: string;
    try { mgrs = mgrsForward([e.lngLat.lng, e.lngLat.lat], 5); } catch { return; }
    ferdig(mgrs);
  });

  // ── KONTEKST: ÉN kilde til hvem/hvor/når/enhet for alle skjemaer ──────────
  // Alle rapportskjemaene (stridsmelding, PERSTAT, LOGSTAT, RFI) henter herfra.
  // Før dette leste hvert panel sine egne verdier — eller lot feltet stå tomt —
  // og «kartsenter» og «egen posisjon» ble blandet fritt.
  function egenposisjon(): Egenposisjon | null {
    const c = cop.map.getCenter();
    return velgEgenposisjon(gpsFix, { lon: c.lng, lat: c.lat }, Date.now());
  }
  /** Tida leses HER (én gang per skjema-oppfriskning) og sendes inn i panelene. */
  function kontekst(): Kontekst {
    return byggKontekst({ bruker: user, posisjon: egenposisjon(), naaMs: Date.now() });
  }

  // ── Feltmelding / PTT (geolokalisert, lyser opp i kartet, TTS) ──────────
  const fieldMsgs = new Map<string, FieldMessage>();
  /**
   * Posisjonen en feltmelding/ordre knyttes til. Rekkefølgen er nå eksplisitt:
   * fersk GPS → kartsenter (`velgEgenposisjon`), i stedet for «første spor med
   * kind==='sensor'» — som i praksis kunne være en HVILKEN SOM HELST SPARK-node
   * i feeden, ikke operatøren selv.
   */
  function myPosition(): [number, number] {
    const p = egenposisjon();
    if (p) return [p.lon, p.lat];
    const c = cop.map.getCenter();
    return [c.lng, c.lat];
  }
  function addField(m: FieldMessage, incoming: boolean) {
    fieldMsgs.set(m.id, m);
    // Behold de siste 50 for å unngå ubegrenset vekst på kartet.
    if (fieldMsgs.size > 50) {
      const oldest = [...fieldMsgs.values()].sort((a, b) => a.tsMs - b.tsMs)[0];
      if (oldest) fieldMsgs.delete(oldest.id);
    }
    cop.renderFields([...fieldMsgs.values()]);
    // «Lyser opp»: puls der handlingen er (markør hvis tolket, ellers avsender).
    cop.highlightAt(m.markLon ?? m.lon, m.markLat ?? m.lat);
    const who = m.author.replace(/^@/, '').replace(/:.*$/, '');
    if (incoming && ptt.ttsEnabled) speak(`${who}: ${m.text}`);
    // Feltmeldingen var ALLEREDE gjort riktig på kartet (markør + puls) — det var
    // malen de andre rapportene manglet. Nå havner den også i loggen, så den kan
    // finnes igjen etter at pulsen er borte.
    loggHendelse({
      id: `fld-${m.id}`, tsMs: m.tsMs, type: 'melding',
      tittel: m.text, detalj: `${who}${incoming ? ' (mottatt)' : ''}`,
      // Fri feltmelding: vi vet ikke hva som står i den. Samme resonnement som
      // `REPORT_PRECEDENCE.MELDING` — å UNDERVURDERE en melding ingen har lest er
      // en verre feil enn å overvurdere den. PRIORITY er dessuten holdt utenfor
      // haste-båndet, så vanlig trafikk kan ikke fylle varselsona.
      hastegrad: 'priority',
      onClick: () => {
        cop.map.flyTo({ center: [m.markLon ?? m.lon, m.markLat ?? m.lat], zoom: 14 });
        cop.highlightAt(m.markLon ?? m.lon, m.markLat ?? m.lat);
      },
    });
  }
  /**
   * Send en feltmelding: markør i kartet, delt med alle, ført i loggen.
   *
   * ⚠ ÉN SENDEVEI for både «Meld fra»-linja og det utvidede panelet (§3). Sto den
   * i to callbacks, ville en rettelse i den ene vært usynlig i den andre.
   */
  function sendFeltmelding(text: string): void {
    const [lon, lat] = myPosition();
    const m = makeFieldMessage({
      id: `fld-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`,
      author: user.id, tsMs: Date.now(), lat, lon, text,
    });
    addField(m, false);
    feed.send?.({ type: 'field', field: m });
    // ⚠ FØRES I DEN FELLES TIDSLINJA (UX-funn F5). Feltmeldinger nådde loggen
    // via `melding`-typen, men bare noen av dem — og en logg man ikke kan stole
    // på som fasit tvinger operatøren til å åpne fire flater for å forstå timen
    // som gikk.
    loggHendelse({
      id: `fld-${m.id}`, tsMs: m.tsMs, type: 'melding',
      tittel: text.slice(0, 90),
      hastegrad: 'priority', // som mottatt feltmelding: ulest innhold, ikke undervurder
      detalj: m.markLat !== undefined ? 'Markør satt i tolket retning' : 'Markør i egen posisjon',
      farge: 'var(--gold)',
      onClick: () => cop.map.flyTo({ center: [m.markLon ?? lon, m.markLat ?? lat], zoom: 14 }),
    });
    shell.showAlarm(m.markLat !== undefined ? 'Feltmelding sendt — markør satt i tolket retning' : 'Feltmelding sendt');
  }

  const ptt = new PttPanel({ onSend: (text) => sendFeltmelding(text) });
  function togglePtt() { ptt.toggle(); }

  // ── «MELD FRA»-LINJA — SYSTEMETS HOVEDVEI (UX-funn F2, HØY) ───────────────
  //
  // UX-gjennomgang 2026-08-18: «si det → markør på kartet» er nøyaktig
  // Delta-følelsen, og den lå som oppføring **5 av 5** i undermenyen «Meldinger».
  // ⚠ Verifisert i koden før endring: dock-gruppa `g-meld` hadde
  // `{ id: 'ptt', label: 'Feltmelding / PTT' }` sist i lista. Rapporten har
  // bokstavelig rett.
  //
  // ⚠ NAVNET ER ENDRET, OG DET LØSER TO FUNN. Rapportens F6 er
  // begrepsforvirring — fire ord for samme verb. Samtidig har vi nettopp bygget
  // EKTE push-to-talk (LiveKit-talekanal, `pttvakt.ts`). «Feltmelding / PTT» på
  // en funksjon som ikke overfører lyd ville blitt direkte misvisende i det
  // øyeblikket talekanalen kommer. Derfor: dette er **«Meld fra»**, og «PTT»
  // betyr fra nå av bare talekanalen.
  //
  // ⚠ ÉN SENDEVEI, IKKE TO. Både linja og panelet kaller `sendFeltmelding` —
  // ellers ville to kodeveier drevet fra hverandre, og en rettelse i den ene
  // vært usynlig i den andre (§3).
  const meldFra = new MeldFraLinje({
    onSend: (tekst) => sendFeltmelding(tekst),
    // UX-rapportens Fase 2: «struktur er en oppgradering, ikke et hinder».
    // Chipen tar med seg det operatøren alt har skrevet inn i komponisten, og
    // `oppgraderFritekst` (contracts) bestemmer HVILKET felt ordene havner i —
    // ordrett, i meldingstypens fritekstfelt, aldri i et strukturert felt.
    onOppgrader: (type, tekst) => {
      // Kartpunktet vinner hvis operatøren alt har pekt: da får meldingen stedet
      // hun mente, ikke stedet hun står. Uten punkt åpnes komponisten som før, og
      // observasjonsstedet forhåndsutfylles ALDRI stille (se `observasjonsForvalg`).
      composer.openMedFritekst(type, tekst);
      shell.showAlarm(`Oppgradert til ${type} — teksten din er med. Fyll ut feltene og plasser meldingen.`);
    },
  });
  shell.mapEl.appendChild(meldFra.el);

  // ── Tidsmaskin (opptak + avspilling av situasjonsbildet, økt-lokalt) ─────
  const pbBanner = document.createElement('div');
  pbBanner.className = 'playback-banner';
  shell.mapEl.appendChild(pbBanner);
  const timeMachine = new TimeMachine(
    shell.mapEl,
    (tracks) => cop.render(tracks), // avspilling driver kartet
    (live, label) => { pbBanner.classList.toggle('show', !live); pbBanner.textContent = label; },
  );
  function toggleTime() { timeMachine.toggle(); }

  // ── BFT / egen posisjon (Blue Force Tracking) ───────────────────────────
  //
  // ⚠ TO FEIL ER RETTET HER, OG DE ER ASYMMETRISKE MED VILJE (som 112-knappen):
  //
  // (a) Å BEGYNNE å dele var ETT klikk. «BFT / min posisjon» i verktøylinja,
  //     kommandopaletten og høyreklikk-menyen kringkastet umiddelbart egen
  //     posisjon til hele nettet. Etiketten sier ikke at det er det som skjer, og
  //     handlingen kan ikke trekkes tilbake — de som mottok posisjonen har den.
  //     Nå ligger en bekreftelse foran. Å SLÅ AV er fortsatt ett klikk: friksjon
  //     hører på å begynne å sende, aldri på å slutte.
  //
  // (b) Markøren ble LIGGENDE når BFT ble slått av — som et navngitt enhetsobjekt
  //     i kartet og i Objekter-panelet, uten alder. Verre: `units`-settet
  //     kringkastes ved hver endring, går inn i CoT/GeoJSON-eksport og teller som
  //     egen styrke i danger-close-sjekken. «Posisjon deles ikke lenger» var
  //     altså usant. VALGT LØSNING: markøren FJERNES ved deaktivering. Grunnen
  //     til at det er riktig og ikke bare enklest — å slå av deling må STOPPE
  //     delingen, ellers lyver bryteren — og informasjonen går ikke tapt: EGEN
  //     posisjon står alltid i MGRS-kortet nederst til høyre, live fra GPS,
  //     uavhengig av BFT. Så lenge BFT er PÅ bærer markøren måletidspunkt og
  //     alder (grunnloven: alder på en måling skal alltid vises), og roper
  //     «GAMMEL» av seg selv hvis fiksene stopper.
  let bftOn = false;
  const myCallsign = user.id.replace(/^@/, '').replace(/:.*$/, '');
  cop.onOwnPosition((lon, lat) => {
    // EGEN posisjon vises ALLTID nederst til høyre (uavhengig av BFT-deling).
    try { shell.setSelfCoord(mgrsForward([lon, lat], 5)); } catch { /* utenfor sone */ }
    // Samme avlesning mater kontekst-kilden — MED tidsstempel, så en gammel fix
    // ikke kan presentere seg som «hvor jeg er nå» i et skjema (`kontekst.ts`).
    gpsFix = { lon, lat, tsMs: Date.now() };
    if (bftOn) units.setSelf(lon, lat, myCallsign, Date.now());
  });
  // Et avslag eller en GPS-timeout SKAL sies høyt. Før endte begge som en evig
  // «ukjent» i EGEN-feltet, og operatøren kunne ikke skille «vet ikke hvor jeg
  // er» fra «nekter å si hvor jeg er» — to helt ulike tilstander i felt.
  // Bare første gang: en nettleser som nekter, nekter ved hvert forsøk, og en
  // alarm per forsøk ville dekket kartet.
  let gpsFeilVarslet = false;
  cop.onOwnPositionError((grunn) => {
    if (gpsFeilVarslet) return;
    gpsFeilVarslet = true;
    shell.showAlarm(`Egen posisjon utilgjengelig: ${grunn}`);
  });
  // ── SLIPP SPOR («drop track») ────────────────────────────────────────────
  //
  // EIERKRAV 2026-08-13: «jeg vil jo at siste posisjon skal bli liggende — til
  // jeg selv velger drop track. Hvis jeg mister en soldat i felt, er det jo
  // håpløst hvis siste spor var for mer enn fem minutter siden.»
  //
  // Personspor auto-slettes derfor ikke lenger i hubben; dette er den eneste
  // veien ut. Bekreftelse kreves fordi handlingen fjerner siste kjente posisjon
  // fra ALLE operatørers skjerm — det er en beslutning, ikke en visningsvalg.
  // ── OBSERVASJON → ETTERRETNINGSPRODUKT (IMINT) ───────────────────────────
  //
  // ⚠ RETTELSE AV MIN EGEN PÅSTAND (2026-08-17): jeg meldte at `ui/imint.ts` var
  // «skrevet, 0 kallsteder». DET VAR FEIL — `IntelWorkspace` har brukt den hele
  // tiden, både skrivebeskyttet i produktvisningen og redigerbart i editoren. Jeg
  // hadde grep'et bare `main.ts`, ikke hele `apps/`. Samme metodefeil som med
  // `alertfeed.ts` dagen før: et kallsted-søk må gå på SYMBOLNAVN over hele
  // treet, aldri på filnavn i én fil.
  //
  // Det som FAKTISK manglet var seks manuelle steg: finn bilde-URL i sporpanelet,
  // kopiér, åpne Etterretning, nytt produkt, lim inn URL, annotér. I praksis
  // betyr seks steg at det ikke blir gjort. Dette er broen — ett klikk fra
  // observasjonen til et ferdig utfylt produktutkast.
  shell.onImint = (t, bildeUrl) => {
    const naa = Date.now();
    // Grunnlaget er det sporet ALT vet: sensor, retning, rekkevidde, opphav.
    // Analytikeren skal ikke måtte skrive av panelet sitt eget innhold.
    const grunnlagLinjer = [
      `Observasjon: ${t.label}`,
      `Tid: ${new Date(t.tsMs).toISOString().slice(0, 19)}Z`,
      `Kilde: ${t.source}`,
    ];
    for (const nokkel of ['Sensor', 'Retning', 'Rekkevidde', 'Synsfelt', 'Rapportert av', 'Objekt', 'Konfidens', '⚠ Opphav', '⚠ Kalibrering']) {
      const v = t.extra?.[nokkel];
      if (v) grunnlagLinjer.push(`${nokkel}: ${v}`);
    }
    intelWs.nyttProduktFraObservasjon({
      imageUrl: bildeUrl,
      tittel: t.label,
      grunnlag: grunnlagLinjer.join('\n'),
      lat: t.lat,
      lon: t.lon,
      mgrs: latLonToMgrs({ lat: t.lat, lon: t.lon }),
      // Gyldigheten starter når observasjonen ble gjort, ikke når analytikeren
      // åpnet editoren — ellers ser et bilde fra i går ferskt ut i produktet.
      observertMs: t.tsMs || naa,
    });
    intelHost.classList.add('open');
  };

  shell.onDroppSpor = (t) => {
    const alder = t.tsMs ? ` (sist sett ${new Date(t.tsMs).toISOString().slice(11, 19)}Z)` : '';
    bekreft({
      tittel: `Slippe sporet «${t.label}»?`,
      tekst: `Siste kjente posisjon${alder} fjernes fra situasjonsbildet for alle `
        + 'operatører. Handlingen føres i revisjonsloggen med din bruker-id.',
      punkter: [
        'Sporet kommer tilbake av seg selv hvis enheten rapporterer igjen.',
        'Er personen savnet, MISTER du utgangspunktet for søket ved å slippe det.',
      ],
      bekreftTekst: 'Slipp sporet',
      onBekreft: () => {
        void fetch('/spor/dropp', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: t.id }),
        }).then(async (r) => {
          if (r.ok) {
            shell.showAlarm(`Sporet «${t.label}» er sluppet.`);
            shell.clearDetail();
            loggHendelse({
              id: `dropp-${t.id}-${Date.now()}`, tsMs: Date.now(), type: 'melding',
              // Ryddearbeid på eget bilde. ROUTINE gir ingen stripe — riktig, for
              // ingen skal se etter denne linja.
              hastegrad: 'routine',
              tittel: `Spor sluppet: ${t.label}`,
              detalj: 'siste kjente posisjon fjernet fra bildet',
              farge: 'var(--text-secondary)',
            });
          } else {
            // Ærlig: sporet kan alt være sluppet av en annen operatør.
            shell.showAlarm(`Kunne ikke slippe sporet (${r.status}).`);
          }
        }).catch(() => shell.showAlarm('Kunne ikke slippe sporet — ingen forbindelse til hubben.'));
      },
    });
  };

  // Klikk på den blå GPS-prikken skal svare om EGEN enhet. Kartet eier
  // avlesningen (nøyaktighet, høyde, fart, kurs, måletid); vi fyller det kartet
  // ikke kan vite: hvem vi er, om posisjonen deles, og MGRS-formatet.
  cop.settEgenposisjonKontekst((fix) => {
    let mgrs = '';
    try { mgrs = mgrsForward([fix.lon, fix.lat], 5); } catch { /* utenfor sone */ }
    return {
      kallesignal: myCallsign,
      deles: bftOn,
      mgrs,
      vekslDeling: () => toggleBft(),
    };
  });
  // Start GPS-sporing ved oppstart så EGEN-avlesningen fylles (deler ikke — kun lokal).
  try { cop.triggerGeolocate(); } catch { /* bruker kan avslå */ }
  // Alderen på BFT-målingen går videre selv om GPS-en tier. Uten dette tikket
  // ville etiketten og objektlista stå fersk for alltid etter siste fiks.
  setInterval(() => { if (bftOn) { units.oppfrisk(); rebuildRoster(); } }, 10_000);
  function slaAvBft() {
    bftOn = false;
    units.clearSelf(); // ingen etterlatt markør → ingen videre deling
    shell.showAlarm('BFT av — posisjon deles ikke lenger, og markøren er fjernet');
  }
  function toggleBft() {
    if (bftOn) { slaAvBft(); return; } // AV = ett klikk, ingen bekreftelse
    bekreft({
      tittel: 'Begynne å dele egen posisjon?',
      tekst: `BFT sender posisjonen din som en vennlig enhet («${myCallsign}») til alle som er `
        + 'koblet til nettet, fortløpende, til du slår det av.',
      punkter: [
        'Posisjonen kan ikke hentes tilbake fra dem som har mottatt den.',
        'Markøren bærer måletidspunkt og alder, og fjernes når du slår BFT av.',
        'Egen posisjon vises nederst til høyre uansett — også uten BFT.',
      ],
      bekreftTekst: 'Begynn å dele',
      onBekreft: () => {
        bftOn = true;
        cop.triggerGeolocate();
        shell.showAlarm('BFT på — din posisjon deles til nettet. Ett klikk på BFT slår det av.');
      },
    });
  }

  // ── SKADE-native chat (TOC-kontakt) — IKKE LENGER ET EGET PANEL HER ─────────
  //
  // «Chat / TOC» var en av NI innganger til samband, og en av to grensesnitt for
  // samme handling: den snakket på kanalen `net` — nøyaktig samme kanal som
  // Feltvekslerens «TOC-nett». To paneler, ett nett, to ulike måter å skrive i
  // det. Panelet er derfor borte fra dock, kommandopalett og hurtigtast (K).
  //
  // Det panelet KUNNE som Feltveksleren ikke kunne — vedlegg (bilde/fil),
  // talemelding, svar på melding og skrive-indikator — er FLYTTET inn i
  // `ui/sentralbord.ts` først. Ingen funksjon forsvant med panelet.
  //
  // `ui/chat.ts` + `panels/chat-panel.ts` står igjen fordi ruta `?panel=chat`
  // fortsatt kan ligge åpen i et poppet-ut TOC-vindu hos noen. Ingen NY dør inn
  // dit finnes i brukerflaten. (Jeg mener ruta bør dø neste gang noen rører
  // pop-out-registeret — se rapport.)

  // ── Kapabilitets-sjekkliste — UTVIKLERVERKTØY, IKKE OPERATIVT ────────────
  //
  // ⚠ DENNE HØRTE ALDRI HJEMME I DEN OPERATIVE APPEN.
  // Navnet lover en militær kapabilitetsoversikt. Innholdet er utviklingsteamets
  // egen funksjonsstatus — 264 punkter der hvert punkt viser KILDEKODEREFERANSER
  // («contracts/cot.ts + hub /cot/ingest», «terrain.ts heightProfile»). Den lå
  // under «Styrke & log», altså midt blant PERSTAT og LOGSTAT, og var synlig for
  // enhver innlogget bruker. Den viste interne filnavn og arkitekturdetaljer til
  // operatører som ikke har noe behov for dem, og den fortalte samtidig hvor lite
  // som var ferdig — i et verktøy som skal brukes til å ta beslutninger.
  //
  // Den er nå utilgjengelig i den operative appen: ingen oppføring i verktøylinja,
  // ingen i kommandopaletten, og ingen egen-vindu-rute. Den bygges bare når appen
  // kjører UTEN backend (`demo`), altså under utvikling.
  //
  // GJENSTÅR (ærlig sagt): strengene ligger fortsatt i JS-pakka fordi importen er
  // statisk. Skal referansene ut av bunten, må `ui/checklist.ts` lastes med en
  // dynamisk import. Det er en egen endring, ikke gjort her.
  const checklist = demo ? new ChecklistPanel() : null;
  if (checklist) shell.mapEl.appendChild(checklist.el);
  function toggleChecklist() {
    if (checklist) checklist.toggle();
    else shell.showAlarm('Kapabilitets-sjekklista er et utviklerverktøy og er ikke tilgjengelig her.');
  }

  // ── ESO — operativ styrkeoppbygging (distribuer + rollup, delt over hubben) ──
  const esoStatuses = new Map<string, EsoSoldierStatus>();
  const esoAssigns = new Map<string, ChecklistAssignment>();
  function upsertEso(s: EsoSoldierStatus) {
    const e = esoStatuses.get(s.soldierId);
    if (!e || s.tsMs >= e.tsMs) esoStatuses.set(s.soldierId, s);
    if (esoStatuses.size > 2000) { const o = [...esoStatuses.values()].sort((a, b) => a.tsMs - b.tsMs)[0]; if (o) esoStatuses.delete(o.soldierId); }
  }
  function upsertAssign(a: ChecklistAssignment) {
    const e = esoAssigns.get(a.id);
    if (!e || a.issuedMs >= e.issuedMs) esoAssigns.set(a.id, a);
    if (esoAssigns.size > 500) { const o = [...esoAssigns.values()].sort((x, y) => x.issuedMs - y.issuedMs)[0]; if (o) esoAssigns.delete(o.id); }
  }
  const esoPanel = new EsoPanel({
    self: { id: user.id, role: user.role, org: user.org },
    getStatuses: () => [...esoStatuses.values()],
    getAssignments: () => [...esoAssigns.values()],
    publishStatus: (s) => { upsertEso(s); feed.send?.({ type: 'eso', eso: s }); },
    distribute: (a) => { upsertAssign(a); feed.send?.({ type: 'checklist', checklist: a }); },
  });
  shell.mapEl.appendChild(esoPanel.el);
  function toggleEso() { esoPanel.toggle(); }

  // ── GNÅ 1:1 / 1:flere (SKADE → GNÅ, toveis via broen) ───────────────────
  //
  // ⚠ PANELET «GNÅ — 1:1 / melding ut» ER BORTE. HVORFOR:
  // Det var en av TRE veier til en 1:1-samtale, og en av to ULIKE grensesnitt for
  // samme handling. Feltveksleren gir en samtaletråd; dette ga et råteknisk skjema
  // der operatøren måtte lime inn en full Matrix-adresse for å skrive til noen som
  // sto i Kontakter rett ved siden av. Beviset lå i Kontakter: en «Kopier»-knapp
  // per rad hvis eneste jobb var å flytte adressen over hit.
  //
  // Alt skjemaet kunne, som Feltveksleren IKKE kunne, er flyttet inn dit først
  // (`ui/sentralbord.ts`): 1:flere til Matrix-rom, adressering av en id som ikke
  // er kontakt (skriv «@…»/«!…» i søkefeltet), tilgivende id-normalisering, og
  // identitets-lenka til privat GNÅ-bruker («swap», via «som»-chipen).
  // `ui/gna-dm.ts` er derfor slettet — den var ikke lenger eneste vei til noe.

  // ── Adressebok (kallesignal-katalog, hierarkisk) ────────────────────────
  // Kontakter: lagrede GNÅ-kontakter (localStorage) + auto-lærte fra innboksen.
  const CONTACTS_KEY = `skade.contacts.${user.org?.district ?? 'na'}`;
  const contactDomain = user.id.includes(':') ? user.id.split(':').slice(1).join(':') : 'matrix.follodefence.com';
  const savedContacts = new Map<string, Contact>();
  const seenContacts = new Map<string, Contact>();
  try {
    const raw = JSON.parse(localStorage.getItem(CONTACTS_KEY) ?? '[]');
    if (Array.isArray(raw)) for (const c of raw) if (c?.id) savedContacts.set(c.id, { id: c.id, name: c.name || c.id, source: 'saved' });
  } catch { /* korrupt cache */ }
  function persistContacts() { try { localStorage.setItem(CONTACTS_KEY, JSON.stringify([...savedContacts.values()])); } catch { /* full */ } }
  function learnContact(id: string, name?: string) {
    if (!id || savedContacts.has(id) || seenContacts.has(id)) return;
    seenContacts.set(id, { id, name: name || id, source: 'seen' });
  }
  // ── Sentralbord — ett samlet samband (kanaler + GNÅ 1:1/rom), Element-inspirert ──
  // Bygges FØR Kontakter fordi Kontakter nå åpner samtaler her (se `onMessage`).
  const sentralbord = new Sentralbord({
    selfId: user.id,
    onChat: (e) => feed.send?.({ type: 'chat', chat: e }),
    getSaved: () => [...savedContacts.values()],
    getSeen: () => [...seenContacts.values()],
    onPopout: () => popOut(POPOUT_KEY_BY_CLASS['sb-panel'] ?? 'feltveksler'),
    defaultDomain: contactDomain,
    // Katalogen over ALLE registrerte GNÅ-brukere (hubben holder admin-tokenet).
    sokGnaBrukere,
    // ── «FINN HAN I KARTET» FRA KONTAKTKORTET ────────────────────────────────
    //
    // Eierkrav 2026-08-18: fra et kontaktkort skal man kunne finne personen i
    // kartet. Sporet finnes alt — GNÅ-posisjoner bærer `person.matrixId` — men
    // det fantes ingen vei fra kortet dit.
    //
    // ⚠ RETURNERER TIDSPUNKTET, IKKE BARE «FUNNET». Kortet skriver ut alderen,
    // fordi et spor på ni minutter er «her VAR han», ikke «her er han». Å
    // panorere til et gammelt punkt uten å si det er samme løgn som å vise et
    // estimat som et målt tall.
    //
    // ⚠ NYESTE SPOR VINNER. Samme person kan ha flere spor (BFT + geotag +
    // bilde); flyr vi til det FØRSTE vi finner, kan operatøren havne på et
    // punkt fra i går mens et ferskt finnes.
    onFinnIKart: (matrixId) => {
      const best = nyesteSporForPerson(lastTracks, matrixId);
      if (!best) return null;
      cop.flyTo(best);
      // Detaljpanelet åpnes samtidig: «finn ham» skal svare på HVEM og HVOR
      // GAMMELT, ikke bare flytte kartet til et punkt uten forklaring.
      shell.showDetail(best);
      return { lat: best.lat, lon: best.lon, tsMs: best.tsMs || Date.now() };
    },
    // Feltveksleren varsler selv (lyd, teller i vindustittel, systemvarsel) —
    // det er felles for docked og pop-out. HOVEDVINDUET kan i tillegg gjøre noe
    // pop-outen ikke kan: legge varselet over kartet, der operatøren står når
    // hun ikke ser på Feltveksleren, med ett klikk inn i samtalen.
    onVarsel: (v) => {
      const tekst = v.tekst.trim();
      shell.showAlarm(`Melding fra ${v.fra}${tekst ? `: ${tekst.slice(0, 80)}` : ''}`);
      loggHendelse({
        id: `sb-${v.fra}-${Date.now()}`, tsMs: Date.now(), type: 'melding',
        hastegrad: 'priority', // et menneske har skrevet direkte til deg
        tittel: `Melding fra ${v.fra}`,
        detalj: tekst.slice(0, 120) || 'uten tekst',
        farge: 'var(--gold)',
        onClick: () => sentralbord.apneDm(v.convId.replace(/^dm:/, '')),
      });
    },
  });
  shell.mapEl.appendChild(sentralbord.el);
  function toggleSentralbord() { sentralbord.toggle(); }

  const directory = new DirectoryPanel({
    // «1:1» ÅPNER samtalen (og oppretter den om nødvendig) i Feltveksleren.
    // Før pekte den på GNÅ-DM-skjemaet og bare forhåndsutfylte et mottakerfelt —
    // altså et hopp til et annet grensesnitt, midt i én og samme handling.
    onMessage: (mxid, navn) => sentralbord.apneDm(mxid, navn),
    getSaved: () => [...savedContacts.values()],
    getSeen: () => [...seenContacts.values()],
    onAdd: (name, id) => { savedContacts.set(id, { id, name, source: 'saved' }); seenContacts.delete(id); persistContacts(); },
    onRemove: (id) => { savedContacts.delete(id); persistContacts(); },
    defaultDomain: contactDomain,
    // Alle registrerte GNÅ-brukere, ikke bare enheter og folk vi har snakket
    // med. Kontakter er panelet operatøren FAKTISK leter i.
    sokGnaBrukere,
  });
  shell.mapEl.appendChild(directory.el);
  function toggleDirectory() { directory.toggle(); }

  shell.mapEl.appendChild(targetPanel.el);
  function toggleTargets() { targetPanel.toggle(); }

  // Varselkø: samler HVT/TST + fiendtlig nærhet + avvik i én prioritert innboks.
  let alertList: Alert[] = [];
  const alertSeen = new Map<string, number>();
  const alertsPanel = new AlertsPanel({
    getAlerts: () => alertList,
    onFocus: (a) => {
      const t = lastTracks.find((x) => x.id === a.refId) ?? lastTracks.find((x) => a.refId && x.id.includes(a.refId));
      if (t) { cop.map.flyTo({ center: [t.lon, t.lat], zoom: 14 }); cop.highlightAt(t.lon, t.lat); }
    },
    // SENS-33: kvitter et talt varsel. Lokal, in-memory tilstand (samme som
    // resten av `alertList`) — ikke synkronisert over hubben, se rapport i PR.
    onKvitter: (a) => {
      alertList = kvitterAlert(alertList, a.key, myCallsign, Date.now());
      alertsPanel.render();
    },
  });
  shell.mapEl.appendChild(alertsPanel.el);
  function toggleAlerts() { alertsPanel.toggle(); }
  /** Bygg varselkøen fra gjeldende situasjonsbilde (egne = beskyttede punkter). */
  function refreshAlerts() {
    const guarded: NamedPoint[] = friendlyPositions().map((f) => ({ id: f.id, label: f.label, lat: f.lat, lon: f.lon }));
    const fresh = buildAlertFeed(
      {
        nowMs: Date.now(), tracks: lastTracks, targets: targetList, guarded, proximityRadiusM: 1500,
        // VALE SENS-21: uposisjonerte deteksjoner (ukalibrert kilde) i SAMME kø.
        sporUtenPosisjon: lastSporUtenPosisjon,
      },
      { seen: alertSeen, dedupeWindowMs: 120_000, limit: 100 },
    );
    if (fresh.length) alertList = [...fresh, ...alertList].slice(0, 100);
    // BLINK på kartet for FERSKE sensorobservasjoner (eierkrav 2026-08-17).
    // Bare `kind: 'sensor'` blinker: de andre varslene (HVT/TST, nærhet) har alt
    // sine egne, sterkere uttrykk i kartet, og hvis alt blinker betyr blink
    // ingenting. `fresh` er dedupet, så samme observasjon blinker ikke på nytt
    // ved hvert snapshot.
    const blink = fresh
      .filter((a) => a.kind === 'sensor' && a.refId)
      .map((a) => lastTracks.find((t) => t.id === a.refId))
      .filter((t): t is Track => !!t)
      .map((t) => ({ id: t.id, lon: t.lon, lat: t.lat }));
    if (blink.length) cop.blinkPunkter(blink);
    // SENS-33: les FERSKE sensorvarsler opp (nb-NO), samme opplesingsmekanisme
    // som innkommende PTT-tekstmeldinger (`speak()`, styrt av `ptt.ttsOn`) —
    // ledningen mellom varselkøen og opplesingen som eier har bedt om.
    // ⚠ `fresh` ER de nye/udempede varslene for DENNE runden (buildAlertFeed har
    // alt dedupet mot `alertSeen`), så samme observasjon leses ikke opp igjen
    // ved hvert snapshot.
    if (ptt.ttsEnabled) announceNewSensorAlerts(fresh, speak);
    // Varsler hører i loggen sammen med rapportene: operatøren skal ikke måtte
    // huske hvilket panel en gitt hendelsestype havnet i.
    for (const a of fresh) {
      loggHendelse({
        id: `alert-${a.key}`, tsMs: a.tsMs, type: 'varsel',
        tittel: a.title, detalj: `${a.severity.toUpperCase()} · ${a.detail}`,
        hastegrad: ALERT_PRECEDENCE[a.severity],
        farge: a.severity === 'kritisk' ? 'var(--danger)' : 'var(--gold)',
        onClick: () => {
          const t = lastTracks.find((x) => x.id === a.refId);
          if (t) { cop.flyTo(t); shell.showDetail(t); cop.highlightAt(t.lon, t.lat); }
        },
      });
    }
    const c = alertCounts(alertList);
    // ⚠ `shell.setCount('alerts', …)` var en STILLE NO-OP: `Shell.counts` fylles
    // bare for kartlag-id-ene (`sensors`/`targets`), så tallet ble skrevet til
    // ingenting. Verktøylinja har en ekte teller for varselkø-knappen.
    dock?.setCount('alerts', c.kritisk + c.advarsel);
    if (alertsPanel.el.classList.contains('open')) alertsPanel.render();
  }

  // ── RFI / innsamlingsstyring (intel, delt over hubben) ──────────────────────
  // ⚠ `innhentingKrav` MÅ være deklarert før `RfiPanel` bygges: konstruktøren
  // kaller `render()` synkront, som synkront kaller `getRequirements()` — og
  // den leser variabelen i samme tick. Sto `let innhentingKrav` under (der den
  // hørte tematisk hjemme, ved siden av `lastInnhenting`), kastet dette en
  // ReferenceError («Cannot access before initialization», TDZ) FØR
  // `bootApp()` nådde linja som setter `window.__skadeMap` — hele appen døde
  // stille i demomodus, og ingen `pageerror` kom fra en tom katalog.
  let innhentingKrav: Requirement[] = [];
  const rfis = new Map<string, Rfi>();
  const MAX_RFIS = 500;
  // Monotonisk upsert (status kan aldri reverteres) + cap mot ubegrenset vekst.
  function upsertRfi(r: Rfi) {
    const existing = rfis.get(r.id);
    rfis.set(r.id, existing ? mergeRfi(existing, r) : r);
    if (rfis.size > MAX_RFIS) {
      const oldest = [...rfis.values()].sort((a, b) => a.createdMs - b.createdMs)[0];
      if (oldest) rfis.delete(oldest.id);
    }
  }
  // KART FØRST (UX_DESIGNSYSTEM.md §2, Bølge 241/SENS-44): RFI-stedfeltet
  // hadde bare MGRS-tekst, ingen kartklikk. Samme kartmodus-mønster som
  // «plasser stridsmelding»/«plasser kapabilitet».
  let pendingRfiSted: ((mgrs: string) => void) | null = null;
  // Delt publish-funksjon for BÅDE RfiPanel og DispatchPanel (DISP-01): samme
  // RFI-liste, samme collab-kanal, samme hendelseslogg-oppføring — en klaim/
  // frigjøring fra dispatch-køen er bare et nytt feltsett på RFI-en, ikke en
  // egen datavei. To skrivesider til samme post ville vært nøyaktig den
  // divergensen §3 forbyr.
  const publishRfi = (r: Rfi) => {
    upsertRfi(r);
    feed.send?.({ type: 'rfi', rfi: r });
    loggHendelse({
      id: `rfi-${r.id}-${r.status}`, tsMs: r.createdMs, type: 'rfi',
      tittel: r.question, detalj: `${r.requester} · ${r.status}`,
      // RFI-en BÆRER hastegraden sin allerede (`RfiPriority` er de samme fire
      // kodeordene). Ingen oversettelse, ingen ny skala.
      hastegrad: r.priority,
      onClick: () => { if (!rfiPanel.el.classList.contains('open')) rfiPanel.toggle(); },
    });
  };
  const rfiPanel = new RfiPanel({
    self: user.id,
    getRfis: () => [...rfis.values()],
    publish: publishRfi,
    kontekst: () => kontekst(),
    naa: () => Date.now(),
    // Kravene RFI-ene henger under — hentes fra innhentingsplanen i hubben.
    getRequirements: () => innhentingKrav,
    // «Observasjoner» på en RFI-rad åpner arkivet filtrert på nettopp den RFI-en:
    // dette er hva som gjør innhentingsplanen AKTIV framfor en liste med
    // spørsmål ingen ser svaret på.
    onVisSvar: (r) => arkivPanel.apneForRfi(r.id),
    armStedKlikk: (ferdig) => {
      kartmodus.taOver('place-rfi-sted');
      pendingRfiSted = ferdig;
      shell.showAlarm('Klikk i kartet der behovet gjelder.');
    },
    // SENS-32: skrivesiden av innhentingsplanen — samme autentiserte GET/POST-
    // mønster mot hubben som `kapabilitetHent`/`kapabilitetSend` (og
    // `arkivHent`/`arkivSend`, som `GET /innhenting` over allerede bruker).
    sendKrav: (k) => arkivSend('/innhenting/krav', k),
    onKravOpprettet: () => void lastInnhenting(),
    sendOppdrag: (t) => arkivSend('/innhenting/oppdrag', t),
    // Ressurser å tildele oppdrag til: kapabilitetsregisteret (SENS-42-panelet,
    // konstruert under) i stedet for et fritekstfelt — se `kapabilitetsplanlegger.ts`.
    // Lest lazy via closure (kalles først når «Tildel oppdrag»-seksjonen
    // faktisk rendres, ikke ved konstruksjon), så TDZ-en `let innhentingKrav`
    // over advarer om er ikke et problem her.
    getRessurser: () => kapabilitetsplanleggerPanel.gjeldende.map((kap) => ({
      id: kap.id, navn: kap.navn?.trim() || KAPABILITET_TYPE_LABEL[kap.type],
    })),
  });
  shell.mapEl.appendChild(rfiPanel.el);

  // ── Dispatch-kø (DISP-01, B-003 «Dispatch = SKADE») ─────────────────────
  // Vaktsjefens oversikt over SAMME RFI-kø som over — se filhodet i
  // `ui/dispatch.ts` for hvorfor dette ikke er et duplikat av RFI-panelet.
  const dispatchPanel = new DispatchPanel({
    self: user.id,
    getRfis: () => [...rfis.values()],
    publish: publishRfi,
    naa: () => Date.now(),
    getRequirements: () => innhentingKrav,
  });
  shell.mapEl.appendChild(dispatchPanel.el);
  function toggleDispatch() { dispatchPanel.toggle(); }

  kartmodus.registrer({
    id: 'place-rfi-sted',
    navn: 'PLASSER RFI-STED',
    stopp: () => { if (pendingRfiSted) { pendingRfiSted = null; kartmodus.frigi('place-rfi-sted'); } },
  });
  cop.map.on('click', (e) => {
    if (!pendingRfiSted) return;
    const ferdig = pendingRfiSted;
    pendingRfiSted = null;
    kartmodus.frigi('place-rfi-sted');
    let mgrs: string;
    try { mgrs = mgrsForward([e.lngLat.lng, e.lngLat.lat], 5); } catch { return; } // utenfor UTM-sone — ingen gyldig MGRS å levere
    ferdig(mgrs);
  });
  function toggleRfi() { rfiPanel.toggle(); }

  // ── OBSERVASJONSARKIVET (SENS-28) ───────────────────────────────────────────
  //
  // Eiermål 2026-08-17: «mulighet for å hente opp historikk for analytikerne på
  // en smart måte — herunder også jobbe aktivt i innhentingsplaner».
  //
  // Arkivet, de fem endepunktene og mønster-/dekningsmotorene var ferdige og
  // testet, men INGEN FLATE nådde dem: analytikeren hadde et komplett arkiv hun
  // ikke kunne spørre i. Dette er ledningen — og det siste leddet i kjeden
  // GNÅ-bilde → hub → arkiv → analytiker → etterretningsprodukt.
  const arkivPanel = new ArkivPanel({
    hent: arkivHent,
    send: arkivSend,
    naa: () => Date.now(),
    // Tråkorset ER kartsenteret (`shell.toggleCrosshair` tegner det der), så
    // «nær tråkorset» og «nær kartsenteret» er samme punkt — ikke to begreper.
    kartSenter: () => kartSenter(),
    kartBoks: () => kartBoks(),
    // ⚠ PANORERING SKJER BARE PÅ FORESPØRSEL. Samme regel som blinkvarselet:
    // kartet rives aldri ut av hendene på en operatør som holder på med noe
    // annet — arkivet TILBYR stedet, operatøren tar det.
    flyTil: (pkt) => cop.map.flyTo({ center: [pkt.lon, pkt.lat], zoom: 14 }),
    getRfis: () => [...rfis.values()],
    visDekning: (celler, celleGrader) => cop.tegnArkivdekning(dekningsrutenett(celler, celleGrader)),
    skjulDekning: () => cop.skjulArkivdekning(),
    // Arkivtreff som ETT kartlag (SENS-31) — hele det gjeldende søkeutvalget,
    // ikke bare fly-til per rad. Geometrien bygges rent i `ui/arkiv.ts`
    // (`arkivtreffFeatureCollection`); `cop.map` eier bare `setData`.
    tegnArkivlag: (fc) => cop.tegnArkivtreff(fc),
    skjulArkivlag: () => cop.skjulArkivtreff(),
    // Arkivutvalg → etterretningspakke. `buildIntelPackage` tar utvalget MED
    // UENDRET (det filtreres ikke mot kartboksen — se funksjonen), og
    // `meta.utvalg` bærer spørringen i ord. Uten den setningen ville en leser i
    // høyere stab lest boksen i metadataen som utvalgets avgrensning, og trodd
    // at det som ikke er i fila, ikke finnes.
    pakkUtvalg: (obs, beskrivelse) => {
      const b = cop.map.getBounds();
      const naa = Date.now();
      const pkg = buildIntelPackage({
        title: `Arkivutvalg ${new Date(naa).toISOString().slice(0, 16).replace('T', ' ')}Z`,
        createdBy: user.id, createdMs: naa,
        bbox: { west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth() },
        // Tidsrommet er utvalgets EGET spenn, ikke «siste 24t»: pakker hun en uke
        // med observasjoner, skal fila si en uke.
        sinceMs: Math.min(...obs.map((o) => o.tsMs)),
        untilMs: Math.max(...obs.map((o) => o.tsMs)),
        tracks: lastTracks, reports: [...reports.values()], intel: intelWs.list(),
        observasjoner: obs, utvalg: beskrivelse,
      });
      download(`skade-arkivutvalg-${naa}.json`, JSON.stringify(pkg, null, 2), 'application/json');
      const c = pkg.meta.counts;
      shell.showAlarm(
        `Pakke lastet ned: ${c.observasjoner} observasjoner · ${c.tracks} spor · ${c.intel} intel`,
      );
    },
    // Arkivpost → ferdig utfylt etterretningsprodukt. Samme bro som `onImint`
    // for et levende spor, men grunnlaget bygges fra arkivposten
    // (`observasjonGrunnlag`) — som i tillegg har MOTTAKSTIDA, det eneste ærlige
    // svaret på «visste vi dette da beslutningen ble tatt?».
    onImint: (o) => {
      if (!o.bildeUrl) return;
      intelWs.nyttProduktFraObservasjon({
        imageUrl: o.bildeUrl,
        tittel: o.label,
        grunnlag: observasjonGrunnlag(o),
        lat: o.lat,
        lon: o.lon,
        mgrs: latLonToMgrs({ lat: o.lat, lon: o.lon }),
        observertMs: o.tsMs,
      });
      intelHost.classList.add('open');
    },
  });
  shell.mapEl.appendChild(arkivPanel.el);
  function toggleArkiv() { arkivPanel.toggle(); }

  // ── INNHENTINGSPLANEN INN I RFI-TRIAGEN ─────────────────────────────────────
  //
  // `analystQueue` veier en RFI tyngre når den henger under et etterretningskrav
  // (PIR > CCIR > FFIR/EEFI) — sjefens uttalte informasjonsbehov. Kravene lever i
  // hubben (`GET /innhenting`), og uten dette kallet falt den vektingen bort:
  // triagen ble ikke gal, bare fattigere, og det sto som en åpen ledning i
  // `ui/rfi.ts`. Lastes én gang ved oppstart og på nytt hver gang arkivet søker,
  // fordi det er da analytikeren faktisk jobber i planen.
  // (`let innhentingKrav` selv er deklarert langt over, ved RFI-panelet — se
  // forklaringen der.)
  async function lastInnhenting(): Promise<void> {
    try {
      const svar = (await arkivHent('/innhenting')) as { krav?: unknown };
      if (Array.isArray(svar?.krav)) {
        innhentingKrav = svar.krav.filter(
          (k): k is Requirement => !!k && typeof k === 'object' && typeof (k as Requirement).id === 'string',
        );
        rfiPanel.render();
        dispatchPanel.render();
      }
    } catch {
      // ⚠ STILLE HER, MED VILJE — men uten å late som. Feiler kallet, står
      // `innhentingKrav` tom og triagen mister KRAV-vektingen; den viser da bare
      // hastegrad/frist/alder, og begrunnelsen i panelet nevner ikke noe krav.
      // Å vise en feilmelding for et valgfritt tilleggssignal ville lært
      // operatøren å overse feilmeldinger.
    }
  }
  void lastInnhenting();

  // ── Dagsedler / kalender (echelon-distribuert) ──────────────────────────────
  // Operatørens enhet utledes fra org-dybden (dypeste felt = deres nivå).
  const selfUnit: UnitRef = {
    level: (user.org?.team ? 'lag' : user.org?.troop ? 'tropp' : user.org?.area ? 'område' : 'distrikt') as CommandLevel,
    district: user.org?.district, area: user.org?.area, troop: user.org?.troop, lag: user.org?.team,
  };
  const CAL_KEY = `skade.calendar.${user.org?.district ?? 'na'}`;
  const activities = new Map<string, CalendarActivity>();
  const MAX_ACTIVITIES = 2000;
  // Persistér lokalt (overlever reload) — hubben distribuerer på tvers av klienter.
  try {
    const saved = JSON.parse(localStorage.getItem(CAL_KEY) ?? '[]');
    if (Array.isArray(saved)) for (const raw of saved) { const a = parseCalendarActivity(raw); if (a) activities.set(a.id, a); }
  } catch { /* korrupt cache ignoreres */ }
  function persistActivities() {
    try { localStorage.setItem(CAL_KEY, JSON.stringify([...activities.values()])); } catch { /* full/utilgjengelig */ }
  }
  function upsertActivity(a: CalendarActivity) {
    const existing = activities.get(a.id);
    activities.set(a.id, existing ? mergeActivity(existing, a) : a);
    if (activities.size > MAX_ACTIVITIES) {
      const oldest = [...activities.values()].sort((x, y) => x.tsMs - y.tsMs)[0];
      if (oldest) activities.delete(oldest.id);
    }
    persistActivities();
  }
  /** Fjerner en aktivitet (dagsedel slettet i GNÅ). True hvis noe ble fjernet. */
  function slettActivity(id: string): boolean {
    if (!activities.delete(id)) return false;
    persistActivities();
    return true;
  }
  const calendarPanel = new CalendarPanel({
    getActivities: () => [...activities.values()],
    getSelfUnit: () => selfUnit,
    getSelfId: () => user.id,
    onSave: (a) => { upsertActivity(a); feed.send?.({ type: 'calendar', activity: a }); },
    onFocus: (a) => { if (a.location) cop.map.flyTo({ center: [a.location.lon, a.location.lat], zoom: 13 }); },
    onMeeting: (a) => { toggleSentralbord(); shell.showAlarm(`GNÅ-møte «${a.meeting?.room ?? a.title}» — åpne kanal i Feltveksler`); },
  });
  shell.mapEl.appendChild(calendarPanel.el);
  function toggleCalendar() { calendarPanel.toggle(); }

  // Feltkalkulatorer (ballistikk/DOPE, CARP, HLZ, GARS) — rene motorer fra kontraktene.
  const fieldCalc = new FieldCalcPanel();
  shell.mapEl.appendChild(fieldCalc.el);
  function toggleFieldCalc() { fieldCalc.toggle(); }

  // ── ORBAT-creator (bygg hele styrkehierarkiet, RBAC-scopet) ─────────────────
  // Seeden (ORBAT_SEED) lastes som default; superadmin/FOH ser & redigerer hele
  // treet, en sjef redigerer sitt eget subtre. Redigeringer persisteres lokalt
  // (localStorage) og deles over collab (type 'orbat').
  const orbatCreator = new OrbatCreator({
    user,
    onEvent: (ev) => feed.send?.({ type: 'orbat', orbat: ev }),
    onPopout: () => popOut('orbat'),
    defaultDomain: contactDomain,
    // «Send nøkkel»: rolle + org er UTLEDET av ORBAT-plasseringen; hubben
    // håndhever RBAC (lagfører+, aldri over egen rolle, innen egen scope).
    onIssueKey: async (role, org) => {
      try {
        const r = await fetch('/admin/enrol-token', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ role, org, ttlMs: 7 * 24 * 60 * 60 * 1000, maxUses: 1 }),
        });
        const j = await r.json().catch(() => ({})) as { code?: string; expiresAt?: number; error?: string };
        if (!r.ok || !j.code || !j.expiresAt) return { error: j.error ?? `HTTP ${r.status}` };
        return { code: j.code, expiresAt: j.expiresAt };
      } catch { return { error: 'nettverksfeil' }; }
    },
    onSendKey: (mxid, navn, tekst) => sentralbord.sendDm(mxid, navn, tekst),
  });
  shell.mapEl.appendChild(orbatCreator.el);
  function toggleOrbat() { orbatCreator.toggle(); }

  // ── Kart-bokmerker (saved views) ────────────────────────────────────────────
  const bookmarks = new BookmarksPanel({
    current: () => { const c = cop.map.getCenter(); return { lon: c.lng, lat: c.lat, zoom: cop.map.getZoom() }; },
    goto: (v) => cop.map.flyTo({ center: [v.lon, v.lat], zoom: v.zoom }),
  });
  shell.mapEl.appendChild(bookmarks.el);
  function toggleBookmarks() { bookmarks.toggle(); }

  // ── Meteorologi (MET) ───────────────────────────────────────────────────────
  const weather = new WeatherPanel({
    center: () => { const c = cop.map.getCenter(); return { lon: c.lng, lat: c.lat }; },
  });
  shell.mapEl.appendChild(weather.el);
  function toggleWeather() { weather.toggle(); }

  // ── Admin (invitere brukere + provisjonering) ───────────────────────────────
  const adminPanel = new AdminPanel({ id: user.id, role: user.role, org: user.org });
  shell.mapEl.appendChild(adminPanel.el);
  function toggleAdmin() { adminPanel.toggle(); }

  // ── Personregister (ORBAT-person-binding: koble + verifisere) ──────────────
  // Selve HTTP-flaten (/admin/brukere, /admin/medlemskap/*) og myndighets-
  // regelen (kanVerifisere — kommandolinje, ikke rolleflagg) lå ferdige i
  // hubben; dette panelet er den ENE veien en operatør faktisk kan trykke på
  // dem fra skjermen (se filkommentaren i ui/personregister.ts).
  const personregisterPanel = new PersonregisterPanel({ hent: personregisterHent, send: personregisterSend });
  shell.mapEl.appendChild(personregisterPanel.el);
  function togglePersonregister() { personregisterPanel.toggle(); }

  // ── Kapabilitetsplanlegger (behovsbasert anbefaling, plassering, bestilling) ─
  // Eiermål 2026-08-19 (vale/RFI_SENSORPLASSERING_LLM_PLAN_2026-08-19.md §3/§8).
  // Samme «flate ingen kan trykke på er ingen flate»-prinsipp som personregisteret.
  //
  // ⚠ KART FØRST (UX_DESIGNSYSTEM.md §2 «kartet er helten»). Plassering via
  // kartklikk deler `kartmodus`-arbiteren med «plasser stridsmelding»/«plasser
  // ildmål» under (samme grunn som der: to verktøy skal ikke kunne tolke samme
  // klikk). `pendingKapabilitet` speiler `pendingReport`/`pendingFire` sitt
  // mønster med vilje.
  let pendingKapabilitet: { type: Kapabilitet['type'] } | null = null;
  const kapabilitetsplanleggerPanel = new KapabilitetsplanleggerPanel({
    hent: kapabilitetHent,
    send: kapabilitetSend,
    naa: () => Date.now(),
    tegnRinger: (fc) => cop.tegnKapabiliteter(fc),
    kartsenter: () => { const c = cop.map.getCenter(); return { lat: c.lat, lon: c.lng }; },
    armPlassering: (type) => {
      // taOver FØRST — en påbegynt måling/rute skal avbrytes, ikke ligge og
      // lytte på samme klikk som plasserer kapabiliteten (se place-report over).
      kartmodus.taOver('place-kapabilitet');
      pendingKapabilitet = { type };
      shell.showAlarm('Klikk i kartet der kapabiliteten skal stå.');
    },
    flyTo: (lat, lon) => { cop.map.flyTo({ center: [lon, lat], zoom: 14 }); cop.highlightAt(lon, lat); },
    onEndret: () => objekter.meld(),
  });
  shell.mapEl.appendChild(kapabilitetsplanleggerPanel.el);
  function toggleKapabilitetsplanlegger() { kapabilitetsplanleggerPanel.toggle(); }
  kartmodus.registrer({
    id: 'place-kapabilitet',
    navn: 'PLASSER KAPABILITET',
    stopp: () => { if (pendingKapabilitet) { pendingKapabilitet = null; kartmodus.frigi('place-kapabilitet'); } },
  });
  // Plasser en ventende kapabilitet på neste kartklikk.
  cop.map.on('click', (e) => {
    if (!pendingKapabilitet) return;
    const p = pendingKapabilitet;
    pendingKapabilitet = null;
    kartmodus.frigi('place-kapabilitet');
    void kapabilitetsplanleggerPanel.plasserPaaPunkt(p.type, e.lngLat.lat, e.lngLat.lng);
  });

  /**
   * Sett EMCON-nivå.
   *
   * ⚠ TO PORTER, OG DEN ANDRE SIER HVA DET KOSTER. Radiotaushet stopper
   * rapportering fra hele styrken. Bekreftelsen skal derfor si konsekvensen, ikke
   * bare spørre «er du sikker» — og den skal si at nødtrafikk fortsatt slipper
   * gjennom, ellers tror operatøren hun har sperret nødkanalen også.
   *
   * ⚠ VARIGHET FORESLÅS, IKKE «STÅENDE». En glemt radiotaushet er en avdeling som
   * ikke kan rapportere når det gjelder, og den som satte den er kanskje av vakt.
   * Derfor er tidsbegrenset standardvalget her, og «stående» krever et ekstra valg.
   */
  function settEmcon(): void {
    const NIVAA: { id: string; navn: string; konsekvens: string }[] = [
      { id: 'delta', navn: 'DELTA — normal drift', konsekvens: 'Opphever all sendebegrensning.' },
      { id: 'charlie', navn: 'CHARLIE — begrenset', konsekvens: 'Stopper efemert (peker, tegning, chat). Rapporter og ordre går.' },
      { id: 'bravo', navn: 'BRAVO — kun nød og ordre', konsekvens: 'Stopper også rapporter og etterretning. Ordre og nød går.' },
      { id: 'alpha', navn: 'ALPHA — full radiotaushet', konsekvens: 'Stopper ALT unntatt nød. Styrken kan ikke rapportere.' },
    ];
    // Kjeden erstatter TRE `window.prompt`-kall (nivå → grunn → varighet) med
    // tre inline dialoger i rekke — samme rekkefølge og samme forgreining
    // (DELTA hopper over varighet-spørsmålet) som før.
    sporTekst({
      tittel: 'EMCON-nivå',
      tekst: NIVAA.map((n, i) => `${i + 1}. ${n.navn}\n   ${n.konsekvens}`).join('\n') + '\n\nSkriv 1-4:',
      placeholder: '1-4',
      bekreftTekst: 'Neste',
      onSvar: (valg) => {
        const idx = Number((valg ?? '').trim()) - 1;
        const nivaa = NIVAA[idx];
        if (!nivaa) return;
        sporTekst({
          tittel: 'EMCON — grunn',
          tekst: 'Grunn (vises til styrken):',
          bekreftTekst: 'Neste',
          onSvar: (grunnRaw) => {
            const grunn = (grunnRaw ?? '').trim();
            const fortsett = (minutterSvar: string) => {
              const minutter = Number(minutterSvar);
              const staaende = nivaa.id !== 'delta' && (!minutterSvar || !Number.isFinite(minutter) || minutter <= 0);
              fullforEmcon(nivaa, grunn, minutter, staaende);
            };
            if (nivaa.id === 'delta') {
              fortsett('0');
            } else {
              sporTekst({
                tittel: 'EMCON — varighet',
                tekst: 'Varighet i minutter (blank = STÅENDE til den endres):',
                bekreftTekst: 'Neste',
                onSvar: (minRaw) => fortsett((minRaw ?? '').trim()),
              });
            }
          },
        });
      },
    });
  }

  function fullforEmcon(
    nivaa: { id: string; navn: string; konsekvens: string }, grunn: string, minutter: number, staaende: boolean,
  ): void {
    bekreft({
      tittel: `Sette ${nivaa.navn}?`,
      tekst: nivaa.konsekvens,
      punkter: [
        'Nødtrafikk slipper alltid gjennom, på alle nivåer.',
        staaende
          ? '⚠ STÅENDE ORDRE — den opphører ikke av seg selv. En glemt radiotaushet er en avdeling som ikke kan rapportere når det gjelder.'
          : `Opphører automatisk etter ${Math.round(minutter)} minutter.`,
        'Ordren føres i revisjonsloggen med din bruker-id, og vises til hele styrken.',
        '⚠ EMCON etterleves i klientene. Hubben eier nivået og holder tilbake sin egen trafikk, men kan ikke hindre et apparat fra å stråle.',
      ],
      bekreftTekst: `Sett ${nivaa.id.toUpperCase()}`,
      onBekreft: () => {
        void (async () => {
          try {
            const r = await fetch('/emcon', {
              method: 'POST', credentials: 'include',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({
                nivaa: nivaa.id, grunn,
                ...(staaende ? {} : { minutter: Math.round(minutter) }),
              }),
            });
            if (!r.ok) {
              const j = await r.json().catch(() => ({}));
              shell.showAlarm(`EMCON ikke satt: ${(j as { error?: string }).error ?? r.status}`);
              return;
            }
            // Hubben kringkaster til alle; vår egen stripe oppdateres av den samme
            // meldinga, så vi trenger ikke sette den her.
            shell.showAlarm(`EMCON ${nivaa.id.toUpperCase()} satt.`);
          } catch (e) {
            shell.showAlarm(`EMCON ikke satt: ${e instanceof Error ? e.message : String(e)}`);
          }
        })();
      },
    });
  }

  /**
   * Sambandsdiagnose: lever broen mot GNÅ, og hvor står meldingene?
   *
   * ⚠ SVARER PÅ SPØRSMÅLET SOM STO UBESVART I DAGEVIS: «det ser ut som man
   * snakker sammen, men jeg får ikke meldingene». Før bølge 209 var «broen har
   * aldri hentet» og «alt er levert» umulig å skille, fordi hubben slettet
   * meldingene ved lesing. Nå er de to forskjellige tall — og denne dialogen er
   * det operatøren kan åpne uten en terminal.
   */
  async function visSambandDiagnose(): Promise<void> {
    let d: {
      konklusjon?: string; broStilleS?: number | null;
      telling?: Record<string, number>; ugyldigeLinjer?: number; skrivefeil?: number;
      utestaaende?: { id: string; til: string; status: string; forsok: number; alderS: number; grunn: string }[];
    };
    try {
      const r = await fetch('/samband/diagnose', { credentials: 'include' });
      if (!r.ok) throw new Error(`hubben svarte ${r.status}`);
      d = await r.json();
    } catch (e) {
      // ⚠ EN FEILET DIAGNOSE ER IKKE «ALT ER BRA». Det er den samme feilklassen
      // diagnosen finnes for å avsløre, og den skal si det om seg selv.
      bekreft({
        tittel: 'Sambandsdiagnose utilgjengelig',
        tekst: `Kunne ikke hente diagnosen fra hubben: ${e instanceof Error ? e.message : String(e)}. `
          + 'Dette er en FEIL, ikke et svar om sambandet.',
        bekreftTekst: 'Lukk', onBekreft: () => {},
      });
      return;
    }
    const t = d.telling ?? {};
    const punkter: string[] = [
      `I kø: ${t['koet'] ?? 0} · hos broen: ${t['utlevert'] ?? 0} · levert: ${t['levert'] ?? 0} · oppgitt: ${t['oppgitt'] ?? 0}`,
      d.broStilleS === null || d.broStilleS === undefined
        ? 'Broen har ALDRI hentet fra utboksen siden hubben startet.'
        : `Broen hentet sist for ${d.broStilleS} sekunder siden.`,
    ];
    if (d.ugyldigeLinjer) punkter.push(`⚠ ${d.ugyldigeLinjer} ulesbare linjer i utboks-journalen.`);
    if (d.skrivefeil) punkter.push(`⚠ ${d.skrivefeil} skrivefeil mot journalen — meldinger kan tapes ved omstart.`);
    for (const u of (d.utestaaende ?? []).slice(0, 8)) {
      punkter.push(
        `${u.til} — ${u.status}, ${u.forsok} forsøk, ${Math.round(u.alderS / 60)} min`
        + (u.grunn ? ` · ${u.grunn}` : ''),
      );
    }
    bekreft({
      tittel: 'Samband SKADE → GNÅ',
      tekst: d.konklusjon ?? 'Hubben svarte uten konklusjon.',
      punkter,
      bekreftTekst: 'Lukk',
      onBekreft: () => {},
    });
  }

  // ── 9-line CAS (nærstøtte) ──────────────────────────────────────────────────
  const nineLine = new NineLineComposer((n) => {
    const c = cop.map.getCenter();
    const full = { ...n, targetLat: c.lat, targetLon: c.lng };
    createReport('CONTACT', { enemy: n.targetDesc || 'CAS-mål', action: buildNineLineText(full) }, c.lng, c.lat);
    cop.highlightAt(c.lng, c.lat);
    shell.showAlarm('9-line CAS sendt (stridsmelding) — plassert på kartsenter.');
  });
  shell.mapEl.appendChild(nineLine.el);
  function toggleNineLine() { nineLine.toggle(); }

  // ── PERSTAT / sanitet (tap-sporing, delt over hubben) ───────────────────────
  const casualties = new Map<string, Casualty>();
  const MAX_CAS = 2000;
  function upsertCasualty(c: Casualty) {
    const existing = casualties.get(c.id);
    casualties.set(c.id, existing ? mergeCasualty(existing, c) : c);
    if (casualties.size > MAX_CAS) {
      const oldest = [...casualties.values()].sort((a, b) => a.tsMs - b.tsMs)[0];
      if (oldest) casualties.delete(oldest.id);
    }
  }
  const casualtyPanel = new CasualtyPanel({
    self: user.id,
    getCasualties: () => [...casualties.values()],
    publish: (c) => {
      // Fest posisjon til egen posisjon (fersk GPS, ellers kartsenter) om ikke
      // satt. `egenposisjon()` er ÉN kilde, og panelet har alt fortalt operatøren
      // hvilken av de to kildene som gjelder — knappeteksten sier det.
      const p = egenposisjon();
      const withPos: Casualty = c.lat !== undefined && c.lon !== undefined
        ? c
        : (p ? { ...c, lat: p.lat, lon: p.lon } : c);
      upsertCasualty(withPos);
      feed.send?.({ type: 'casualty', casualty: withPos });
      loggHendelse({
        id: `cas-${withPos.id}-${withPos.updatedMs}`, tsMs: withPos.tsMs, type: 'perstat',
        // Triagekategorien ER det militære svaret på «hvor fort» — se
        // `casualtyPrecedence`. MEDEVAC-flagget løfter alltid til IMMEDIATE.
        hastegrad: casualtyPrecedence(withPos),
        tittel: `${withPos.callsign} — ${withPos.status.toUpperCase()}${withPos.medevac ? ' · MEDEVAC' : ''}`,
        detalj: `${withPos.unit || 'enhet ikke satt'} · meldt av ${kortId(withPos.reportedBy)}`,
        onClick: () => casualtyPanel.el.classList.contains('open') || casualtyPanel.toggle(),
      });
    },
    focus: (c) => { if (c.lat !== undefined && c.lon !== undefined) { cop.map.flyTo({ center: [c.lon, c.lat], zoom: 14 }); cop.highlightAt(c.lon, c.lat); } },
    kontekst: () => kontekst(),
    naa: () => Date.now(),
  });
  shell.mapEl.appendChild(casualtyPanel.el);
  function toggleCasualty() { casualtyPanel.toggle(); }

  // ── Logistikk / forsyning (LOGSTAT + resupply, delt over hubben) ────────────
  const logstats = new Map<string, LogStat>();
  const resupplies = new Map<string, ResupplyRequest>();
  const myUnit = user.org?.troop || user.org?.area || user.org?.district || myCallsign;
  function upsertLogStat(l: LogStat) {
    const e = logstats.get(l.id); logstats.set(l.id, e ? mergeLogStat(e, l) : l);
    if (logstats.size > 1000) { const o = [...logstats.values()].sort((a, b) => a.tsMs - b.tsMs)[0]; if (o) logstats.delete(o.id); }
  }
  function upsertResupply(r: ResupplyRequest) {
    const e = resupplies.get(r.id); resupplies.set(r.id, e ? mergeResupply(e, r) : r);
    if (resupplies.size > 1000) { const o = [...resupplies.values()].sort((a, b) => a.tsMs - b.tsMs)[0]; if (o) resupplies.delete(o.id); }
  }
  const logisticsPanel = new LogisticsPanel({
    self: user.id, myUnit,
    getLogStats: () => [...logstats.values()],
    getResupply: () => [...resupplies.values()],
    publishLogStat: (l) => {
      upsertLogStat(l);
      feed.send?.({ type: 'logstat', logstat: l });
      const w = worstLevel(l);
      loggHendelse({
        id: `log-${l.id}-${l.updatedMs}`, tsMs: l.tsMs, type: 'logstat',
        tittel: `LOGSTAT ${l.unit}${w ? ` — verste: ${w.toUpperCase()}` : ''}`,
        // SVART (tom) er IMMEDIATE, rød er PRIORITY — se SUPPLY_LEVEL_PRECEDENCE.
        // Uten hastegrad settes ingen stripe: en LOGSTAT uten oppgitte klasser
        // sier ingenting om hvor mye det haster, og skal ikke late som.
        hastegrad: w ? SUPPLY_LEVEL_PRECEDENCE[w] : undefined,
        detalj: (Object.entries(l.levels) as Array<[SupplyClass, SupplyLevel]>)
          .map(([c, lv]) => `${c}:${lv}`).join(' · ') || 'ingen klasser oppgitt',
        farge: w ? SUPPLY_LEVEL_COLOR[w] : undefined,
        onClick: () => logisticsPanel.el.classList.contains('open') || logisticsPanel.toggle(),
      });
    },
    publishResupply: (r) => {
      // Leveringssted: egen posisjon (fersk GPS, ellers kartsenter) — samme ÉNE
      // kilde som skjemaet viste operatøren, ikke et separat oppslag her.
      const p = egenposisjon();
      const withPos: ResupplyRequest = r.lat !== undefined && r.lon !== undefined
        ? r
        : (p ? { ...r, lat: p.lat, lon: p.lon } : r);
      upsertResupply(withPos); feed.send?.({ type: 'resupply', resupply: withPos });
      loggHendelse({
        id: `res-${withPos.id}-${withPos.status}`, tsMs: withPos.tsMs, type: 'logstat',
        tittel: `Anmodning: ${withPos.supplyClass} — ${withPos.quantity}`,
        // `ResupplyPriority` er et undersett av de samme kodeordene (ingen FLASH).
        hastegrad: withPos.priority,
        detalj: `${withPos.requester} · ${withPos.priority} · ${withPos.status}`,
        onClick: () => logisticsPanel.el.classList.contains('open') || logisticsPanel.toggle(),
      });
    },
    kontekst: () => kontekst(),
    naa: () => Date.now(),
    // Delta-rapportering: forrige innsendte LOGSTAT for EGEN enhet. Samme
    // datalager som lista viser — ingen egen kopi som kan komme ut av takt.
    forrigeLogStat: () => logstats.get(`log-${myUnit}`) ?? null,
  });
  shell.mapEl.appendChild(logisticsPanel.el);
  function toggleLogistics() { logisticsPanel.toggle(); }

  // ── Etterretningspakke: gjeldende kartutsnitt + siste 24t → nedlastbar fil ─
  function exportIntelPackage() {
    const b = cop.map.getBounds();
    const now = Date.now();
    const pkg = buildIntelPackage({
      title: `SKADE-pakke ${new Date(now).toISOString().slice(0, 16).replace('T', ' ')}Z`,
      createdBy: user.id, createdMs: now,
      bbox: { west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth() },
      sinceMs: now - 24 * 60 * 60 * 1000, untilMs: now,
      tracks: lastTracks, reports: [...reports.values()], intel: intelWs.list(),
    });
    const blob = new Blob([JSON.stringify(pkg, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `skade-pakke-${now}.json`;
    a.click();
    URL.revokeObjectURL(url);
    const c = pkg.meta.counts;
    shell.showAlarm(`Etterretningspakke lastet ned: ${c.tracks} spor · ${c.reports} meldinger · ${c.intel} intel`);
  }

  // ── Eksport av situasjonsbildet til GIS/ATAK-standardformater (GeoJSON/KML) ─
  function gatherExportPoints(): ExportPoint[] {
    const pts: ExportPoint[] = [];
    for (const t of lastTracks) pts.push(trackToExportPoint(t));
    for (const u of units.snapshot()) {
      pts.push({ lat: u.lat, lon: u.lon, name: u.label, props: { kind: 'unit', sidc: u.sidc } });
    }
    for (const r of reports.values()) pts.push(reportToExportPoint(r));
    return pts;
  }
  function download(filename: string, text: string, mime: string) {
    const blob = new Blob([text], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }
  function exportCotFile() {
    const events: string[] = [];
    for (const t of lastTracks) { try { events.push(trackToCotXml(t)); } catch { /* dropp korrupt spor */ } }
    for (const r of reports.values()) { try { events.push(reportToCotXml(r)); } catch { /* dropp korrupt melding */ } }
    if (!events.length) { shell.showAlarm('Ingenting å eksportere til CoT ennå.'); return; }
    const stamp = new Date().toISOString().slice(0, 16).replace(/[T:]/g, '-');
    download(`skade-${stamp}.cot.xml`, `<events>${events.join('')}</events>`, 'application/xml');
    shell.showAlarm(`Eksportert ${events.length} CoT-events (ATAK/TAK-kompatibelt).`);
  }
  function exportSituation(format: 'geojson' | 'kml') {
    const pts = gatherExportPoints();
    if (!pts.length) { shell.showAlarm('Ingenting å eksportere ennå — ingen spor, enheter eller meldinger.'); return; }
    const stamp = new Date().toISOString().slice(0, 16).replace(/[T:]/g, '-');
    if (format === 'geojson') {
      download(`skade-${stamp}.geojson`, toGeoJSON(pts), 'application/geo+json');
    } else {
      download(`skade-${stamp}.kml`, toKML(pts, `SKADE ${stamp}`), 'application/vnd.google-earth.kml+xml');
    }
    shell.showAlarm(`Eksportert ${pts.length} objekter til ${format.toUpperCase()}.`);
  }

  // ── «Neste klikk plasserer …»-modiene ─────────────────────────────────────
  // Disse er også kartmodi og meldes derfor inn i arbitreringen: å arme pekeren
  // midt i en påbegynt måling skal AVBRYTE målinga, ikke la begge lytte.
  let pointerArmed = false;
  function avarmPointer() {
    if (!pointerArmed) return;
    pointerArmed = false;
    cop.map.getCanvas().style.cursor = '';
    kartmodus.frigi('pointer');
  }
  kartmodus.registrer({ id: 'pointer', navn: 'PEKER', stopp: avarmPointer });
  kartmodus.registrer({
    id: 'place-report',
    navn: 'PLASSER MELDING',
    stopp: () => { if (pendingReport) { pendingReport = null; kartmodus.frigi('place-report'); } },
  });
  kartmodus.registrer({
    id: 'place-fire',
    navn: 'PLASSER ILDMÅL',
    stopp: () => {
      if (pendingFire || pendingWez) { pendingFire = null; pendingWez = null; kartmodus.frigi('place-fire'); }
    },
  });

  // Digital pointer: neste kartklikk sender et efemert pekepunkt til andre.
  cop.map.on('click', (e) => {
    if (!pointerArmed) return;
    avarmPointer();
    cop.showPointer(e.lngLat.lng, e.lngLat.lat, 'meg');
    feed.send?.({ type: 'pointer', lon: e.lngLat.lng, lat: e.lngLat.lat });
  });
  function armPointer() {
    kartmodus.taOver('pointer');
    pointerArmed = true;
    cop.map.getCanvas().style.cursor = 'pointer';
  }
  // Plasser en ventende stridsmelding på neste kartklikk.
  cop.map.on('click', (e) => {
    if (!pendingReport) return;
    const p = pendingReport;
    pendingReport = null;
    kartmodus.frigi('place-report');
    createReport(p.type, p.fields, e.lngLat.lng, e.lngLat.lat, p.omObjekt);
  });
  // Plasser ildmål / trussel-ring på neste kartklikk.
  cop.map.on('click', (e) => {
    if (pendingFire) { const p = pendingFire; pendingFire = null; kartmodus.frigi('place-fire'); placeFireMission(p, e.lngLat.lng, e.lngLat.lat); return; }
    if (pendingWez) { const t = pendingWez; pendingWez = null; kartmodus.frigi('place-fire'); placeWez(t, e.lngLat.lng, e.lngLat.lat); }
  });
  draw.onChange(() => {
    feed.send?.({ type: 'draw', features: draw.snapshot() as unknown[] });
    // Objektlista skal endre seg NÅR kartet gjør det. Uten denne meldingen sto
    // en fersk påtegning i kartet men ikke i lista før noe annet tegnet den om.
    objekter.meld();
  });
  // Terra-draws egen seleksjon er nå ETT med COP-ens (fem-øyer-problemet).
  draw.onSeleksjon = (id) => {
    if (id === null) { seleksjon.opphev(); return; }
    const o = objekter.finn(objektId('tegning', String(id)));
    if (o) { seleksjon.velg(o); visObjektIPanel(o); }
  };

  // ── Testhåndtak (KUN demomodus) ───────────────────────────────────────────
  // `apps/cop/e2e/tegneverktoy.e2e.mjs` driver den EKTE appen i Chromium og må
  // kunne lese kartets kamera og tegnede figurer for å avgjøre om et verktøy
  // faktisk gjorde noe. Alternativet — å lese piksler — kan ikke skille «figuren
  // flyttet seg» fra «kartet panorerte», og det var nettopp den forvekslingen som
  // gjorde QA-funnet vanskelig å tolke.
  // Håndtakene settes IKKE når appen kjører mot en backend: en operatørmaskin
  // skal ikke tilby kamerakontroll til noe som måtte havne i sidekonteksten.
  if (demo) {
    Object.assign(window as unknown as Record<string, unknown>, {
      __skadeMap: cop.map,
      __skadeDraw: draw,
      __skadeKartmodus: kartmodus,
      __skadeMeasure: measure,
      __skadeTac: tac,
      __skadeRoute: route,
      // Kartmanøver fase 2/3: e2e må kunne lese hvilken ID enheten faktisk fikk
      // på kartet. Det er hele påstanden — at kartenheten ER ORBAT-enheten — og
      // den kan ikke leses av et skjermbilde.
      __skadeUnits: units,
      __skadeOrbatVelger: orbatVelger,
    });
  }

  // ── Modus-stripe: hva eier kartet nå, og hvordan kommer jeg ut? ────────────
  // Et verktøy man ikke SER at er på, er et verktøy som lyver. Stripa er derfor
  // ikke en hjelpetekst, den er den synlige delen av arbitreringen: den finnes
  // hvis og bare hvis et verktøy eier kartklikkene.
  const modusStripe = document.createElement('div');
  modusStripe.className = 'kartmodus-stripe';
  modusStripe.setAttribute('role', 'status');
  const modusNavn = document.createElement('b');
  const modusUt = document.createElement('button');
  modusUt.type = 'button';
  modusUt.className = 'kartmodus-ut';
  modusUt.textContent = 'Avslutt (Esc)';
  modusUt.onclick = () => kartmodus.stoppAlt();
  modusStripe.append(modusNavn, document.createTextNode(' eier kartklikkene · '), modusUt);
  shell.mapEl.appendChild(modusStripe);
  kartmodus.onEndring((v) => {
    modusNavn.textContent = v?.navn ?? '';
    // Den NØYTRALE standardeieren («velg») er ikke et verktøy og skal ikke stå i
    // stripa: stripa finnes hvis og bare hvis et verktøy eier KLIKKENE. Uten
    // dette skillet ville stripa stått permanent og lovet «Avslutt (Esc)» for en
    // tilstand operatøren aldri slo på — og da er den bare støy.
    modusStripe.classList.toggle('vis', v !== null && v.noytral !== true);
  });
  // Escape eies av gest-avtalen (`ui/gester.ts`), som kjører i KAPRE-fasen med
  // prioriteten modal → verktøy → seleksjon → panel. Den gamle lytteren her lå i
  // boble-fasen og tapte derfor mot panel-lukking — nøyaktig løftebruddet stripa
  // over gjorde synlig. Den er fjernet, ikke flyttet: to eiere av Escape er selve
  // problemet rapporten beskrev.
  // Toolbar/palett skal vise hvilket verktøy som eier kartet.
  kartmodus.onEndring((v) => {
    // `dock` bygges senere i main(); lytteren kjører først ved et modusbytte.
    for (const id of kartmodus.registrerte) dock?.setActive(id, v?.id === id);
  });

  cop.map.on('mousemove', (e) => {
    try { shell.setMouseCoord(mgrsForward([e.lngLat.lng, e.lngLat.lat], 5)); } catch { /* utenfor sone */ }
  });

  // Trådkorset sitter i kartsenteret, så senterets MGRS ER siktepunktet. Mates
  // ved hver kartbevegelse; nedre rad i coord-kortet viser TRÅDKORS i stedet for
  // MUS så lenge sikte er aktivt (shell.renderLowerCoord velger kilde).
  function refreshCrosshairCoord() {
    const c = cop.map.getCenter();
    try { shell.setCrosshairCoord(mgrsForward([c.lng, c.lat], 5)); } catch { shell.setCrosshairCoord(''); }
  }
  cop.map.on('move', refreshCrosshairCoord);
  refreshCrosshairCoord();

  function parseCoord(q: string): [number, number] | null {
    const s = q.trim();
    const dec = s.match(/^(-?\d+(?:\.\d+)?)[,\s]+(-?\d+(?:\.\d+)?)$/);
    if (dec) {
      const lat = parseFloat(dec[1]), lon = parseFloat(dec[2]);
      if (Math.abs(lat) <= 90 && Math.abs(lon) <= 180) return [lon, lat];
    }
    try {
      const p = mgrsToPoint(s.replace(/\s+/g, '').toUpperCase());
      if (Array.isArray(p) && p.length === 2) return [p[0], p[1]];
    } catch { /* ikke MGRS */ }
    return null;
  }
  function gotoCoord(lonlat: [number, number]) {
    cop.map.flyTo({ center: lonlat, zoom: 14 });
    cop.dropPin(lonlat);
  }

  const commands: Command[] = [
    { id: 'measure', title: 'Mål avstand og peiling', hint: 'M', keywords: 'range bearing rb', run: () => measure.toggle() },
    { id: 'measure-clear', title: 'Slett avstandsmålinger', keywords: 'measure clear slett fjern mål avstand peiling rydd', run: () => slettMaalinger() },
    { id: 'route', title: 'Planlegg rute', hint: 'R', keywords: 'route waypoint navigasjon', run: () => route.toggle() },
    { id: 'axis-friend', title: 'Tegn fremrykningsakse (venn)', hint: 'A', keywords: 'pil akse akse taktisk graphics', run: () => { tac.setAffiliation('friend'); tac.start(); } },
    { id: 'axis-hostile', title: 'Tegn fiendtlig akse', keywords: 'pil akse fiende taktisk', run: () => { tac.setAffiliation('hostile'); tac.start(); } },
    { id: 'axis-clear', title: 'Slett taktisk grafikk', keywords: 'pil akse clear', run: () => tac.clear() },
    { id: 'unit', title: 'Plasser enhet (APP-6)', hint: 'U', keywords: 'enhet unit symbol app6 styrke stridsvogn infanteri', run: () => unitPalette.toggle() },
    { id: 'orbat-plasser', title: 'Plasser enhet fra ORBAT — koblet til styrkestrukturen', keywords: 'orbat enhet plasser styrkestruktur avdeling lag tropp kompani distrikt hv koble kobling manøver kartmanøver', run: () => orbatVelger.toggle() },
    { id: 'unit-clear', title: 'Slett alle plasserte enheter', keywords: 'enhet unit clear', run: () => units.clear() },
    { id: 'bft', title: 'BFT — del min posisjon', hint: 'B', keywords: 'bft posisjon blue force gps egen', run: () => toggleBft() },
    { id: 'report', title: 'Ny stridsmelding', hint: 'C', keywords: 'melding salute sitrep medevac contact rapport tic', run: () => composer.toggle() },
    // ── FELTNETT-LINSER (UX-rapportens Fase 1) ────────────────────────────
    // Én oppføring per linse, med søkeord som treffer det operatøren TENKER
    // («hvor er sanitetsmeldingene») framfor navnet vi ga knappen. `filtrer` og
    // `linse` er med på alle, så én blindtreff finner hele familien.
    ...LINSER.map((l) => ({
      id: `feltnett-${l}`,
      title: `Tidslinje: vis ${LINSE_ETIKETT[l].toLowerCase()}`,
      keywords: `tidslinje feltnett logg hendelse filtrer filter linse ${l} ${LINSE_ETIKETT[l].toLowerCase()} ${LINSE_SOKEORD[l]}`,
      run: () => shell.settLinse(l),
    })),
    { id: 'fire', title: 'Ildstøtte / kill-chain (CFF, danger close, WEZ)', hint: 'X', keywords: 'ild fire cff call for fire artilleri bombekaster mål target wez danger close skytter', run: () => fireComposer.toggle() },
    { id: 'fire-clear', title: 'Tøm ildstøtte-laget (ringer/mål)', keywords: 'ild fire clear wez ring', run: () => clearFires() },
    { id: 'myndighet', title: 'Myndighet & ROE (matrise + delegasjoner)', keywords: 'myndighet roe autorisasjon delegasjon delegering matrise beslutningsrett frigivning kommando fullmakt', run: () => toggleMyndighet() },
    { id: 'targets', title: 'Mållista (kill-chain)', keywords: 'mål target liste kill chain prioritet tst hpt', run: () => toggleTargets() },
    { id: 'nineline', title: '9-line CAS (nærstøtte)', keywords: '9line nine line cas nærstøtte fly luftstøtte brief', run: () => toggleNineLine() },
    { id: 'rfi', title: 'RFI / innsamlingsstyring (intel)', keywords: 'rfi informasjonsbehov innsamling collection pir tasking etterretning behov', run: () => toggleRfi() },
    { id: 'dispatch', title: 'Dispatch-kø (vaktsjef)', keywords: 'dispatch kø vaktsjef anropskø lastdeling klaim tildeling triage zello', run: () => toggleDispatch() },
    { id: 'calendar', title: 'Dagsedler / kalender', keywords: 'dagsedel kalender aktivitet åt årlig trening skyting øvelse tasking distribuer echelon', run: () => toggleCalendar() },
    { id: 'fieldcalc', title: 'Feltkalkulatorer (DOPE/CARP/HLZ/GARS)', keywords: 'ballistikk dope carp luftdropp droppsone hlz landingsplass gars kalkulator beregn vind', run: () => toggleFieldCalc() },
    { id: 'orbat', title: 'ORBAT-creator — bygg styrkehierarki', keywords: 'orbat ordre battle styrke hierarki creator bygg tropp lag kompani bataljon brigade distrikt forsvarsgren foh echelon sidc milsymbol deleger sjef', run: () => toggleOrbat() },
    { id: 'orbat-popout', title: 'ORBAT på egen skjerm', keywords: 'orbat popout skjerm vindu styrkehierarki creator', run: () => popOut('orbat') },
    { id: 'rfi-popout', title: 'RFI på egen skjerm', keywords: 'rfi popout skjerm vindu', run: () => popOut('rfi') },
    { id: 'dispatch-popout', title: 'Dispatch-kø på egen skjerm', keywords: 'dispatch popout skjerm vindu vaktsjef', run: () => popOut('dispatch') },
    { id: 'perstat', title: 'PERSTAT / sanitet (tap-sporing)', keywords: 'perstat sanitet tap casualty kia wia mia triage mist medevac stridsevne', run: () => toggleCasualty() },
    { id: 'perstat-popout', title: 'PERSTAT på egen skjerm', keywords: 'perstat popout skjerm vindu sanitet', run: () => popOut('perstat') },
    { id: 'logistics', title: 'Logistikk / forsyning (LOGSTAT + resupply)', keywords: 'logistikk forsyning logstat resupply ammo drivstoff vann ammunisjon anmodning class supply', run: () => toggleLogistics() },
    { id: 'logistics-popout', title: 'Logistikk på egen skjerm', keywords: 'logistikk popout skjerm vindu forsyning', run: () => popOut('logistics') },
    { id: 'sentralbord', title: 'Feltveksler (samlet samband)', hint: 'S', keywords: 'feltveksler sentralbord samband chat toc kontakt melding melding ut rom kanal dm direktemelding 1:1 1:flere gnå matrix element samtale vedlegg tale swap', run: () => toggleSentralbord() },
    // «Chat / TOC» og «GNÅ 1:1 / melding ut» sto her som egne destinasjoner for
    // det Feltveksleren gjør (TOC-nettet er en kanal der; 1:1/rom er en samtale
    // der). Søkeordene deres er beholdt PÅ Feltveksleren over, så den som leter
    // etter «chat», «dm» eller «melding ut» finner den ene rette flata.
    { id: 'directory', title: 'Kontakter (adressebok + GNÅ-kontakter)', keywords: 'kontakter adressebok katalog kallesignal enhet distrikt område matrix id oppslag hvem gnå person soldat', run: () => toggleDirectory() },
    { id: 'feltveksler-popout', title: 'Feltveksler på egen skjerm', keywords: 'feltveksler popout skjerm vindu samband chat toc', run: () => popOut('feltveksler') },
    { id: 'ptt', title: 'Feltmelding / PTT (geolokalisert)', hint: 'F', keywords: 'ptt feltmelding tale melding geo posisjon snakk', run: () => togglePtt() },
    // ⚠ SØKEORDENE DEKKER BÅDE «kanal» OG «all call». En operatør som skal sette
    // hele distriktet på ett nett leter etter kommandoen, ikke etter panelnavnet —
    // og «dualwatch»/«triwatch» er ordene radiobrukeren har for vaktlista.
    { id: 'kanaler', title: 'Kanaler (PTT) — vaktliste, søk, egne grupper, All Call', keywords: 'kanal kanaler ptt vakt vaktliste dualwatch triwatch tx rx senderett all call allcall tvangs paalegg gruppe nett samband lytte', run: () => toggleKanaler() },
    { id: 'intel', title: 'Etterretning (INTEL)', hint: 'I', keywords: 'intel etterretning analyse stanag produkt vurdering', run: () => toggleIntel() },
    { id: 'intel-popout', title: 'Etterretning på egen skjerm', keywords: 'intel popout skjerm', run: () => popOut('intel') },
    { id: 'intel-package', title: 'Opprett etterretningspakke (kartutsnitt, 24t)', keywords: 'pakke eksport package rapport', run: () => exportIntelPackage() },
    { id: 'export-geojson', title: 'Eksporter situasjonsbilde (GeoJSON)', keywords: 'eksport export geojson gis qgis kart del', run: () => exportSituation('geojson') },
    { id: 'export-kml', title: 'Eksporter situasjonsbilde (KML → ATAK/Google Earth)', keywords: 'eksport export kml atak wintak google earth del', run: () => exportSituation('kml') },
    { id: 'export-cot', title: 'Eksporter til CoT/ATAK (XML)', keywords: 'eksport export cot atak tak wintak cursor on target del', run: () => exportCotFile() },
    { id: 'time', title: 'Tidsmaskin (spol tilbake / spill av)', hint: 'T', keywords: 'tid tidslinje historikk avspilling playback time', run: () => toggleTime() },
    { id: 'eso', title: 'ESO — Styrkeoppbygging (distribuer + rollup)', keywords: 'eso sjekkliste styrkeoppbygging klar beredskap distribuer lag tropp kompani bataljon brigade hierarki', run: () => toggleEso() },
    { id: 'eso-popout', title: 'ESO / Styrkeoppbygging på egen skjerm', keywords: 'eso popout skjerm styrkeoppbygging', run: () => popOut('eso') },
    { id: 'territory', title: 'HV-ansvarsområde (grense + nøkkelobjekter)', keywords: 'ansvarsområde aor territorium grense hv-område kristiansund nordmøre objektsikring nøkkelobjekt', run: () => toggleTerritory() },
    { id: 'admin', title: 'Administrasjon — inviter brukere', keywords: 'admin inviter enrolment bruker provisjonering rolle tilgang innstillinger', run: () => toggleAdmin() },
    { id: 'personregister', title: 'Personregister — koble og verifisere brukere til ORBAT', keywords: 'personregister medlemskap orbat binding verifisere verifisering verifiser tilbakekall bruker mannskapsliste roster godkjenn overordnet kommandolinje tilhørighet', run: () => togglePersonregister() },
    { id: 'kapabilitetsplanlegger', title: 'Kapabiliteter — anbefaling, plassering og bestilling', keywords: 'kapabilitet sensor drone bakkeradar kystradar kystmeldepost kyp soldat mobil observasjonspost rekkevidde behov anbefaling bestilling plassering utplassering', run: () => toggleKapabilitetsplanlegger() },
    { id: 'kapabilitetsplanlegger-popout', title: 'Kapabiliteter på egen skjerm', keywords: 'kapabilitet popout skjerm vindu', run: () => popOut('kapabilitetsplanlegger') },
    { id: 'personregister-popout', title: 'Personregister på egen skjerm', keywords: 'personregister popout skjerm vindu verifisering', run: () => popOut('personregister') },
    // ⚠ SAMBANDSDIAGNOSEN MÅ VÆRE NÅBAR UTEN curl. Endepunktene fra bølge 209
    // svarer på «lever broen, og hvor står meldingene» — men en diagnose ingen
    // kan åpne er like ubrukelig som ingen diagnose. Kommandopaletten er riktig
    // sted: dette er noe man leter etter i et feilsøkingsøyeblikk, ikke et panel
    // man har stående åpent.
    { id: 'samband-diagnose', title: 'Samband — lever broen mot GNÅ?', keywords: 'samband diagnose bro gnå levering kø utboks meldinger kommer ikke fram feilsøk', run: () => void visSambandDiagnose() },
    // ⚠ EMCON HØRER I KOMMANDOPALETTEN, IKKE I ET LAG-PANEL. Å pålegge
    // radiotaushet lammer avdelingens evne til å rapportere — det er en
    // kommandohandling på linje med ildfrigivning, og skal ikke ligge som en
    // vippebryter man treffer med albuen.
    { id: 'emcon-sett', title: 'EMCON — sett sendebegrensning (kommandohandling)', keywords: 'emcon radiotaushet radio silence sendeforbud emisjon stille alpha bravo charlie delta signaturdisiplin', run: () => void settEmcon() },
    { id: 'admin-popout', title: 'Administrasjon på egen skjerm', keywords: 'admin popout skjerm vindu', run: () => popOut('admin') },
    { id: 'pointer', title: 'Digital pointer (pek for laget)', hint: 'P', keywords: 'peker pointer', run: () => armPointer() },
    { id: 'draw-poly', title: 'Tegn polygon', hint: 'D', keywords: 'draw tegn', run: () => draw.setMode('polygon') },
    { id: 'draw-line', title: 'Tegn linje', keywords: 'draw', run: () => draw.setMode('linestring') },
    { id: 'draw-circle', title: 'Tegn sirkel', keywords: 'draw', run: () => draw.setMode('circle') },
    { id: 'draw-free', title: 'Frihåndstegning', keywords: 'draw', run: () => draw.setMode('freehand') },
    { id: 'draw-edit', title: 'Merk / flytt påtegning', keywords: 'select merk flytt rediger dra', run: () => draw.select() },
    { id: 'draw-del', title: 'Slett merket påtegning', keywords: 'delete slett valgt merket', run: () => { if (!draw.deleteSelected()) shell.showAlarm('Ingen påtegning er merket'); } },
    { id: 'draw-clear', title: 'Slett alle tegninger', keywords: 'clear slett alle', run: () => draw.clear() },
    { id: 'geofence-new', title: 'Ny geofence (tegn polygon)', hint: 'G', keywords: 'sone zone', run: () => { draw.setMode('polygon'); } },
    { id: 'geofence-save', title: 'Lagre tegning som geofence', keywords: 'sone zone', run: () => saveGeofence() },
    { id: 'grid', title: 'Rutenett av/på', keywords: 'grid mgrs', run: () => grid.toggle() },
    { id: 'crosshair', title: 'Sikte-kryss av/på', hint: 'H', keywords: 'crosshair sikte kryss reticle senter', run: () => shell.toggleCrosshair() },
    { id: 'meldfra', title: 'Meld fra — snakk eller skriv, markør på kartet', hint: 'F', keywords: 'meld fra feltmelding melding rapporter si observasjon markør hurtig delta tale diktere', run: () => meldFra.fokuser() },
    { id: 'arkiv', title: 'Observasjonsarkiv — søk i historikken', keywords: 'arkiv historikk observasjon bilde imint bildevegg mønster pattern of life dekningshull innhenting analytiker etterretning søk tid sted', run: () => toggleArkiv() },
    { id: 'kritiske-all', title: 'Kritiske objekter — vis alle + tegnforklaring (J-5)', keywords: 'kritiske objekter j5 hvt nøkkelobjekt infrastruktur militær energi transport tegnforklaring legend', run: () => showKritiske() },
    { id: 'kritiske-legend', title: 'Kritiske objekter — tegnforklaring av/på', keywords: 'tegnforklaring legend symbol forklaring kritiske kategori', run: () => kritiskePanel.toggle() },
    { id: 'kritiske-skjul', title: 'Kritiske objekter — skjul alle', keywords: 'skjul av kritiske objekter j5', run: () => { for (const k of ['MIL', 'EN', 'TR'] as const) kritiske.setOn(k, false); } },
    { id: 'infra-osm', title: 'Infrastruktur (OSM) — master/kraft/sykehus med høyde', keywords: 'infrastruktur osm overpass mast tårn antenne høyde kraft trafo sykehus dekning radiohorisont', run: () => showInfra() },
    { id: 'skandia', title: 'SKANDIA-styrker (Sverige + Danmark) av/på', keywords: 'skandia sverige danmark hemvärnet hjemmeværnet se dk bataljon distrikt', run: () => skandia.toggle() },
    { id: 'kritiske-mil', title: 'Kritiske objekter — militære', keywords: 'kritiske militær forsvar base leir flystasjon radar', run: () => kritiske.toggle('MIL') },
    { id: 'kritiske-en', title: 'Kritiske objekter — energi', keywords: 'kritiske energi kraft trafo gass olje raffineri', run: () => kritiske.toggle('EN') },
    { id: 'kritiske-tr', title: 'Kritiske objekter — transport/logistikk', keywords: 'kritiske transport bru havn bane spod apod tunnel', run: () => kritiske.toggle('TR') },
    { id: 'sunmoon', title: 'Lysforhold — BMNT/EENT, tussmørke, månefase og NVG', keywords: 'sol måne sun moon bmnt eent tussmørke lys dag natt nvg vindu lysforhold månefase illuminasjon midnattssol mørketid skydekke', run: () => toggleLysforhold() },
    { id: 'sikt', title: 'Siktelinje — fri/blokkert med høydeprofil (terreng)', keywords: 'sikt siktelinje siktlinje los line of sight profil hindring op observasjonspost maskering krumming', run: () => toggleSikt() },
    { id: 'malpeking', title: 'Målpeking — pek i kamerabildet, få avstand + kartvindu', keywords: 'målpeking target toggle kamera bilde horisont havflate landemerke resection asimut magnetometer avstand maritim båt kyst', run: () => toggleMalpeking() },
    { id: 'dekning', title: 'Dekning — viewshed for OP/radar/antenne', keywords: 'dekning viewshed synlighet radar antenne radiohorisont fresnel sensor sektor rekkevidde', run: () => toggleDekning() },
    { id: 'skygge', title: 'Terrengskygge — skjul og motlys for valgt tidspunkt', keywords: 'skygge terreng sol skjul dekning motlys op observasjonspost tidsskyver shadow', run: () => toggleSkygge() },
    { id: 'geoint', title: 'GEOINT-lag — ortofoto/DSM/termisk fra droneprosessering', keywords: 'geoint fotogrammetri ortofoto dsm dtm termisk drone odm nodeodm flis mbtiles kartlag', run: () => toggleGeointLag() },
    { id: 'planprosess', title: 'Utvikling til Strid — planprosess (HPBP)', keywords: 'uts utvikling til strid hpbp planprosess oppdragsanalyse handlemåte varslingsordre vo kavod vtssn ipb trinn stab plan', run: () => togglePlanprosess() },
    { id: 'ordre', title: 'Siste fempunktsordre (forflytning)', keywords: 'ordre fempunktsordre forflytning hildr smeac oppdrag manøver flytt', run: () => {
      if (ordrePanel.gjeldendeOrdre()) ordrePanel.el.classList.add('open');
      else shell.showAlarm('Ingen ordre ennå — dra en enhet til ny posisjon for å skrive en.');
    } },
    { id: 'terreng3d', title: '3D-terreng av/på', keywords: '3d terreng relieff høyde pitch vipp kamera terrain', run: () => toggle3d() },
    { id: 'weather', title: 'Meteorologi — vær for kartsenter (MET)', keywords: 'vær meteorologi met api.met.no vind temperatur nedbør weather forecast varsel', run: () => toggleWeather() },
    { id: 'bookmark-save', title: 'Lagre kart-bokmerke (nåværende utsnitt)', keywords: 'bokmerke bookmark saved view aoi lagre utsnitt', run: () => bookmarks.saveCurrent() },
    { id: 'bookmarks', title: 'Kart-bokmerker (interesseområder)', keywords: 'bokmerke bookmark saved view aoi hopp område', run: () => toggleBookmarks() },
    { id: 'mode', title: 'Bytt dag/natt/NVG-modus', hint: 'N', keywords: 'night nvg', run: () => cycleMode() },
    { id: 'center', title: 'Sentrer på egen posisjon', hint: 'Mellomrom', keywords: 'senter self', run: () => centerSelf() },
    { id: 'layer-sensors', title: 'Vis/skjul SPARK-sensorer', keywords: 'layer lag', run: () => cop.setLayerVisible('sensors', true) },
    { id: 'layer-targets', title: 'Vis/skjul deteksjonsmål', keywords: 'layer lag', run: () => cop.setLayerVisible('targets', true) },
  ];

  // Utviklerverktøy: bare tilgjengelig når appen kjører UTEN backend. Se
  // kommentaren over `checklist` for hvorfor kapabilitets-sjekklista ikke skal
  // kunne åpnes av en operatør.
  if (demo) {
    commands.push({
      id: 'checklist',
      title: 'Kapabilitets-sjekkliste (UTVIKLERVERKTØY)',
      keywords: 'sjekkliste kapabilitet utviklingsstatus dev intern',
      run: () => toggleChecklist(),
    });
  }

  function saveGeofence() {
    const polys = draw.snapshot().filter((f) => f.geometry.type === 'Polygon');
    if (!polys.length) { shell.showAlarm('Ingen polygon tegnet — tegn en teig først.'); return; }
    let lagret = 0;
    const brukte: (string | number)[] = [];
    for (const p of polys) {
      const ring = (p.geometry as { coordinates: number[][][] }).coordinates[0];
      if (geofence.add(ring, `Teig ${geofence.count() + 1}`)) {
        lagret++;
        if (p.id !== undefined) brukte.push(p.id as string | number);
      }
    }
    // Fjern KUN polygonene vi konverterte. `draw.clear()` her slettet tidligere
    // alle påtegninger for ALLE operatører, fordi onChange kringkaster hele
    // tegnesettet — et tomt sett vant over alt de andre hadde tegnet.
    draw.remove(brukte);
    shell.showAlarm(lagret
      ? `${lagret} teig${lagret === 1 ? '' : 'er'} lagret og delt med GNÅ`
      : 'Ingen gyldige teiger (et polygon trenger minst tre hjørner)');
  }
  function centerSelf() {
    const me = lastTracks.find((t) => t.kind === 'sensor');
    if (me) cop.map.flyTo({ center: [me.lon, me.lat], zoom: 14 });
  }

  const palette = new CommandPalette(commands, (q) => {
    const out: Command[] = [];
    const c = parseCoord(q);
    if (c) out.push({ id: 'goto', title: `Gå til ${mgrsForward(c, 5)}`, hint: 'Koordinat', run: () => gotoCoord(c) });
    const ql = q.toLowerCase();
    for (const t of lastTracks) {
      if (t.label.toLowerCase().includes(ql) || t.source.toLowerCase().includes(ql)) {
        out.push({ id: `t-${t.id}`, title: `Gå til ${t.label}`, hint: t.kind, run: () => { cop.flyTo(t); shell.showDetail(t); } });
      }
      if (out.length > 6) break;
    }
    return out;
  });

  new ContextMenu(cop.map, (lngLat) => {
    const lonlat: [number, number] = [lngLat.lng, lngLat.lat];
    let mgrs = '';
    try { mgrs = mgrsForward(lonlat, 5); } catch { /* utenfor sone */ }
    return [
      { label: '▣ Plasser enhet her', run: () => unitPalette.openAt(lonlat[0], lonlat[1]) },
      { label: '⛭ Plasser ORBAT-enhet her', run: () => orbatVelger.openAt(lonlat[0], lonlat[1]) },
      { label: '✎ Ny stridsmelding her', run: () => composer.openAt(lonlat[0], lonlat[1]) },
      { label: mgrs ? `Kopier: ${mgrs}` : 'Posisjon utenfor UTM', run: () => navigator.clipboard?.writeText(mgrs) },
      { label: 'Mål herfra', run: () => { measure.start(); } },
      { label: 'Dropp punkt', run: () => cop.dropPin(lonlat) },
      { label: 'Sentrer her', run: () => cop.map.flyTo({ center: lonlat }) },
    ];
  });

  installShortcuts(
    [
      { key: 'm', label: 'Mål avstand', run: () => measure.toggle() },
      { key: 'r', label: 'Planlegg rute', run: () => route.toggle() },
      { key: 'a', label: 'Tegn fremrykningsakse', run: () => { tac.setAffiliation('friend'); tac.start(); } },
      { key: 'p', label: 'Digital pointer', run: () => armPointer() },
      { key: 'u', label: 'Plasser enhet', run: () => unitPalette.toggle() },
      { key: 'b', label: 'BFT / min posisjon', run: () => toggleBft() },
      { key: 'i', label: 'Etterretning', run: () => toggleIntel() },
      { key: 'f', label: 'Feltmelding/PTT', run: () => togglePtt() },
      // «K — Chat/TOC» er fjernet med panelet: S (Feltveksler) er ÉN tast til
      // samband, og en hurtigtast som viste seg i hjelpen måtte ikke lede til et
      // grensesnitt som ikke finnes (UX-regel 7).
      { key: 's', label: 'Feltveksler', run: () => toggleSentralbord() },
      { key: 'c', label: 'Ny stridsmelding', run: () => composer.toggle() },
      { key: 't', label: 'Tidsmaskin', run: () => toggleTime() },
      { key: 'd', label: 'Tegn polygon', run: () => draw.setMode('polygon') },
      { key: 'g', label: 'Ny geofence', run: () => draw.setMode('polygon') },
      { key: 'n', label: 'Dag/natt/NVG', run: () => cycleMode() },
      { key: 'x', label: 'Ildstøtte / kill-chain', run: () => fireComposer.toggle() },
      { key: 'h', label: 'Sikte-kryss av/på', run: () => shell.toggleCrosshair() },
      { key: ' ', label: 'Sentrer på meg', run: () => centerSelf() },
    ],
    () => palette.toggle(),
  );

  // ── Bunn-verktøylinje (GRUPPERT for lav oppløsning) ─────────────────────
  dock = new ToolDock(shell.mapEl, [
    {
      id: 'g-kart', glyph: icon('map'), label: 'Kart', items: [
        { id: 'mode', glyph: '◐', label: 'Dag/natt/NVG', key: 'N', title: 'Bytt kartmodus', run: () => cycleMode() },
        // AV/PÅ-LAGENE MELDER TILSTAND. Før gjorde de det ikke: bryterne fungerte,
        // men docken viste aldri om laget var på, og med 17 oppføringer i denne
        // gruppa var de i praksis ugjenfinnbare. Fagansvarlig meldte at hen ikke
        // fant verken tråkorset eller AO-rødmerkinga — de FANTES, men var usynlige.
        // En bryter uten tilstand er ikke en bryter, det er en gjetning.
        { id: 'grid', glyph: '▦', label: 'Rutenett', title: 'Rutenett av/på', run: () => dock?.setActive('grid', grid.toggle()) },
        { id: 'crosshair', glyph: icon('crosshair'), label: 'Tråkors (sikte)', title: 'Tråkors i kartsenteret — for å lese av grid (H)', run: () => dock?.setActive('crosshair', shell.toggleCrosshair()) },
        { id: 'compass', glyph: icon('compass'), label: 'Kompass-overlegg', title: 'Kompassrose (grader + mils)', run: () => toggleCompass() },
        { id: 'weather', glyph: icon('weather'), label: 'Meteorologi', title: 'Vær for kartsenter (MET / api.met.no)', run: () => toggleWeather() },
        { id: 'lysforhold', glyph: LYSFORHOLD_IKON, label: 'Lysforhold', title: 'BMNT/EENT, tussmørke, månefase og NVG-vurdering for kartsenter', run: () => toggleLysforhold() },
        { id: 'sikt-clear', glyph: icon('trash'), label: 'Slett siktelinje', title: 'Fjern siktelinja, punktene og hindringsmarkøren fra kartet', run: () => slettSikt() },
        { id: 'malpeking-clear', glyph: icon('trash'), label: 'Slett målpeking', title: 'Fjern målpeking-punktene, ellipsen/sektoren fra kartet', run: () => nullstillMalpeking() },
        { id: 'dekning-clear', glyph: icon('trash'), label: 'Slett dekning', title: 'Fjern dekningsflate, kildepunkt og viewshed fra kartet', run: () => slettDekning() },
        { id: 'sikt', glyph: icon('crosshair'), label: 'Siktelinje', title: 'Fri/blokkert sikt mellom to punkt, med høydeprofil og hindringspunkt (henter høydedata)', run: () => toggleSikt() },
        { id: 'malpeking', glyph: icon('crosshair'), label: 'Målpeking', title: 'Pek i kamerabildet: avstand fra horisont+havflate, asimut fra landemerke-resection — vises som punkt/sektor + σ + metode', run: () => toggleMalpeking() },
        { id: 'dekning', glyph: '◉', label: 'Dekning', title: 'Viewshed for OP/radar/antenne — synlighet, radiohorisont og Fresnel (henter høydedata)', run: () => toggleDekning() },
        { id: 'skygge', glyph: SKYGGE_IKON, label: 'Terrengskygge', title: 'Terrengskygge for valgt tidspunkt — skjul/motlys (henter høydedata)', run: () => toggleSkygge() },
        { id: 'geoint', glyph: GEOINT_IKON, label: 'GEOINT-lag', title: 'Ferdige fotogrammetriprodukter (ortofoto/DSM/termisk) fra droneprosesseringskøen', run: () => toggleGeointLag() },
        // «3D-terreng» LÅ HER OGSÅ — fjernet (revisjonsfunn 25b). To brytere for én
        // tilstand er en løgn som venter på å skje. Bryteren bor nå bare i
        // kartlag-velgeren (⧉ oppe til venstre på kartet), sammen med Relieff,
        // Bygninger (3D) og «Overdriv»-glidebryteren som hører til den. Se
        // `TERRENG_EIES_AV_KARTVELGEREN` i `map/basemaps.ts` for hele begrunnelsen.
        // Docken beholder TERRENG-VERKTØYENE (siktelinje, dekning, terrengskygge):
        // de er noe man UTFØRER, ikke en innstilling for hvordan kartet tegnes.
        { id: 'bookmarks', glyph: '★', label: 'Bokmerker', title: 'Kart-bokmerker (interesseområder)', run: () => toggleBookmarks() },
        { id: 'time', glyph: '⏱', label: 'Tidsmaskin', key: 'T', title: 'Spol tilbake / spill av', run: () => toggleTime() },
        { id: 'territory', glyph: '▟', label: 'Eget AO — rødmerking', title: 'Rødmerking av eget ansvarsområde (kommune/fylke) — av/på', run: () => toggleTerritory() },
        { id: 'kritiske', glyph: icon('critical'), label: 'Kritiske objekter', title: 'J-5: militære/energi/transport (MIL/EN/TR) + tegnforklaring', run: () => showKritiske() },
        { id: 'infra', glyph: icon('mast'), label: 'Infrastruktur (OSM)', title: 'Master/tårn, kraft og sykehus MED høyde — henter for kartutsnittet (nett kreves)', run: () => showInfra() },
        { id: 'skandia', glyph: icon('unit'), label: 'SKANDIA-styrker', title: 'Svenske + danske hjemmevern-enheter (SE/DK) — av/på', run: () => skandia.toggle() },
        { id: 'center', glyph: '⊙', label: 'Sentrer på meg', key: '␣', title: 'Sentrer på egen posisjon', run: () => centerSelf() },
      ],
    },
    {
      id: 'g-tegn', glyph: icon('pencil'), label: 'Tegn & mål', items: [
        { id: 'draw', glyph: '✎', label: 'Tegn polygon/linje', key: 'D', title: 'Tegn', run: () => draw.setMode('polygon') },
        { id: 'draw-select', glyph: '⬚', label: 'Merk / flytt', title: 'Velg påtegning — klikk merk, dra flytt (også hjørner)', run: () => { draw.select(); shell.showAlarm('Merk-modus: klikk en påtegning, dra for å flytte'); } },
        { id: 'draw-del', glyph: '⌫', label: 'Slett valgt', title: 'Slett den merkede påtegningen', run: () => { if (!draw.deleteSelected()) shell.showAlarm('Ingen påtegning er merket — bruk «Merk / flytt» først'); } },
        { id: 'draw-clear', glyph: icon('trash'), label: 'Slett alle', title: 'Fjern alle påtegninger', run: () => draw.clear() },
        { id: 'pointer', glyph: '◉', label: 'Digital pointer', key: 'P', title: 'Pek for laget', run: () => armPointer() },
        { id: 'geofence', glyph: '⬡', label: 'Geofence', key: 'G', title: 'Ny geofence', run: () => draw.setMode('polygon') },
        { id: 'axis', glyph: '↗', label: 'Fremrykningsakse', key: 'A', title: 'Tegn akse', run: () => { tac.setAffiliation('friend'); tac.start(); } },
        { id: 'measure', glyph: '↔', label: 'Mål avstand', key: 'M', title: 'Mål avstand og peiling', run: () => measure.toggle() },
        { id: 'route-clear', glyph: icon('trash'), label: 'Slett ruta', title: 'Fjern ruteplanen fra kartet. NB: R-tasten og ruteknappen kaster ruta, mens «Avslutt (Esc)» beholder den — denne er den eksplisitte sletteveien', run: () => { route.stop(); shell.showAlarm('Ruteplanen er fjernet.'); } },
        { id: 'axis-clear', glyph: icon('trash'), label: 'Slett taktisk grafikk', title: 'Fjern alle fremrykningsakser fra kartet', run: () => { const n = tac.antallAkser; tac.clear(); shell.showAlarm(n ? `${n} akse(r) fjernet.` : 'Ingen taktisk grafikk på kartet.'); } },
        { id: 'measure-clear', glyph: icon('trash'), label: 'Slett målinger', title: 'Fjern alle fullførte avstandsmålinger fra kartet (én måling: lukk etiketten dens)', run: () => slettMaalinger() },
        { id: 'route', glyph: '⇢', label: 'Planlegg rute', key: 'R', title: 'Rute', run: () => route.toggle() },
      ],
    },
    {
      id: 'g-enhet', glyph: icon('unit'), label: 'Enheter & ild', items: [
        { id: 'unit', glyph: icon('unit'), label: 'Plasser enhet (APP-6)', key: 'U', title: 'Enhet', run: () => unitPalette.toggle() },
        // To inngangar med vilje: paletten markerer HVA SOM HELST (også fiende,
        // som ikke står i vår ORBAT), ORBAT-velgeren plasserer EGNE avdelinger
        // koblet til styrkestrukturen — det er koblingen ordre og stridsevne
        // leser. Tittelen sier forskjellen, så valget ikke krever forkunnskap.
        { id: 'orbat-plasser', glyph: '⛭', label: 'Plasser fra ORBAT', title: 'Plasser en EGEN avdeling fra styrkestrukturen — enheten på kartet blir den samme enheten som i ORBAT, slik at ordre får riktig nivå', run: () => orbatVelger.toggle() },
        { id: 'bft', glyph: '◎', label: 'BFT / min posisjon', key: 'B', title: 'Blue Force Tracking', run: () => toggleBft() },
        { id: 'fire', glyph: icon('fire'), label: 'Ildstøtte / kill-chain', key: 'X', title: 'CFF, danger close, WEZ', run: () => fireComposer.toggle() },
        { id: 'unit-clear', glyph: icon('trash'), label: 'Ta alle enheter av kartet', title: 'Fjern alle enheter DU har plassert. ORBAT-strukturen røres ikke', run: () => bekreftAlleEnheterAv() },
        { id: 'fire-clear', glyph: icon('trash'), label: 'Tøm ildstøtte-laget', title: 'Fjern alle fareavstands- og trusselringer og alle målkryss fra kartet', run: () => clearFires() },
        { id: 'targets', glyph: icon('crosshair'), label: 'Mållista', title: 'Kill-chain: prioritet + status', run: () => toggleTargets() },
        { id: 'alerts', glyph: icon('bell'), label: 'Varselkø', title: 'HVT/TST + nærhet + avvik, prioritert', run: () => toggleAlerts() },
        { id: 'myndighet', glyph: icon('authority'), label: 'Myndighet & ROE', title: 'Matrisa over beslutningsrett — iboende og delegert, fra nasjonalt nivå til enkeltmann. Gater frigivning av ild.', run: () => toggleMyndighet() },
      ],
    },
    {
      // ÉN inngang per destinasjon. Borte herfra: «GNÅ 1:1 / melding ut» og
      // «Chat / TOC» (samme samtale/nett som Feltveksleren — se kommentarene i
      // koden over), og «… på egen skjerm» (hvert panel har sin egen ⤢-knapp i
      // tittellinja; docken skal ikke være et andre sted å lære det fra).
      id: 'g-meld', glyph: icon('message'), label: 'Meldinger', items: [
        { id: 'sentralbord', glyph: icon('broadcast'), label: 'Feltveksler', key: 'S', title: 'Samlet samband — kanaler, GNÅ 1:1 og rom, vedlegg/tale, søk, kontaktkort', run: () => toggleSentralbord() },
        { id: 'report', glyph: icon('message'), label: 'Ny stridsmelding', key: 'C', title: 'SALUTE/SITREP/MEDEVAC', run: () => composer.toggle() },
        // Planprosessen hører under Meldinger, ikke Kart: den PRODUSERER ordrer.
        // Den lå tidligere i «Kart»-gruppa fordi de første koblingene (VTSSN fra
        // MET, KAVOD fra terrengmotoren) leser fra kartet — men det er hvor den
        // henter underlag fra, ikke hva den er til for.
        { id: 'planprosess', glyph: PLANPROSESS_IKON, label: 'Utvikling til Strid', title: 'Planprosessen (HPBP): fem trinn, oppdragsanalyse, handlemåter og varslingsordre', run: () => togglePlanprosess() },
        { id: 'directory', glyph: icon('contacts'), label: 'Kontakter', title: 'Finn HV-enheter + GNÅ-kontakter, «1:1» åpner samtalen i Feltveksler', run: () => toggleDirectory() },
        // ⚠ «Meld fra» er nå en PERMANENT LINJE nederst på kartet, ikke et
        // panel bak en meny (UX-funn F2). Dock-oppføringen står igjen som en vei
        // til å FINNE den — men den gir linja fokus i stedet for å åpne noe nytt,
        // for et panel som dukker opp over en linje som alt er der ville vært to
        // veier til samme handling (F6).
        { id: 'meldfra', glyph: icon('mic'), label: 'Meld fra', key: 'F', title: 'Snakk eller skriv → markør på kartet. Linja står alltid nederst.', run: () => meldFra.fokuser() },
        { id: 'ptt', glyph: icon('mic'), label: 'Meld fra — flere valg', title: 'Opplesning av innkommende meldinger (TTS) og utvidet felt', run: () => togglePtt() },
        { id: 'kanaler', glyph: icon('radio'), label: 'Kanaler (PTT)', title: 'Vaktliste, søk, egne grupper og All Call', run: () => toggleKanaler() },
      ],
    },
    {
      // ── Etterretning ─────────────────────────────────────────────────────
      // Gruppa hadde ÉN oppføring mens naboene hadde 2–17. En knapp som ser ut
      // som naboene, men bare åpner én ting, undergraver tilliten til hele
      // verktøylinja: operatøren slutter å tro at et ikon betyr en gruppe.
      // Innholdet her er derfor funksjoner som ALLEREDE FANTES (og bare var
      // tilgjengelige via kommandopaletten Ctrl+K, som man må vite om), samlet
      // der etterretningsarbeidet faktisk foregår. Ingen ny funksjonalitet.
      //
      // RFI/innsamlingsstyring er FLYTTET hit fra «Meldinger». Den ble lagt der
      // fordi en RFI sendes som en melding — men det er transporten, ikke
      // formålet. RFI ER innsamlingsstyring: krav → RFI → tasking → svar, samme
      // OODA-loop som etterretningspanelet. «Meldinger» har 9 oppføringer igjen.
      id: 'g-intel', glyph: icon('intel'), label: 'Etterretning', items: [
        { id: 'intel', glyph: icon('intel'), label: 'Etterretning (data→info→intel)', key: 'I', title: 'Etterretningsarbeidsflyt', run: () => toggleIntel() },
        { id: 'rfi', glyph: icon('question'), label: 'RFI / innsamling', title: 'Innsamlingsstyring: informasjonsbehov, tasking og svar', run: () => toggleRfi() },
        // DISP-01/B-003: vaktsjefens oversikt over SAMME RFI-kø, gruppert på
        // hastegrad med poengsum/begrunnelse og klaim/frigjøring (lastdeling
        // mellom operatører) — se filhodet i `ui/dispatch.ts`.
        { id: 'dispatch', glyph: icon('dispatch'), label: 'Dispatch-kø', title: 'Vaktsjefens delte anropskø: rangert på hastegrad, med poengsum, begrunnelse og klaim mellom operatører', run: () => toggleDispatch() },
        { id: 'arkiv', glyph: icon('arkiv'), label: 'Observasjonsarkiv', title: 'Historikken: søk i tid/sted, bildevegg (IMINT), mønster i liv, dekningshull — og koble funn til RFI', run: () => toggleArkiv() },
        { id: 'intel-package', glyph: icon('package'), label: 'Etterretningspakke', title: 'Pakk gjeldende kartutsnitt + siste 24t (spor, meldinger, intel) til nedlastbar fil', run: () => exportIntelPackage() },
        // «Etterretning på egen skjerm» er IKKE en dock-oppføring (FUNN 15): ett
        // mønster for eget vindu, og det er ⤢ i panelets egen tittellinje
        // (`intel-host` → POPOUT_KEY_BY_CLASS). To steder å lære samme handling
        // fra er hvordan docken vokste til 24 knapper i utgangspunktet.
      ],
    },
    {
      id: 'g-kalender', glyph: icon('calendar'), label: 'Dagsedler', items: [
        { id: 'calendar', glyph: icon('calendar'), label: 'Dagsedler / kalender', title: 'Echelon-distribuert aktivitetsstyring (ÅT, skyting, øvelse)', run: () => toggleCalendar() },
        { id: 'fieldcalc', glyph: icon('calc'), label: 'Feltkalkulatorer', title: 'Ballistikk/DOPE, luftdropp (CARP), HLZ-egnethet, GARS-rutenett', run: () => toggleFieldCalc() },
      ],
    },
    {
      id: 'g-styrke', glyph: icon('mountain'), label: 'Styrke & log', items: [
        { id: 'eso', glyph: icon('mountain'), label: 'ESO — styrkeoppbygging', title: 'Distribuer sjekklister + rollup', run: () => toggleEso() },
        { id: 'orbat', glyph: icon('orbat'), label: 'ORBAT-creator', title: 'Bygg styrkehierarki (FOH → lag), milsymbol + deleger sjef', run: () => toggleOrbat() },
        { id: 'perstat', glyph: '✚', label: 'PERSTAT / sanitet', title: 'Tap-sporing + triage', run: () => toggleCasualty() },
        { id: 'logistics', glyph: icon('fuel'), label: 'Logistikk / forsyning', title: 'LOGSTAT + resupply', run: () => toggleLogistics() },
      ],
    },
  ]);
  const dockEl = shell.mapEl.querySelector<HTMLElement>('.tool-dock');
  if (dockEl) stillBunnstabelDynamisk(meldFra.el, dockEl);

  // Speil lag-tilstanden i verktøylinja med én gang (av + 0 ved oppstart) slik at
  // knappen alltid forteller sannheten, ikke bare etter første klikk.
  syncKritiskeUi();
  syncPlanprosessUi();
  // Av/på-lagene MÅ speiles ved oppstart, ikke bare ved klikk. Tråkorset er nå på
  // som standard (og kan være husket av), så uten dette ville docken påstå «av»
  // mens korset står i kartet — verre enn ingen tilstandsvisning.
  dock?.setActive('crosshair', shell.isCrosshairOn());
  dock?.setActive('territory', territory.isVisible());
  dock?.setActive('grid', grid.isVisible());

  // ── Vindus-system: gjør flytende paneler til flyttbare/skalerbare vinduer ──
  // Registrer generisk (dekker alle nåværende + fremtidige paneler).
  const wm = new WindowManager();
  // `.plan-panel` står eksplisitt selv om den også bærer `.report-composer`:
  // treffer den bare via basisklassen, er registreringen avhengig av at ingen
  // senere fjerner den klassen fra planpanelet. Selektoren er en union, så
  // elementet registreres uansett bare én gang.
  // `.chat-panel` og `.gna-dm-panel` er ute: de panelene bygges ikke lenger her
  // (samlet i Feltveksleren). En selektor som peker på noe som ikke finnes er
  // stille løgn om hva appen består av.
  const WIN_SELECTORS = '.sb-panel, .kritiske-panel, .report-composer, .plan-panel, .checklist-panel, .intel-host, .ptt-panel, .thread-host, .bookmarks-panel, .admin-panel, .personregister-panel, .kapabilitetsplanlegger-panel, .directory-panel, .calendar-panel, .orbat-panel, .orbat-creator, .eso-panel, .myndighet-panel, .rfi-panel, .dispatch-panel, .arkiv-panel, .casualty-panel, .logistics-panel, .targetlist-panel, .alerts-panel, .fieldcalc-panel';
  /**
   * ÉN regel for kommunikasjonspanelene (FUNN 17): å åpne ett lukker de andre i
   * samme kategori. De fem her er de flytende panelene operatøren bruker til å
   * SNAKKE — de konkurrerer om samme plass foran kartet og om samme oppgave, og
   * stablet seg tidligere oppå hverandre uten at noe ryddet. Kartlag,
   * tegnforklaring, planprosess osv. står IKKE her: de skal kunne være åpne
   * samtidig som du melder.
   *
   * Verdien er panelets egen lukkefunksjon, ikke bare «fjern .open» — panelene
   * eier et internt `open`-flagg, og uten dette ville neste dock-klikk krevd to
   * trykk. `close()` der den finnes, ellers `toggle()` (som bare kalles på et
   * panel WindowManager vet er åpent).
   */
  const KOMM_KATEGORI = 'komm';
  const KOMM_LUKK: Partial<Record<string, () => void>> = {
    'sb-panel': () => sentralbord.toggle(),
    'directory-panel': () => directory.toggle(),
    'rfi-panel': () => rfiPanel.toggle(),
  };
  shell.mapEl.querySelectorAll<HTMLElement>(WIN_SELECTORS).forEach((el, i) => {
    const classes = el.className.split(/\s+/);
    const id = classes.find((c) => (c.endsWith('-panel') || c.endsWith('-host')))
      ?? classes.find((c) => c.endsWith('-creator'))
      ?? classes.find((c) => c.endsWith('-composer') && c !== 'report-composer')
      ?? classes.find((c) => c.endsWith('-composer'))
      ?? `win-${i}`;
    // Gi ⤢ pop-out-knapp der panelet har en frittstående mount (POPOUT_KEY_BY_CLASS).
    const popKey = classes.map((c) => POPOUT_KEY_BY_CLASS[c]).find(Boolean);
    const lukk = KOMM_LUKK[id];
    wm.register(id, el, undefined, popKey ? () => popOut(popKey) : undefined,
      lukk ? { kategori: KOMM_KATEGORI, lukk } : undefined);
  });
  // «Ny stridsmelding» og PTT henger i <body>, ikke i kartkolonnen, og fanges
  // derfor IKKE av sveipen over. Oppdaget i nettleseren: regelen virket mellom
  // Feltveksler og Kontakter, men stridsmeldingen la seg oppå Feltveksleren.
  // De registreres bare for kategori-regelen — plassering/z-orden og overflow
  // beholder de fra sin egen CSS (se `registrerKategori`).
  wm.registrerKategori('report-composer', composer.el, KOMM_KATEGORI, () => composer.close());
  wm.registrerKategori('ptt-panel', ptt.el, KOMM_KATEGORI, () => ptt.close());
  shell.onSingleScreen = (on) => wm.setSingle(on);
  shell.getSingleScreen = () => wm.isSingle();
  // Escape fjerner `.open` direkte på elementet, utenom panelets egen toggle().
  // Dock-merket må følge etter, ellers viser verktøylinja at et lukket panel er
  // åpent. Bare paneler som FAKTISK speiler tilstand i dock-en står her.
  wm.onLukket = (id) => { if (id === 'plan-panel') syncPlanprosessUi(); };

  // Levende objektliste (spor + plasserte enheter) i høyre panel.
  function affFromSidc(sidc: string): string {
    return { F: 'friend', H: 'hostile', N: 'neutral', U: 'unknown' }[sidc[1]] ?? 'unknown';
  }
  /** Bare sifrene i en Matrix-id («@gna4791994407:dom» → «4791994407»), som
   * skjult søkeord: operatøren får et telefonnummer, ikke en Matrix-adresse. */
  function sifreAv(id: string): string {
    return (id.match(/\d+/g) ?? []).join('');
  }
  // ── KARTOBJEKT-REGISTERET — «alt som ligger i kartet» ─────────────────────
  //
  // Eierkrav 2026-08-14: «alt som plasseres i kartet — enten det er enheter
  // eller det er tegneverktøy — må komme opp som objekter til høyre i skjermen
  // og være mulig å flytte/redigere.»
  //
  // Lista bygde tidligere på NØYAKTIG TO kilder: spor fra feeden og egne
  // plasserte enheter. Alt annet — siktelinjer, dekningsflater, teiger, ruter,
  // akser, målinger, påtegninger, ildstøtte — var usynlig for lista, for søket,
  // for seleksjonen og for detaljpanelet. Nå spør ÉN liste ETT register, og hver
  // eier svarer for sitt eget. Se `objekt/kartobjekt.ts` for hvorfor evnene er
  // DATA og ikke kode per panel.
  const objekter = new KartobjektRegister();
  const seleksjon = new Seleksjonseier();

  // Den nøytrale standardeieren. Uten den fantes det ingen tilstand der både
  // tegninger OG kartobjekter kunne merkes (se `KartVerktoy.noytral`).
  kartmodus.registrer({ id: 'velg', navn: 'VELG', noytral: true, stopp: () => { /* ingen tilstand */ } });
  kartmodus.settStandard('velg');

  /**
   * Merk et objekt: seleksjonen, det generiske detaljpanelet, så objektets egen
   * visning.
   *
   * REKKEFØLGEN ER BEVISST. Det generiske panelet tegnes FØRST og er GULVET:
   * hvert objekt får dermed nøyaktig de affordansene det har erklært, uten at
   * noen må huske å koble dem. En type med et rikere panel (spor via
   * `showDetail`, enhet via `visEnhetIPanel`) skriver over etterpå og vinner.
   * Alternativet — en betingelse per type på om panelet skal fylles — er nøyaktig
   * den «21 eiere med tomme celler»-modellen registeret finnes for å avskaffe.
   */
  function velgObjekt(o: Kartobjekt) {
    seleksjon.velg(o);
    visObjektIPanel(o);
    o.velg?.();
  }
  seleksjon.onEndring((s) => shell.settValgt(s?.id ?? null));

  /**
   * Detaljpanelet for et kartobjekt — handlingene DERIVERES av `evner`.
   *
   * Panelet rendrer nøyaktig det objektet har erklært: verken en «Flytt»-knapp
   * på en måling, eller et objekt uten vei ut. J3-funn 3 lukkes her.
   */
  function visObjektIPanel(o: Kartobjekt) {
    const naa = Date.now();
    const rader: Array<[string, string]> = [];
    if (o.senter) {
      let mgrs = '';
      try { mgrs = mgrsForward([o.senter.lon, o.senter.lat], 5); } catch { /* utenfor sone */ }
      if (mgrs) rader.push(['MGRS', mgrs]);
      rader.push(['POSISJON', `${o.senter.lat.toFixed(5)}, ${o.senter.lon.toFixed(5)}`]);
    }
    // ALDER på alt som har et tidspunkt — grunnloven, uten unntak.
    if (o.tsMs !== undefined) rader.push(['ALDER', maalingsLinje(o.tsMs, naa)]);
    if (o.eier) rader.push(['EIER', o.eier]);
    if (o.bevares) rader.push(['RETENSJON', 'bevares til noen slipper sporet']);
    rader.push(['KILDE', o.kilde]);
    const handlinger: Array<{ label: string; run: () => void }> = [];
    if (o.evner.flyttbar && o.flytt) handlinger.push({ label: 'Flytt …', run: () => o.flytt?.() });
    if (o.evner.redigerbar && o.rediger) handlinger.push({ label: '✎ Rediger', run: () => o.rediger?.() });
    if (o.evner.slettbar && o.slett) handlinger.push({ label: 'Slett', run: () => slettObjekt(o) });
    shell.showDetailRows(
      o.navn,
      `${TYPE_ETIKETT[o.type].toUpperCase()} · ${o.undertittel}`,
      rader,
      handlinger.length ? handlinger : undefined,
    );
  }

  /** «1,4 km» / «840 m» — samme form som resten av COP-en bruker. */
  function meterTekst(m: number): string {
    return m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${Math.round(m)} m`;
  }

  /**
   * Slett med bekreftelse NÅR handlingen rammer andre enn meg selv.
   *
   * Bindende presedens (bølge 148): friksjon på å BEGYNNE, ett klikk for å
   * stoppe. Men rapportens §4b fant asymmetrien motsatt vei — «slett alle
   * tegninger» gikk uten et spørsmål, mens det å begynne å dele posisjon krevde
   * en punktliste. Regelen her: et DELT objekt spør, mitt eget gjør det ikke.
   */
  function slettObjekt(o: Kartobjekt) {
    if (!o.evner.slettbar || !o.slett) return;
    if (!o.evner.delbar) { o.slett(); return; }
    bekreft({
      tittel: `Slette «${o.navn}»?`,
      tekst: `${TYPE_ETIKETT[o.type]} fjernes for ALLE operatører som ser dette bildet.`,
      punkter: ['Handlingen kan ikke angres for andre enn deg.'],
      bekreftTekst: 'Slett',
      onBekreft: () => o.slett?.(),
    });
  }

  // ── Kilde: SPOR (sensorer, deteksjoner, GNÅ-telefoner) ────────────────────
  objekter.registrer('spor', () => {
    const anom = anomalyByTrack(detectAnomalies(lastTracks)); // regelbasert triage
    const naa = Date.now();
    return lastTracks.map<Kartobjekt>((t) => {
      const a = anom.get(t.id);
      // GNÅ-BRUKERE SKAL SE UT SOM FOLK, IKKE SOM MASKIN-ID-ER.
      //
      // Eierkrav 2026-08-13: «Jeg vil ha alle GNÅ i listen over objekter når de
      // ligger i kartet, gjerne søkbart … I tillegg vil jeg se navnet (ikke
      // matrix-bruker gnaxxxx). Det blir ryddigere.»
      //
      // Navnet kommer fra hubben (ORBAT-bindingen, ellers visningsnavnet i
      // Synapse) og ligger i `t.person.navn` / `t.label`. Faller alt annet bort,
      // står localparten — det er sant, om enn stygt, og bedre enn tomt.
      // Matrix-id-en vises IKKE i raden, men er søkbar via `sok`: et nummer lest
      // opp over radio må fortsatt kunne slås opp.
      const erGna = t.kind === 'person' || t.source === GNA_BFT_SOURCE;
      const navn = t.person?.navn?.trim() || t.label;
      const mxid = t.person?.matrixId ?? '';
      const sub = erGna
        // «GNÅ» sier hvor sporet kommer fra (telefon i felt, ikke sensor eller
        // TAK-import); grad, alder (påkrevd på enhver måling, grunnloven) og
        // fart (når telefonen har GPS-fart) bygges av `gnaRosterSub` — se
        // DISP-02-kommentaren der for hvorfor batteri IKKE står i denne raden.
        ? gnaRosterSub(t, naa, a?.note)
        : (a ? `${t.kind} · ⚠ ${a.note}` : t.kind);
      return {
        id: objektId('spor', t.id),
        type: 'spor',
        kilde: erGna ? 'delt' : 'sensor',
        navn: erGna ? navn : t.label,
        undertittel: sub,
        sok: erGna ? `gnå gna ${mxid} ${sifreAv(mxid)} ${t.label}` : undefined,
        aff: t.affiliation,
        senter: { lon: t.lon, lat: t.lat },
        tsMs: t.tsMs,
        ferskhetMs: t.ferskhetMs,
        bevares: t.bevares,
        // ⚠ Et spor er en MÅLING: aldri flyttbar, aldri redigerbar. «Slipp
        // sporet» finnes BARE for bevarte spor (siste kjente posisjon) — det er
        // den ene fjern-veien retensjonsmodellen fra bølge 181 gir.
        evner: { ...OBSERVASJON, slettbar: t.bevares === true, delbar: true },
        velg: () => { cop.flyTo(t); shell.showDetail(t); cop.highlightAt(t.lon, t.lat); },
        slett: t.bevares === true ? () => shell.onDroppSpor?.(t) : undefined,
      };
    });
  });

  // ── Kilde: EGEN POSISJON (BFT) ────────────────────────────────────────────
  objekter.registrer('egenposisjon', () => {
    const u = units.get(BFT_SELF_ID);
    if (!u) return [];
    const naa = Date.now();
    return [{
      id: objektId('egenposisjon', u.id),
      type: 'egenposisjon',
      kilde: 'egen',
      navn: u.label,
      // Egen BFT-posisjon er en MÅLING og skal aldri stå i lista som «enhet» —
      // da leses den som noe en operatør har plassert og står inne for. Alderen
      // står i undertittelen, alltid (grunnloven).
      undertittel: `egen posisjon · ${u.tsMs === undefined ? 'måletid ukjent' : maalingsLinje(u.tsMs, naa)}`,
      aff: affFromSidc(u.sidc),
      senter: { lon: u.lon, lat: u.lat },
      tsMs: u.tsMs,
      // Deling slås av med BFT-bryteren, ikke ved å «slette» målingen.
      evner: { ...OBSERVASJON },
      velg: () => units.focus(u.id),
    }];
  });

  // ── Kilde: PLASSERTE ENHETER ──────────────────────────────────────────────
  objekter.registrer('enheter', () => units.snapshot()
    .filter((u) => u.id !== BFT_SELF_ID)
    .map<Kartobjekt>((u) => ({
      id: objektId('enhet', u.id),
      type: 'enhet',
      kilde: 'egen',
      navn: u.label,
      undertittel: 'enhet',
      aff: affFromSidc(u.sidc),
      senter: { lon: u.lon, lat: u.lat },
      tsMs: u.endretMs,
      // Enhetene deles til alle operatører (`feed.send({type:'units'})`), så
      // sletting spør. Flytting går via klikk-modusen, som også gir nettbrett en
      // vei — kartlaget lytter bare på `mousedown` (funn 9, Fase C).
      evner: { ...PLANOBJEKT, delbar: true },
      velg: () => units.focus(u.id),
      flytt: () => {
        if (units.armFlytting(u.id)) {
          shell.showAlarm(`Klikk der ${u.label} skal stå. Esc avbryter.`);
        }
      },
      rediger: () => unitPalette.openForEndring({ id: u.id, label: u.label, sidc: u.sidc }),
      slett: () => {
        if (units.remove(u.id)) {
          shell.clearDetail();
          shell.showAlarm(`${u.label} tatt av kartet.`);
        }
      },
    })));

  // ── Kilde: PÅTEGNINGER (terra-draw) ───────────────────────────────────────
  objekter.registrer('tegninger', () => draw.snapshot().flatMap<Kartobjekt>((f) => {
    const g = f.geometry as { type: string; coordinates: unknown };
    // Terra-draws EGNE håndtak (midtpunkt/hjørne) er features i samme lager.
    // De er ikke operatørens tegninger og skal ikke stå i lista — de spiser
    // dessuten av taket på 500 features vi kringkaster (funn 12).
    const p = (f.properties ?? {}) as { mode?: string; midPoint?: boolean; selectionPoint?: boolean };
    if (p.midPoint || p.selectionPoint) return [];
    const id = String((f as { id?: string | number }).id ?? '');
    if (!id) return [];
    const senter = geometriSenter(g);
    const form = { Polygon: 'flate', LineString: 'linje', Point: 'punkt' }[g.type] ?? g.type;
    return [{
      id: objektId('tegning', id),
      type: 'tegning',
      kilde: 'egen',
      navn: `Påtegning ${form}`,
      undertittel: p.mode ? `${form} · ${p.mode}` : form,
      color: 'var(--gold)',
      senter,
      // Eierbeslutning 2026-08-14: «et tegneverktøy må kunne slettes, flyttes og
      // redigeres». Terra-draws «select»-modus ER både flytting og redigering
      // (dra figuren, dra hjørner, dra midtpunkt) — `flytt` og `rediger` peker
      // derfor samme sted, og det er sant heller enn to knapper som lover ulikt.
      evner: { ...PLANOBJEKT, delbar: true },
      velg: () => { if (senter) cop.map.flyTo({ center: [senter.lon, senter.lat] }); },
      flytt: () => { draw.select(); shell.showAlarm('Velg-modus: dra figuren eller hjørnene.'); },
      rediger: () => { draw.select(); shell.showAlarm('Velg-modus: dra hjørner og midtpunkt for å endre formen.'); },
      slett: () => {
        if (draw.remove([id])) {
          feed.send?.({ type: 'draw', features: draw.snapshot() });
          objekter.meld();
          shell.showAlarm('Påtegningen er slettet.');
        }
      },
    }];
  }));

  // ── Kilde: TEIGER (geofence) ──────────────────────────────────────────────
  //
  // ⚠ Dette var det ALVORLIGSTE av de fem manglende fjerne-veiene:
  // `GeofenceTool.remove()` fantes ferdig skrevet og ble ALDRI kalt fra UI, mens
  // en lagret teig deles til alle operatører OG til alle GNÅ-telefoner. En teig
  // var altså permanent for hele nettet.
  objekter.registrer('teiger', () => geofence.snapshot().map<Kartobjekt>((t) => {
    const senter = t.center
      ? { lon: t.center.lon, lat: t.center.lat }
      : ringSenter(t.polygon.map((p) => [p.lon, p.lat]));
    const form = t.shape === 'circle' ? `sirkel ${Math.round((t.radius_m ?? 0))} m` : `${t.polygon.length} punkt`;
    return {
      id: objektId('teig', t.id),
      type: 'teig',
      kilde: 'delt',
      navn: t.name || 'Teig uten navn',
      undertittel: `${form} · varsler ${t.retninger.join('/')} · ${t.gjelder_for}`,
      color: 'var(--aff-hostile)',
      senter,
      tsMs: t.updated_at_ms,
      evner: { ...OBSERVASJON, slettbar: true, delbar: true },
      velg: () => { if (senter) cop.map.flyTo({ center: [senter.lon, senter.lat] }); },
      slett: () => {
        if (geofence.remove(t.id)) {
          objekter.meld();
          shell.showAlarm(`Teigen «${t.name || t.id}» er slettet — også i GNÅ-appene.`);
        }
      },
    };
  }));

  // ── Kilde: SIKTELINJE ─────────────────────────────────────────────────────
  //
  // §6.5 i rapporten: et sikteresultat er en PÅSTAND OM TERRENGET på et
  // tidspunkt, med inndata. Uten identitet kan ingen se hva påstanden bygget på.
  objekter.registrer('siktelinje', () => {
    const st = sikt.tilstand;
    if (!st.a || !st.b) return [];
    const status = sikt.statusNå();
    return [{
      id: objektId('siktelinje', 'aktiv'),
      type: 'siktelinje',
      kilde: 'egen',
      navn: 'Siktelinje A→B',
      undertittel: `${meterTekst(st.avstandM())} · obs ${st.observatorHoydeM} m → mål ${st.malHoydeM} m · ${status}`,
      color: 'var(--info)',
      senter: { lon: st.a.lon, lat: st.a.lat },
      evner: { velgbar: true, flyttbar: true, redigerbar: true, slettbar: true, delbar: false },
      velg: () => cop.map.flyTo({ center: [st.a!.lon, st.a!.lat] }),
      // «Flytt» her betyr flytt OBSERVATØREN og regn på nytt — det er den
      // flyttingen som betyr noe for en siktelinje.
      flytt: () => { sikt.armPunkter(); shell.showAlarm('Klikk nytt observatørpunkt (A), så mål (B).'); },
      rediger: () => { if (!sikt.apen) sikt.toggle(); },
      slett: () => slettSikt(),
    }];
  });

  // ── Kilde: RADIODEKNING ───────────────────────────────────────────────────
  objekter.registrer('dekning', () => {
    const k = dekning.kildepunkt;
    if (!k) return [];
    const i = dekning.innstillinger;
    return [{
      id: objektId('dekning', 'aktiv'),
      type: 'dekning',
      kilde: 'egen',
      navn: `Radiodekning ${i.modus}`,
      undertittel: `ant ${i.antennehoydeM} m · ${i.freqMHz} MHz · r ${i.radiusKm} km · ${dekning.statusNå()}`,
      color: 'var(--sage)',
      senter: { lon: k.lon, lat: k.lat },
      evner: { velgbar: true, flyttbar: true, redigerbar: true, slettbar: true, delbar: false },
      velg: () => cop.map.flyTo({ center: [k.lon, k.lat] }),
      flytt: () => { dekning.armKilde(); shell.showAlarm('Klikk ny kilde (antenneplassering).'); },
      rediger: () => { if (!dekning.apen) dekning.toggle(); },
      slett: () => slettDekning(),
    }];
  });

  // ── Kilde: ILDSTØTTE (mål med ring og målkryss) ───────────────────────────
  objekter.registrer('ildstotte', () => targetList.map<Kartobjekt>((t) => ({
    id: objektId('ild', t.id),
    type: 'ild',
    kilde: 'egen',
    navn: t.number,
    undertittel: `${FIRE_STATUS_LABEL[t.status]} · ${FIRE_TARGET_PRIORITY_LABEL[t.priority]}`,
    color: 'var(--aff-hostile)',
    senter: { lon: t.lon, lat: t.lat },
    tsMs: t.tsMs,
    evner: { ...OBSERVASJON, slettbar: true },
    velg: () => { cop.map.flyTo({ center: [t.lon, t.lat] }); cop.highlightAt(t.lon, t.lat); },
    slett: () => { fjernMaal(t.id); objekter.meld(); },
  })));

  // ── Kilde: KAPABILITETER (sensorer/droner/observasjonsposter) ────────────
  // Eiermål 2026-08-19. Velgbar/slettbar/delbar, men IKKE flyttbar herfra —
  // flytting skjer via slett+plasser på nytt med bevisst NYE parametre (en
  // kapabilitet SKAL kunne endre posisjon, men et drag i kartet ville ikke
  // spurt om ny rekkevidde/dag-natt), samme disiplin som at en MÅLING ikke er
  // flyttbar (bølge 149), bare omvendt begrunnet.
  objekter.registrer('kapabiliteter', () => kapabilitetsplanleggerPanel.gjeldende.map((k) => ({
    id: objektId('kapabilitet', k.id),
    type: 'kapabilitet',
    kilde: 'delt',
    navn: k.navn?.trim() || KAPABILITET_TYPE_LABEL[k.type],
    undertittel: `${KAPABILITET_TYPE_LABEL[k.type]} · rekkevidde ${(k.rekkeviddeM / 1000).toFixed(1)} km`,
    color: 'var(--info)',
    senter: { lon: k.lon, lat: k.lat },
    tsMs: k.opprettetMs,
    evner: { velgbar: true, flyttbar: false, redigerbar: false, slettbar: true, delbar: true },
    velg: () => { cop.map.flyTo({ center: [k.lon, k.lat] }); cop.highlightAt(k.lon, k.lat); },
    // `onEndret`-hooken (kapabilitetsplanlegger-konstruktøren) melder
    // objektregisteret etter at `last()` henter listen på nytt — sletting er
    // async, og objektlisten skal ikke vise en fjernet rad borte før hubben
    // faktisk har bekreftet den.
    slett: () => { void kapabilitetSend('/kapabilitet/slett', { id: k.id }).then(() => kapabilitetsplanleggerPanel.last()); },
  })));

  // ── Kilde: RUTEPLAN ───────────────────────────────────────────────────────
  objekter.registrer('rute', () => {
    const p = route.forstePunkt;
    if (!p || route.antallWp < 2) return [];
    return [{
      id: objektId('rute', 'aktiv'),
      type: 'rute',
      kilde: 'egen',
      navn: 'Ruteplan',
      undertittel: `${route.antallWp} veipunkt · ${meterTekst(route.lengdeM)}`,
      color: 'var(--info)',
      senter: p,
      evner: { ...OBSERVASJON, slettbar: true },
      velg: () => cop.map.flyTo({ center: [p.lon, p.lat] }),
      slett: () => { route.stop(); objekter.meld(); shell.showAlarm('Ruta er slettet.'); },
    }];
  });

  // ── Kilde: FREMRYKNINGSAKSER ──────────────────────────────────────────────
  objekter.registrer('akser', () => tac.akser().map<Kartobjekt>((a) => ({
    id: objektId('akse', a.id),
    type: 'akse',
    kilde: 'egen',
    navn: `Fremrykningsakse ${a.id}`,
    undertittel: `${a.aff === 'hostile' ? 'fiendtlig' : 'egen'} · ${meterTekst(a.lengdeM)}`,
    aff: a.aff,
    senter: a.senter,
    evner: { ...OBSERVASJON, slettbar: true },
    velg: () => cop.map.flyTo({ center: [a.senter.lon, a.senter.lat] }),
    slett: () => { if (tac.fjernAkse(a.id)) shell.showAlarm('Aksen er slettet.'); },
  })));

  // ── Kilde: AVSTANDSMÅLINGER ───────────────────────────────────────────────
  objekter.registrer('maalinger', () => measure.maalinger().map<Kartobjekt>((m) => ({
    id: objektId('maaling', m.id),
    type: 'maaling',
    kilde: 'egen',
    navn: `Måling ${meterTekst(m.lengdeM)}`,
    undertittel: `${m.feats.length - 1} punkt`,
    color: 'var(--gold)',
    senter: m.senter,
    // En avstandsmåling er et RESULTAT, ikke en plan: den kan ikke flyttes.
    evner: { ...OBSERVASJON, slettbar: true },
    velg: () => cop.map.flyTo({ center: [m.senter.lon, m.senter.lat] }),
    slett: () => { if (measure.fjernMaaling(m.id)) { objekter.meld(); shell.showAlarm('Målingen er slettet.'); } },
  })));

  /** Representativt punkt for en GeoJSON-geometri (til `flyTo` og lista). */
  function geometriSenter(g: { type: string; coordinates: unknown }): { lon: number; lat: number } | undefined {
    if (g.type === 'Point') {
      const c = g.coordinates as [number, number];
      return { lon: c[0], lat: c[1] };
    }
    if (g.type === 'LineString') return ringSenter(g.coordinates as number[][]);
    if (g.type === 'Polygon') return ringSenter((g.coordinates as number[][][])[0] ?? []);
    return undefined;
  }
  /** Aritmetisk midtpunkt av en ring. Godt nok for `flyTo`; ikke et tyngdepunkt. */
  function ringSenter(ring: number[][]): { lon: number; lat: number } | undefined {
    if (!ring.length) return undefined;
    let lon = 0; let lat = 0;
    for (const p of ring) { lon += p[0]; lat += p[1]; }
    return { lon: lon / ring.length, lat: lat / ring.length };
  }

  /**
   * Tegn Objekter-lista fra registeret.
   *
   * Navnet står som det gjorde («roster») fordi det kalles fra ~10 steder, men
   * den bygger ikke lenger sin egen mening om hva som finnes i kartet.
   */
  function rebuildRoster() {
    const alle = objekter.alle();
    // Et merket objekt som er borte skal ikke bli en aktiv rad uten rad, og ikke
    // et mål for Delete.
    seleksjon.behold((id) => alle.some((o) => o.id === id));
    if (objekter.brudd.length) {
      // Kontraktsbrudd skal ikke være stille. De havner i konsollen med kildenavn
      // — det er utvikleren som skal se dem, ikke operatøren.
      console.warn('[kartobjekt] kontraktsbrudd:', objekter.brudd);
    }
    shell.setRoster(alle.map<RosterItem>((o) => ({
      id: o.id,
      label: o.navn,
      sub: o.undertittel,
      aff: o.aff ?? 'unknown',
      color: o.color,
      sok: [o.sok, TYPE_ETIKETT[o.type]].filter(Boolean).join(' '),
      gruppe: TYPE_ETIKETT[o.type],
      onClick: () => velgObjekt(o),
      onSlett: o.evner.slettbar ? () => slettObjekt(o) : undefined,
      slettTittel: `Slett ${o.navn}`,
    })));
  }
  objekter.onEndring(() => rebuildRoster());

  // Testhåndtak for Fase B. ⚠ De MÅ settes her, ikke i blokka lenger opp:
  // `objekter` og `seleksjon` deklareres på dette punktet i filen, og en
  // `Object.assign` som leser dem tidligere kaster ReferenceError (temporal dead
  // zone) og river hele oppstarten. Fanget av e2e-en, som ikke fikk appen opp.
  //
  // Påstanden — «alt som plasseres i kartet kommer opp som objekter til høyre» —
  // kan ikke leses av et skjermbilde, og en DOM-opptelling ser ikke forskjell på
  // en tom liste og en liste som aldri ble tegnet.
  if (demo) {
    Object.assign(window as unknown as Record<string, unknown>, {
      __skadeObjekter: objekter,
      __skadeSeleksjon: seleksjon,
    });
  }

  // ── GEST-FAMILIEN: Escape, Delete og ⌘Z under ÉN avtale ───────────────────
  //
  // Se `ui/gester.ts` for hvorfor dette er en PORT foran de åtte gamle Escape-
  // lytterne og ikke en niende. Prioriteten her er hele kontrakten:
  //
  //   Escape:  aktivt verktøy  →  merket objekt  →  (videre til panel/overlegg)
  //   Delete:  merket objekt (med bekreftelse når det er delt)
  //   ⌘Z:      angre påtegning
  //
  // Verktøy FØR panel er rettelsen av det konkrete løftebruddet: modus-stripa
  // sier «Avslutt (Esc)», men med et panel åpent lukket Escape panelet i stedet.
  installerGester({
    escape: [
      { navn: 'verktøy', kan: () => kartmodus.eierKlikk(), utfor: () => { kartmodus.stoppAlt(); } },
      { navn: 'seleksjon', kan: () => seleksjon.valgt !== null, utfor: () => { seleksjon.opphev(); shell.clearDetail(); } },
    ],
    slett: [{
      navn: 'slett merket',
      kan: () => {
        const s = seleksjon.valgt;
        if (!s) return false;
        const o = objekter.finn(s.id);
        return o?.evner.slettbar === true && typeof o.slett === 'function';
      },
      utfor: () => {
        const s = seleksjon.valgt;
        const o = s ? objekter.finn(s.id) : undefined;
        if (o) slettObjekt(o);
      },
    }],
    angre: [{ navn: 'påtegning', kan: () => draw.kanAngre(), utfor: () => { draw.angre(); objekter.meld(); } }],
    gjenta: [{ navn: 'påtegning', kan: () => draw.kanGjenta(), utfor: () => { draw.gjenta(); objekter.meld(); } }],
  });

  // ── Datafeed ──────────────────────────────────────────────────────────
  const feed: Feed = wsUrl ? new LiveFeed(wsUrl) : new DemoFeed();
  const seenAlarms = new Set<string>();
  // Delt tegne-tavle: andre operatørers grafikk, holdt per forfatter så samtidige
  // tegnere ikke overskriver hverandre. Egne tegninger vises av terra-draw selv.
  const sharedDraw = new Map<string, unknown[]>();

  // Mister vi sesjonen (WS avvist 4401 / utløpt) → tilbake til innlogging.
  (feed as LiveFeed).onAuthError?.(() => {
    showLogin(root!, (u) => bootApp(u, false), 'Sesjonen utløp — logg inn på nytt.');
  });

  // ── EMCON-STRIPA ──────────────────────────────────────────────────────────
  //
  // `emcon.ts` har hatt null kallsteder siden den ble skrevet. Hubben eier nå
  // nivået; dette er der operatøren SER det. ⚠ EMCON håndheves i klienten —
  // visningen er ikke pynt over en backend-regel, den er hvordan regelen virker.
  const emconStripe = new EmconStripe();
  shell.mapEl.appendChild(emconStripe.el);

  // ── KANALOVERSIKT + ALL CALL (PTT-15/17) ──────────────────────────────────
  //
  // Eier: «det viktigste er at vi kan ta ibruk actionknapp på iphone og at vi får en
  // ryddig kanaloversikt, som også er søkbar og at man kan opprette egne grupper» +
  // «en slags "all Call" Spesielt for å sikre at alle er stilt på riktig kanal».
  //
  // ⚠ HUBBEN EIER VAKTLISTA (§4): hvilke kanaler en klient er med i er en
  // tilgangsavgjørelse. Panelet viser og ber; det bestemmer ikke.
  async function pttKall(op: string, kropp: Record<string, unknown>): Promise<string | null> {
    const r = await fetch(op === 'allcall' ? '/ptt/allcall' : '/ptt/vakt', {
      method: 'POST', credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(kropp),
    });
    const svar = await r.json().catch(() => ({})) as {
      melding?: string; error?: string; advarsel?: string; avvist?: Array<{ bruker: string; grunn: string }>;
    };
    // ⚠ 409 ER ET REGELAVSLAG, IKKE EN FEIL. Teksten fra contracts sier hvilken
    // regel — «vaktlista er full», «nødkanalen kan ikke fjernes» — og den er skrevet
    // for operatøren. Å vise «HTTP 409» her ville kastet bort hele poenget.
    if (!r.ok) return svar.error ?? `Kunne ikke utføre (${r.status})`;
    // ⚠ ADVARSELEN VINNER OVER KVITTERINGEN. En All Call der tre ikke fikk kanalen
    // er ikke en vellykket All Call, og «Sendt» ville vært en løgn.
    if (svar.advarsel) {
      const navn = (svar.avvist ?? []).map((a) => a.bruker).join(', ');
      return `${svar.advarsel}${navn ? ` (${navn})` : ''}`;
    }
    return svar.melding ?? null;
  }

  const kanalPanel = new KanaloversiktPanel({
    hent: async () => {
      const r = await fetch('/ptt/vakt', { credentials: 'include' });
      if (!r.ok) return null;
      return await r.json() as KanaloversiktData;
    },
    settTx: (kanalId) => pttKall('vakt', { op: 'tx', kanalId }),
    leggTil: (kanalId, navn) => pttKall('vakt', { op: 'legg-til', kanalId, navn }),
    fjern: (kanalId) => pttKall('vakt', { op: 'fjern', kanalId }),
    // ⚠ KNAPPEN BYGGES UANSETT, OG HUBBEN AVGJØR. Skjulte vi den for brukere uten
    // kommandofullmakt, ville klient-gating vært den eneste kontrollen — og §4 er
    // tydelig: klient-gating er aldri nok. Mangler fullmakten, svarer hubben 403 med
    // en tekst operatøren kan lese.
    allCall: (kanalId, navn, grunn, ogsaaTx) =>
      pttKall('allcall', { kanalId, navn, grunn, ogsaaTx }),
    varsle: (t) => shell.showAlarm(t),
  });
  shell.mapEl.appendChild(kanalPanel.el);
  function toggleKanaler() { kanalPanel.toggle(); }

  // En All Call fra en annen operatør skal RIVE i den som får den, og havne i
  // tidslinja — det er en kommandohandling, og loggsporet ER kontrollen.
  feed.onCollab?.((msg) => {
    const m = msg as unknown as { kind?: string; allcall?: { navn?: string; paalagtAv?: string; grunn?: string; ogsaaTx?: boolean } };
    if (m.kind !== 'ptt-allcall' || !m.allcall) return;
    const ac = m.allcall;
    const tekst = `${ac.paalagtAv ?? 'SKADE'} har satt alle på ${ac.navn ?? 'kanal'}`
      + `${ac.ogsaaTx ? ' (også senderett)' : ' (lytting)'}${ac.grunn ? `: ${ac.grunn}` : ''}`;
    shell.showAlarm(tekst);
    loggHendelse({
      id: `allcall-${Date.now()}`, tsMs: Date.now(), type: 'autorisasjon',
      tittel: `All Call: ${ac.navn ?? 'kanal'}`,
      detalj: tekst,
      // IMMEDIATE: en operatør som ikke får med seg kanalbyttet lytter på feil nett.
      hastegrad: 'immediate',
      onClick: () => { kanalPanel.el.classList.add('open'); void kanalPanel.oppfrisk(); },
    });
    void kanalPanel.oppfrisk();
  });

  // ── GRUNNBILDET: ÅPNE KILDER PÅ KARTET (eierbeslutning 2026-08-18) ────────
  //
  // «Jeg vil heller at politihendelsene legges i kart på "estimert posisjon", med
  // mulighet for å flytte den hvis den ikke er relevant — som med alle andre
  // hendelser, mål eller observasjoner. Hvis vi ikke er sikre — skal det vises, men
  // være ærlig på at vi faktisk ikke vet helt sikkert.»
  //
  // ⚠ RETTINGEN GÅR TIL HUBBEN, ikke bare til dette kartet. Rettet Ivar markøren i
  // sin nettleser og Kari ikke fikk det, ville de to sett ulike posisjoner for samme
  // hendelse — og et delt situasjonsbilde der to operatører ser ulike steder er
  // verre enn et grovt estimat begge ser.
  const grunnbildeObjekter = new Map<string, GrunnbildeObjekt>();
  const grunnbildelag = new Grunnbildelag(cop.map, {
    annenEier: () => kartmodus.eierKlikk(),
    onFlyttet: (id, lat, lon) => { void flyttGrunnbilde(id, lat, lon); },
    onKlikk: (o) => visGrunnbildeObjekt(o),
  });

  /** Detaljpanelet for et grunnbildeobjekt. */
  function visGrunnbildeObjekt(o: GrunnbildeObjekt): void {
    const naa = Date.now();
    const rader: Array<[string, string]> = [
      ['Kilde', `${o.proveniens.kildeNavn}${o.proveniens.lisens ? ` · ${o.proveniens.lisens}` : ''}`],
      ['Kategori', o.type],
      // ⚠ POSISJONSLINJA STÅR ØVERST BLANT DETALJENE. Det er den opplysningen som
      // avgjør om operatøren kan handle på markøren eller må rette den først.
      ['Posisjon', posisjonsTekst(o, naa)],
      ['Registrert', new Date(o.tsMs).toLocaleString('nb-NO')],
      // ⚠ FORSINKELSEN VISES NÅR DEN BETYR NOE. En hendelse som var en time gammel
      // da vi fikk den er noe helt annet enn en fersk.
      ['Forsinkelse', grunnbildeForsinkelseTekst(o) || 'fersk ved mottak'],
      // ⚠ BEGGE LEVETIDENE VISES. «Grå etter et døgn» og «borte etter en uke» er to
      // ulike lovnader til operatøren, og hun skal kunne se hvor i løpet hendelsen
      // er — ellers er gråfargen en tilstand uten forklaring.
      ['Ferskhet', foreldelsesTekst(o, naa) || 'oppdatert av kilden nylig'],
      ['Står i bildet', grunnbildeLevetidTekst(o, naa)],
      ['Utdrag', o.utdrag || '(ingen tekst)'],
    ];
    if (o.forbehold) rader.push(['⚠ Forbehold', o.forbehold]);
    const handlinger: Array<{ label: string; run: () => void }> = [];
    if (o.proveniens.url) {
      // ⚠ LENKE UT, IKKE HELE ARTIKKELEN. Opphavsrett: vi viser overskrift + utdrag
      // + lenke (se MAKS_UTDRAG i contracts).
      handlinger.push({ label: 'Åpne hos kilden ↗', run: () => window.open(o.proveniens.url, '_blank', 'noopener') });
    }
    if (o.lat !== undefined && o.lon !== undefined) {
      const lon = o.lon, lat = o.lat;
      handlinger.push({ label: '⌖ Vis på kartet', run: () => { cop.map.flyTo({ center: [lon, lat], zoom: 13 }); cop.highlightAt(lon, lat); } });
    }
    shell.showDetailRows(o.tittel, `ÅPEN KILDE · ${o.proveniens.kildeNavn}`, rader, handlinger);
  }

  /**
   * Send operatørens retting til hubben.
   *
   * ⚠ KARTET RULLES TILBAKE HVIS HUBBEN AVVISER. Uten det ville markøren stått der
   * operatøren slapp den mens hubben — og alle andre operatører — hadde den gamle
   * posisjonen. Da er hennes kart det eneste som viser rettingen, og hun tror den er
   * delt.
   */
  async function flyttGrunnbilde(id: string, lat: number, lon: number): Promise<void> {
    const foer = grunnbildeObjekter.get(id);
    try {
      const r = await fetch('/grunnbilde/flytt', {
        method: 'POST', credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ id, lat, lon }),
      });
      if (!r.ok) {
        const svar = await r.json().catch(() => ({})) as { forklaring?: string; error?: string };
        shell.showAlarm(`Rettingen ble IKKE lagret: ${svar.forklaring ?? svar.error ?? r.status}`);
        if (foer) { grunnbildelag.oppdater(foer); }
        return;
      }
      const svar = await r.json() as { objekt?: GrunnbildeObjekt };
      if (svar.objekt) {
        grunnbildeObjekter.set(id, svar.objekt);
        grunnbildelag.oppdater(svar.objekt);
        shell.showAlarm(`Posisjon rettet — ${posisjonsTekst(svar.objekt, Date.now())}`);
        // Rettingen er en beslutning et menneske har tatt om situasjonsbildet, og
        // hører i tidslinja sammen med de andre.
        loggHendelse({
          id: `gb-flytt-${id}-${Date.now()}`, tsMs: Date.now(), type: 'melding',
          tittel: `Posisjon rettet: ${svar.objekt.tittel}`,
          detalj: posisjonsTekst(svar.objekt, Date.now()),
          hastegrad: 'routine',
          onClick: () => visGrunnbildeObjekt(svar.objekt!),
        });
      }
    } catch {
      shell.showAlarm('Rettingen kunne ikke sendes — ingen tilkobling til hubben. Markøren er satt tilbake.');
      if (foer) grunnbildelag.oppdater(foer);
    }
  }

  /**
   * Hent grunnbildet fra hubben.
   *
   * ⚠ ET TOMT SVAR FORKLARES, ALDRI BARE VIST SOM ET TOMT KART. Hubbens
   * `sammendrag` sier om grunnbildet er tomt fordi ingen adaptere er bygget, fordi
   * kildene er av (OPSEC-bryteren), eller fordi det faktisk er stille.
   */
  async function lastGrunnbilde(): Promise<void> {
    try {
      const r = await fetch('/grunnbilde', { credentials: 'include' });
      if (!r.ok) return;
      const svar = await r.json() as { objekter?: GrunnbildeObjekt[]; sammendrag?: string; koblede?: number };
      const objekter = Array.isArray(svar.objekter) ? svar.objekter : [];
      grunnbildeObjekter.clear();
      for (const o of objekter) grunnbildeObjekter.set(o.id, o);
      grunnbildelag.render(objekter);
      // Hendelsene inn i tidslinja — også de som mangler posisjon, som er hele
      // grunnen til at tidslinja finnes for dem.
      const naa = Date.now();
      for (const o of objekter) {
        if (o.klasse !== 'hendelse') continue;
        const foreldet = erForeldet(o, naa);
        loggHendelse({
          id: `gb-${o.id}`, tsMs: o.tsMs, type: 'varsel',
          tittel: o.tittel,
          // ⚠ FORELDELSESTEKSTEN ERSTATTER POSISJONEN i undertittelen når den
          // gjelder. Sto begge, ble linja for lang og det viktigste — at kilden har
          // vært stille — havnet bak et ellipsetegn.
          detalj: foreldet
            ? `${o.proveniens.kildeNavn} · ${foreldelsesTekst(o, naa)}`
            : `${o.proveniens.kildeNavn} · ${posisjonsTekst(o, naa)}`,
          // Eierbeslutning 2026-08-18: grå etter et døgn, borte etter en uke.
          foreldet,
          // ⚠ HASTEGRADEN SENKES IKKE MED ALDEREN. En CONTACT fra i går er gammel,
          // ikke ufarlig — og en stripe som forsvant med tida ville gjort at det som
          // hastet i går ser ut som rutine i dag. Gråfargen bærer alderen; stripa
          // bærer alvoret.
          hastegrad: 'priority',
          onClick: () => visGrunnbildeObjekt(o),
        });
      }
      // ⚠ SIER FRA NÅR GRUNNBILDET ER TOMT AV EN GRUNN. Et stille tomt kart er
      // umulig å skille fra «ingenting skjer i Oslofjorden».
      if (!objekter.length && svar.sammendrag && !svar.koblede) {
        loggHendelse({
          id: 'gb-tomt', tsMs: Date.now(), type: 'varsel',
          tittel: 'Grunnbildet (åpne kilder) er tomt',
          detalj: svar.sammendrag,
          hastegrad: 'routine',
        });
      }
    } catch {
      // Nettverksfeil mot egen hub håndteres av kø-stripa og WS-gjenoppkoblingen;
      // her ville en alarm bare duplisert den beskjeden.
    }
  }
  void lastGrunnbilde();
  // Hvert kvarter, som hubbens egen henting. Å polle tettere gir ingen ny
  // informasjon — kildene oppdaterer ikke oftere.
  window.setInterval(() => { void lastGrunnbilde(); }, 15 * 60_000);

  // ── SENDEKØ-STRIPA (UX-rapportens Fase 3, funn F7) ────────────────────────
  // `LiveFeed.send()` kastet meldinger stille når WebSocket-en ikke var åpen. Nå
  // køes de — og her ser operatøren at de gjør det. En kø hun ikke ser er nesten
  // like ille: hun tror meldingen er hos TOC-en, og TOC-en har den ikke.
  const koStripe = new KoStripe();
  shell.mapEl.appendChild(koStripe.el);
  feed.onKo?.((st, tekst) => {
    koStripe.oppdater(st, tekst);
    // ⚠ ALARM BARE VED TAP, ikke ved venting. Venting er normal drift på dårlig
    // samband, og en alarm hvert tiende sekund ville lært operatøren å ignorere
    // alarmene — også den som betyr noe.
    if (st.overlop) shell.showAlarm(tekst);
  });
  feed.onEmcon?.((st) => {
    const v = emconStripe.oppdater(st);
    // Et nytt nivå skal RIVE i operatøren én gang, ikke bare stå der stille.
    if (v.vis) shell.showAlarm(v.tekst);
    loggHendelse({
      // ⚠ `autorisasjon`, IKKE en ny type. EMCON er samme familie som
      // ildfrigivning og delegert myndighet: en sjef som begrenser hva styrken
      // får gjøre, der loggsporet ER kontrollen. En egen type ville skilt den
      // fra nettopp de hendelsene en gransker leter etter den sammen med.
      id: `emcon-${Date.now()}`, tsMs: Date.now(), type: 'autorisasjon',
      // IMMEDIATE: et nytt EMCON-nivå endrer hva du har LOV til å sende. En
      // operatør som ikke får med seg det, sender i radiotaushet.
      hastegrad: 'immediate',
      tittel: v.tekst, detalj: v.detalj, farge: 'var(--gold)',
    });
  });
  // Hent nivået ved oppstart: en operatør som logger inn MENS det er radiotaushet
  // skal se det umiddelbart, ikke først ved neste endring.
  void (async () => {
    try {
      const r = await fetch('/emcon', { credentials: 'include' });
      if (r.ok) emconStripe.oppdater(await r.json());
    } catch { /* offline/demo — stripa står skjult, og det er ærlig */ }
  })();

  feed.onHistory?.((history) => cop.renderHistory(history));
  feed.onCollab?.((msg) => {
    if (msg.kind === 'pointer' && msg.lon !== undefined && msg.lat !== undefined) {
      cop.showPointer(msg.lon, msg.lat, msg.from);
    } else if (msg.kind === 'draw' && msg.features) {
      // ⚠ MINE EGNE TEGNINGER SKAL TILBAKE SOM MINE (eierfunn 2026-08-14).
      // Hubben replayer forfatter-settene ved tilkobling, også vårt eget. Før
      // havnet det i den PASSIVE `shared`-kilden, så etter F5 kunne operatøren
      // bare SE sine egne tegninger — ikke merke, flytte, redigere eller slette
      // dem, og «Slett alle» gjorde ingenting uten å si det.
      if (msg.from === user.id) {
        const n = draw.gjenoppta(msg.features);
        if (n) shell.showAlarm(`${n} av dine påtegninger er tilbake og kan redigeres.`);
      } else if (msg.features.length) {
        // Slå sammen tegninger PER FORFATTER (siste event per forfatter vinner) så
        // to operatører som tegner samtidig ikke overskriver hverandre.
        sharedDraw.set(msg.from, msg.features);
        cop.renderShared([...sharedDraw.values()].flat());
      } else {
        // Tomt sett = forfatteren har visket ut → fjern deres lag fra tavla.
        sharedDraw.delete(msg.from);
        cop.renderShared([...sharedDraw.values()].flat());
      }
    } else if (msg.kind === 'units' && msg.units) {
      // ⚠ SAMME FEIL, ALVORLIGERE UTFALL: egne enheter kom tilbake som andres
      // etter F5 — ikke dragbare, ikke i Objekter-lista, ikke slettbare, og de
      // FALT UT AV DANGER-CLOSE-SJEKKEN og eksporten (begge leser `snapshot()`).
      if (msg.from === user.id) {
        const n = units.gjenoppta(msg.units as PlacedUnit[]);
        if (n) {
          rebuildRoster();
          shell.showAlarm(`${n} av dine plasserte enheter er tilbake under din kontroll.`);
        }
      } else {
        // Slå sammen PER FORFATTER (som tegninger) så samtidige operatører og
        // hub-replay (ett event per forfatter) ikke overskriver hverandre.
        units.renderShared(msg.from, msg.units as PlacedUnit[]);
      }
    } else if (msg.kind === 'report' && msg.report) {
      for (const r of parseReports([msg.report])) addReport(r, false);
    } else if (msg.kind === 'intel' && msg.intel) {
      const p = parseIntelOne(msg.intel); if (p) intelWs.ingest(p);
    } else if (msg.kind === 'field' && msg.field) {
      const f = parseFieldMessage(msg.field); if (f) addField(f, true);
    } else if (msg.kind === 'chat' && msg.chat) {
      const e = parseChatEvent(msg.chat); if (e) sentralbord.ingestChat(e);
    } else if (msg.kind === 'rfi' && msg.rfi) {
      // MOTTATTE meldinger logges på samme måte som egne: loggen er nettets
      // hendelser, ikke bare mine egne handlinger.
      const r = parseRfiOne(msg.rfi);
      if (r) {
        upsertRfi(r); rfiPanel.render(); dispatchPanel.render();
        loggHendelse({
          id: `rfi-${r.id}-${r.status}`, tsMs: r.createdMs, type: 'rfi',
          tittel: r.question, detalj: `${r.requester} · ${r.status} (mottatt)`,
          hastegrad: r.priority,
          onClick: () => rfiPanel.el.classList.contains('open') || rfiPanel.toggle(),
        });
      }
    } else if (msg.kind === 'casualty' && msg.casualty) {
      const c = parseCasualtyOne(msg.casualty);
      if (c) {
        upsertCasualty(c); casualtyPanel.render();
        loggHendelse({
          id: `cas-${c.id}-${c.updatedMs}`, tsMs: c.tsMs, type: 'perstat',
          hastegrad: casualtyPrecedence(c),
          tittel: `${c.callsign} — ${c.status.toUpperCase()}${c.medevac ? ' · MEDEVAC' : ''}`,
          detalj: `${c.unit || 'enhet ikke satt'} · ${kortId(c.reportedBy)} (mottatt)`,
          onClick: () => casualtyPanel.el.classList.contains('open') || casualtyPanel.toggle(),
        });
      }
    } else if (msg.kind === 'logstat' && msg.logstat) {
      const l = parseLogStatOne(msg.logstat);
      if (l) {
        upsertLogStat(l); logisticsPanel.render();
        const w = worstLevel(l);
        loggHendelse({
          id: `log-${l.id}-${l.updatedMs}`, tsMs: l.tsMs, type: 'logstat',
          tittel: `LOGSTAT ${l.unit}${w ? ` — verste: ${w.toUpperCase()}` : ''}`,
          detalj: `${kortId(l.reportedBy)} (mottatt)`,
          hastegrad: w ? SUPPLY_LEVEL_PRECEDENCE[w] : undefined,
          farge: w ? SUPPLY_LEVEL_COLOR[w] : undefined,
          onClick: () => logisticsPanel.el.classList.contains('open') || logisticsPanel.toggle(),
        });
      }
    } else if (msg.kind === 'resupply' && msg.resupply) {
      const r = parseResupplyOne(msg.resupply);
      if (r) {
        upsertResupply(r); logisticsPanel.render();
        loggHendelse({
          id: `res-${r.id}-${r.status}`, tsMs: r.tsMs, type: 'logstat',
          tittel: `Anmodning: ${r.supplyClass} — ${r.quantity}`,
          hastegrad: r.priority,
          detalj: `${r.requester} · ${r.priority} · ${r.status} (mottatt)`,
          onClick: () => logisticsPanel.el.classList.contains('open') || logisticsPanel.toggle(),
        });
      }
    } else if (msg.kind === 'gnadm' && msg.dm) {
      const dm = msg.dm as { from?: string; text?: string; tsMs?: number };
      if (dm.from) { sentralbord.ingestInbound(dm.from, dm.text ?? '', dm.tsMs ?? Date.now()); learnContact(dm.from); shell.showAlarm(`GNÅ-svar fra ${dm.from.replace(/^@/, '').replace(/:.*$/, '')}`); }
    } else if (msg.kind === 'myndighet' && msg.myndighet) {
      const d = parseDelegasjon(msg.myndighet);
      if (d) { upsertDelegasjon(d); loggDelegasjon(d); myndighetPanel.render(); }
    } else if (msg.kind === 'eso' && msg.eso) {
      const s = EsoSoldierStatusSchema.safeParse(msg.eso);
      if (s.success) { upsertEso(s.data); esoPanel.onData(); }
    } else if (msg.kind === 'checklist' && msg.checklist) {
      const a = ChecklistAssignmentSchema.safeParse(msg.checklist);
      if (a.success) { upsertAssign(a.data); esoPanel.onData(); }
    } else if (msg.kind === 'calendar' && msg.activity) {
      const a = parseCalendarActivity(msg.activity);
      if (a) { upsertActivity(a); calendarPanel.render(); }
    } else if (msg.kind === 'kalender-slett' && msg.kalenderId) {
      // Dagsedel slettet i GNÅ → fjern den fra COP-kalenderen. Uten denne ble en
      // slettet aktivitet stående i TOC-kalenderen for alltid.
      if (slettActivity(msg.kalenderId)) calendarPanel.render();
    } else if (msg.kind === 'rfi-svar' && msg.rfiSvar) {
      // Svar på et etterretningskrav fra GNÅ. Broen leverte det aldri før —
      // en soldat kunne svare i appen og TOC så ingenting.
      const sv = msg.rfiSvar as { rfiId?: string; text?: string; by?: string };
      const eksisterende = sv.rfiId ? rfis.get(sv.rfiId) : undefined;
      if (eksisterende && sv.text) {
        upsertRfi(answerRfi(eksisterende, sv.text, sv.by || msg.from, msg.ts || Date.now()));
        rfiPanel.render(); dispatchPanel.render();
        shell.showAlarm(`RFI besvart fra felt: ${eksisterende.question.slice(0, 60)}`);
        loggHendelse({
          id: `rfi-svar-${sv.rfiId}-${msg.ts || 0}`, tsMs: msg.ts || Date.now(), type: 'rfi',
          // Svaret arver kravets hastegrad: en FLASH-RFI som endelig blir besvart
          // er like hastende å LESE som den var å stille.
          hastegrad: eksisterende.priority,
          tittel: `Svar mottatt: ${eksisterende.question}`,
          detalj: `${sv.by || msg.from} · fra GNÅ`,
          onClick: () => rfiPanel.el.classList.contains('open') || rfiPanel.toggle(),
        });
      } else if (sv.rfiId) {
        // Svar på en RFI vi ikke har (opprettet før denne sesjonen). Si det —
        // et tapt svar skal ikke være stille.
        shell.showAlarm(`RFI-svar mottatt for ukjent krav ${sv.rfiId} — åpne RFI-panelet og hent historikk`);
      }
    } else if (msg.kind === 'teig' && msg.teig) {
      // Teig fra hubben — enten en annen operatørs, eller en lagfører sin fra
      // GNÅ. Ingest ekko den ikke tilbake (ingen sløyfe).
      const t = TeigSchema.safeParse(msg.teig);
      if (t.success) geofence.ingest({ op: 'upsert', teig: t.data });
    } else if (msg.kind === 'teig-slett' && msg.teigId) {
      geofence.ingest({ op: 'delete', id: msg.teigId, tsMs: msg.ts || Date.now() });
    } else if (msg.kind === 'orbat' && msg.orbat) {
      const ev = OrbatEventSchema.safeParse(msg.orbat);
      if (ev.success) orbatCreator.ingestEvent(ev.data);
    } else if (msg.kind === 'emergency' && msg.lon !== undefined && msg.lat !== undefined) {
      cop.setEmergency(msg.from, msg.lon, msg.lat, !msg.cancel);
      if (!msg.cancel) shell.showAlarm(`112-NØDALARM fra ${msg.from}`);
      const naaEm = Date.now();
      const lonEm = msg.lon, latEm = msg.lat;
      loggHendelse({
        id: `em-${msg.from}-${naaEm}`, tsMs: naaEm, type: 'varsel',
        hastegrad: msg.cancel ? 'priority' : 'flash', // menneskeerklært nød — se egen 112 over
        tittel: msg.cancel ? `112 avlyst av ${kortId(msg.from)}` : `112-NØDALARM fra ${kortId(msg.from)}`,
        detalj: 'posisjon kringkastet til nettet',
        farge: msg.cancel ? 'var(--sage)' : 'var(--danger)',
        onClick: () => { cop.map.flyTo({ center: [lonEm, latEm], zoom: 15 }); cop.highlightAt(lonEm, latEm); },
      });
    }
  });
  // ── FJERNET 2026-08-13 (eierbeslutning): kortet «Ingen kilder rapporterer
  // ennå» midt i kartet.
  //
  // Det ble lagt inn 2026-07-29 for å si HVORFOR kartflaten var blank. Men
  // opplysningen finnes alt to steder som er bedre plassert og alltid sanne:
  // tellerne i venstre kartlag-liste (SPARK-sensorer 0 / Deteksjonsmål 0) og
  // Objekter-panelet til høyre («Ingen spor eller plasserte enheter …»). Kortet
  // sa altså en tredje gang det panelene alt sier, og gjorde det midt i
  // operatørens arbeidsflate — over Oslofjorden, der sporene faktisk kommer.
  // Regelen den brøt: informasjon skal stå ETT sted, der operatøren leter.
  //
  // Tomt kart er fortsatt ærlig: tellerne står på 0, ikke skjult.
  // VALE SENS-21: uposisjonerte deteksjoner har ingen kartposisjon å oppdatere
  // (ingen `rebuildRoster`/`timeMachine` — begge er posisjons-drevet), men skal
  // likevel inn i varselkøen. Valgfri fordi `DemoFeed` ikke sender noen.
  feed.onSporUtenPosisjon?.((spor) => {
    lastSporUtenPosisjon = spor;
    refreshAlerts();
  });
  feed.onTracks((tracks) => {
    lastTracks = tracks;
    timeMachine.record(tracks, Date.now());
    rebuildRoster();
    refreshAlerts();
    shell.setCount('sensors', tracks.filter((t) => t.kind === 'sensor').length);
    shell.setCount('targets', tracks.filter((t) => t.kind === 'detection').length);
    // Kart/geofence/alarm kun LIVE — under avspilling driver tidsmaskinen kartet.
    if (!timeMachine.isLive) return;
    cop.render(tracks);
    // Ankerlinjene følger sporene (funn F4): et spor beveger seg, og en linje som
    // bare ble tegnet da meldingen kom ville pekt på der kjøretøyet sto for fem
    // minutter siden.
    tegnAnkerlinjer();
    geofence.check(tracks);
    let newestAlarm: Track | null = null;
    for (const t of tracks) {
      if (t.kind !== 'detection' || seenAlarms.has(t.id)) continue;
      seenAlarms.add(t.id);
      lastAlarmTrack = t;
      newestAlarm = t;
      shell.showAlarm(`DETEKSJON: ${t.label.replace('MÅL ', '')}${t.estimated ? ' (estimert)' : ''}`);
    }
    // Automatisk fokus på NY deteksjon (f.eks. skudd): zoom inn + marker stedet,
    // så operatøren straks ser hvor det skjer. Én flytur per batch (nyeste mål).
    if (newestAlarm) {
      cop.flyTo(newestAlarm);
      cop.highlightAt(newestAlarm.lon, newestAlarm.lat);
    }
  });
  feed.start();
}
